import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/env';
import { useAuth } from '../context/AuthContext';
import ChatwootConversationMenu from '../components/ChatwootConversationMenu';
import ChatwootAgentCheckIcon from '../components/ChatwootAgentCheckIcon';
import ChatwootTransferConversationButton from '../components/ChatwootTransferConversationButton';
import { OPEN_CONVERSATION_KEY } from '../components/ChatwootAssignmentToastListener';
import { PULSE_CMD_CONV_UNREAD_KEY } from '../config/pulseCommand';
import { canAssignChatwootAgent } from '../utils/chatwootPermissions';
import {
  fetchChatwootMeta,
  fetchConversationsEstadoBulk,
  fetchConversationTemplates,
  requestHumanFinalize,
  requestHumanReturnToBot,
  requestHumanTakeover,
  sendConversationTemplate
} from '../api/chatwoot';
import { buildCrmSwalBody, fireConfirmDialog } from '../utils/swalConfirm';
import { CrmStatusBadgeFrom } from '../components/CrmStatusBadge';
import {
  resolveChatStatusBadge,
  resolveClienteStatusBadge,
  resolveTycStatusBadge
} from '../utils/crmStatusBadge';
import { dispatchConversationReturnToBot } from '../utils/chatwootConversationEvents';
import { getFloatingPanelFixedStyle } from '../utils/floatingPanelPosition';
import '../styles/conversaciones-premium.css';
import ConversacionesMessageToastRail from '../components/conversaciones/ConversacionesMessageToastRail';
import {
  ConversationListAvatar,
  ConversationListHandlingBadge,
  ConversationListSkeleton,
  CwIconAll,
  CwIconChevronFirst,
  CwIconChevronLast,
  CwIconChevronNext,
  CwIconChevronPrev,
  CwIconPending,
  CwIconProfile,
  CwIconResolved,
  CwIconScrollDown,
  CwIconUnread,
  CwListSearch
} from '../components/conversaciones/CwUiParts';

const LIVE_REFRESH_MS = 2500;
const CHATWOOT_CONVERSATIONS_CHANGED_EVENT = 'pulse:chatwoot-conversations-changed';
const CHAT_LOAD_OLDER_THRESHOLD_PX = 120;
const TOAST_TTL_MS = 6000;
const VIRTUAL_AGENT_AVATAR = '/anita.png';
const ASSIGNEE_FILTER_CACHE_KEY = 'crm_cw_assignee_filter_v1';
const CONTACT_NAME_CACHE_KEY = 'crm_cw_contact_names_v1';
const CONTACT_NAME_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const PROFILE_PREFETCH_CONCURRENCY = 18;
const CONVERSATIONS_LIST_PAGE_SIZE = 10;
const CHATWOOT_INBOXES = [
  { id: 5, label: 'Farmacia GSA', hint: 'Inbox #5' },
  { id: 6, label: 'Sanatorio Regional', hint: 'Inbox #6' }
];
const FARMACIA_MED_PAGE_SIZE_OPTIONS = [
  { value: '5', label: '5' },
  { value: '10', label: '10' },
  { value: '25', label: '25' },
  { value: '50', label: '50' },
  { value: 'all', label: 'Todos' }
];

/** Frases para atención humana: se insertan en el compositor, no se envían solas. */
const HUMAN_QUICK_PHRASES = [
  {
    id: 'greet',
    label: 'Saludo',
    text: '¡Hola! Soy del equipo del sanatorio. ¿En qué puedo ayudarte?'
  },
  {
    id: 'wait',
    label: 'Un momento',
    text: 'Aguardá un momento por favor, estoy revisando tu consulta.'
  },
  {
    id: 'dni',
    label: 'Pedir DNI',
    text: '¿Podés indicarme tu DNI para ubicar tu ficha?'
  },
  {
    id: 'rx-photo',
    label: 'Foto receta',
    text: '¿Podés enviarnos una foto clara de la receta, por favor?'
  },
  {
    id: 'coverage',
    label: 'Cobertura',
    text: '¿Tenés obra social o prepaga? Contame cuál para verificar la cobertura.'
  },
  {
    id: 'delivery',
    label: 'Envío',
    text: 'Te confirmo los datos de envío en unos minutos.'
  },
  {
    id: 'thanks',
    label: 'Gracias',
    text: 'Gracias por tu paciencia. Cualquier otra consulta, escribinos.'
  },
  {
    id: 'bye',
    label: 'Despedida',
    text: '¡Gracias! Que tengas un buen día.'
  }
];

const FLAG_COUNTRY_CODES = (
  'AC AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ '
  + 'CA CC CD CF CG CH CI CK CL CM CN CO CP CR CU CV CW CX CY CZ DE DG DJ DK DM DO DZ EA EC EE EG EH ER ES ET EU FI '
  + 'FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU IC ID IE IL IM IN IO IQ '
  + 'IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML '
  + 'MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS '
  + 'PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TA TC TD TF TG TH TJ TK '
  + 'TL TM TN TO TR TT TV TW TZ UA UG UM UN US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW'
).split(' ');

function countryCodeToFlagEmoji(code) {
  const safe = String(code || '').toUpperCase();
  if (!/^[A-Z]{2}$/.test(safe)) return '';
  const A = 127462;
  return String.fromCodePoint(A + (safe.charCodeAt(0) - 65), A + (safe.charCodeAt(1) - 65));
}

const FLAG_EMOJIS = [
  '🏁',
  '🚩',
  '🎌',
  '🏴',
  '🏳️',
  '🏳️‍🌈',
  '🏳️‍⚧️',
  '🏴‍☠️',
  ...FLAG_COUNTRY_CODES.map(countryCodeToFlagEmoji).filter(Boolean)
];

const EMOJI_CATEGORIES = [
  {
    label: '😀',
    category: 'Smileys & Emotion',
    emojis: '😀😃😄😁😆😅🤣😂🙂🙃🫠😉😊😇🥰😍🤩😘😗😚😙🥲😋😛😜🤪😝🤑🤗🤭🫢🫣🤫🤔🫡🤐🤨😐😑😶🫥😶‍🌫️😏😒🙄😬😮‍💨🤥😌😔😪🤤😴😷🤒🤕🤢🤮🤧🥵🥶🥴😵😵‍💫🤯🤠🥳🥸😎🤓🧐😕🫤😟🙁😮😯😲😳🥺🥹😦😧😨😰😥😢😭😱😖😣😞😓😩😫🥱😤😡😠🤬😈👿💀☠️💩🤡👹👺👻👽👾🤖😺😸😹😻😼😽🙀😿😾🙈🙉🙊💌💘💝💖💗💓💞💕💟❣️💔❤️‍🔥❤️‍🩹❤️🧡💛💚💙💜🤎🖤🤍💋💯💢💥💫💦💨'
  },
  {
    label: '👋',
    category: 'People & Body',
    emojis: '👋🤚🖐️✋🖖🫱🫲🫳🫴👌🤌🤏✌️🤞🫰🤟🤘🤙👈👉👆🖕👇☝️🫵👍👎✊👊🤛🤜👏🙌🫶👐🤲🤝🙏✍️💅🤳💪🦾🦿🦵🦶👂🦻👃🧠🫀🫁🦷🦴👀👁️👅👄🫦👶🧒👦👧🧑👱👨🧔🧔‍♂️🧔‍♀️👨‍🦰👨‍🦱👨‍🦳👨‍🦲👩👩‍🦰🧑‍🦰👩‍🦱🧑‍🦱👩‍🦳🧑‍🦳👩‍🦲🧑‍🦲👱‍♀️👱‍♂️🧓👴👵'
  },
  {
    label: '🐵',
    category: 'Animals & Nature',
    emojis: '🐵🐒🦍🦧🐶🐕🦮🐕‍🦺🐩🐺🦊🦝🐱🐈🐈‍⬛🦁🐯🐅🐆🐴🐎🦄🦓🦌🦬🐮🐂🐃🐄🐷🐖🐗🐽🐏🐑🐐🐪🐫🦙🦒🐘🦣🦏🦛🐭🐁🐀🐹🐰🐇🐿️🦫🦔🦇🐻🐻‍❄️🐨🐼🦥🦦🦨🦘🦡🐾🦃🐔🐓🐣🐤🐥🐦🐧🕊️🦅🦆🦢🦉🦤🌲🌳🌴🌵🌾🌿☘️🍀🍁🍂🍃'
  },
  {
    label: '🍇',
    category: 'Food & Drink',
    emojis: '🍇🍈🍉🍊🍋🍌🍍🥭🍎🍏🍐🍑🍒🍓🫐🥝🍅🫒🥥🥑🍆🥔🥕🌽🌶️🫑🥒🥬🥦🧄🧅🥜🫘🌰🍞🥐🥖🫓🥨🥯🥞🧇🧀🍖🍗🥩🥓🍔🍟🍕🌭🥪🌮🌯🫔🥙🧆🥚🍳🥘🍲🫕🥣🥗🍿🧈🧂🍱🍘🍙🍚🍛🍜🍝🍠🍢🍣🍤🍥🥮🍡🥟🥠🥡🦀🦞🦐🦑🦪🍦🍧🍨🍩🍪🎂🍰🧁🥧🍫🍬🍭🍮🍯'
  },
  {
    label: '🌍',
    category: 'Travel & Places',
    emojis: '🌍🌎🌏🌐🗺️🗾🧭🏔️⛰️🌋🗻🏕️🏖️🏜️🏝️🏞️🏟️🏛️🏗️🧱🪨🪵🛖🏘️🏚️🏠🏡🏢🏣🏤🏥🏦🏨🏩🏪🏫🏬🏭🏯🏰💒🗼🗽⛪🕌🛕🕍⛩️🕋⛲⛺🌁🌃🏙️🌄🌅🌆🌇🌉'
  },
  {
    label: '🎃',
    category: 'Activities',
    emojis: '🎃🎄🎆🎇🧨✨🎈🎉🎊🎋🎍🎎🎏🎐🎑🧧🎀🎁🎗️🎟️🎫🎖️🏆🏅🥇🥈🥉⚽⚾🥎🏀🏐🏈🏉🎾🥏🎳🏏🏑🏒🥍🏓🏸🥊🥋🥅⛳⛸️🎣🤿🎽🎿🛷🥌🎯🪀🪁'
  },
  {
    label: '👓',
    category: 'Objects',
    emojis: '👓🕶️🥽🥼🦺👔👕👖🧣🧤🧥🧦👗👘🥻🩱🩲🩳👙👚👛👜👝🛍️🎒🩴👞👟🥾🥿👠👡🩰👢👑👒🎩🎓🧢🪖⛑️📿💄💍💎🔇🔈🔉🔊📢📣📯🔔🔕🎼🎵🎶🎙️🎚️🎛️🎤🎧📻🎷🪗🎸🎹🎺🎻🪕🥁🪘📱📲☎️📞📟📠🔋🪫🔌💻🖥️🖨️⌨️🖱️🖲️💽💾💿📀'
  },
  {
    label: '🏧',
    category: 'Symbols',
    emojis: '🏧🚮🚰♿🚹🚺🚻🚼🚾🛂🛃🛄🛅⚠️🚸⛔🚫🚳🚭🚯🚱🚷📵🔞☢️☣️⬆️↗️➡️↘️⬇️↙️⬅️↖️↕️↔️↩️↪️⤴️⤵️🔃🔄🔙🔚🔛🔜🔝🛐⚛️🕉️✡️☸️☯️✝️☦️☪️☮️🕎🔯♈♉♊♋♌♍♎♏♐♑♒♓⛎🔀🔁🔂'
  },
  {
    label: '🏁',
    category: 'Flags',
    emojis: FLAG_EMOJIS
  }
];

function splitEmojiGraphemes(value) {
  if (Array.isArray(value)) {
    return value.filter((segment) => String(segment || '').trim());
  }

  const text = String(value || '');
  if (!text) return [];

  if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
    const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
    return Array.from(segmenter.segment(text), (part) => part.segment).filter((segment) => segment.trim());
  }

  return Array.from(text).filter((segment) => segment.trim());
}

function EmojiPicker({ onSelectEmoji, onClose, buttonRef }) {
  const [activeCategory, setActiveCategory] = useState(0);
  const pickerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = () => {
    if (!buttonRef?.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const pickerWidth = 360;
    const pickerHeight = 420;
    const viewportPadding = 12;
    const gap = 8;

    const spaceAbove = rect.top - viewportPadding;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const showAbove = spaceAbove >= pickerHeight || spaceAbove > spaceBelow;

    const top = showAbove
      ? Math.max(viewportPadding, rect.top - pickerHeight - gap)
      : Math.min(window.innerHeight - pickerHeight - viewportPadding, rect.bottom + gap);

    const left = Math.min(
      window.innerWidth - pickerWidth - viewportPadding,
      Math.max(viewportPadding, rect.right - pickerWidth)
    );

    setPosition({ top, left });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        const isButton = buttonRef?.current?.contains(event.target);
        if (!isButton) onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, buttonRef]);

  useEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [buttonRef]);

  const currentEmojis = EMOJI_CATEGORIES[activeCategory]?.emojis || '';
  const emojis = splitEmojiGraphemes(currentEmojis);

  if (typeof document === 'undefined') return null;

  return createPortal((
    <div
      className="crm-emoji-picker"
      ref={pickerRef}
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`
      }}
    >
      <div className="crm-emoji-header">
        <div className="crm-emoji-tabs">
          {EMOJI_CATEGORIES.map((cat, idx) => (
            <button
              key={idx}
              type="button"
              className={`crm-emoji-tab ${activeCategory === idx ? 'active' : ''}`}
              onClick={() => setActiveCategory(idx)}
              title={cat.category}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>
      <div className="crm-emoji-grid">
        {emojis.map((emoji, idx) => (
          <button
            key={idx}
            type="button"
            className="crm-emoji-btn"
            onClick={() => {
              onSelectEmoji(emoji);
              onClose();
            }}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  ), document.body);
}

function formatTime(unixTime) {
  if (!unixTime) return '';
  return new Date(unixTime * 1000).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/** Hora compacta para el listado (deja más lugar al nombre). */
function formatListTime(unixTime) {
  if (!unixTime) return '';
  const d = new Date(unixTime * 1000);
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startDate = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startToday - startDate) / (1000 * 60 * 60 * 24));
  const time = d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 0) return time;
  if (diffDays === 1) return `Ayer ${time}`;
  if (d.getFullYear() === now.getFullYear()) {
    return `${d.getDate()}/${d.getMonth() + 1} ${time}`;
  }
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function sanitizeDisplayName(value, fallback = 'Sin nombre') {
  const trimmed = String(value || '').trim();
  if (!trimmed) return fallback;

  const compact = trimmed.replace(/\s+/g, ' ');
  const alnum = compact
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '');

  if (!alnum) return fallback;
  return compact;
}

function getChatwootSenderName(conversation) {
  return (
    conversation?.meta?.sender?.name ||
    conversation?.sender?.name ||
    ''
  );
}

function toTitleCaseWord(word) {
  const safe = String(word || '').trim();
  if (!safe) return '';
  const lower = safe.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function toTitleCaseName(text) {
  return String(text || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(toTitleCaseWord)
    .join(' ');
}

/** Convierte "GARCIA, CARLOS EMILIANO" o campos Geclisa a "Carlos Emiliano Garcia". */
function formatPersonDisplayName({ apellido = '', nombre = '', raw = '' } = {}) {
  const a = String(apellido || '').trim();
  const n = String(nombre || '').trim();
  if (n && a) return toTitleCaseName(`${n} ${a}`);

  const source = String(raw || n || a).trim();
  if (!source) return '';

  if (source.includes(',')) {
    const parts = source
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length >= 2) {
      const [first, ...rest] = parts;
      return toTitleCaseName(`${rest.join(' ')} ${first}`);
    }
  }

  return toTitleCaseName(source);
}

function readContactNameCache() {
  try {
    const raw = localStorage.getItem(CONTACT_NAME_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function getCachedContactDisplayName(phone) {
  const key = normalizeDigits(phone);
  if (!key) return '';
  const entry = readContactNameCache()[key];
  if (!entry?.name) return '';
  if (Date.now() - Number(entry.at || 0) > CONTACT_NAME_CACHE_TTL_MS) return '';
  return String(entry.name).trim();
}

function persistContactDisplayName(phone, displayName) {
  const key = normalizeDigits(phone);
  const name = String(displayName || '').trim();
  if (!key || !name) return;
  try {
    const cache = readContactNameCache();
    cache[key] = { name, at: Date.now() };
    localStorage.setItem(CONTACT_NAME_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // fallback silencioso
  }
}

function getClientDisplayNameFromProfile(profile) {
  if (!profile) return '';
  const apellido = String(profile.apellido_geclisa || '').trim();
  const nombre = String(profile.nombre_geclisa || '').trim();
  const fromGeclisa = formatPersonDisplayName({ apellido, nombre });
  if (fromGeclisa) return sanitizeDisplayName(fromGeclisa, '');

  const nombrePlex = String(profile.nombre_plex || '').trim();
  const fromPlex = formatPersonDisplayName({ raw: nombrePlex });
  return sanitizeDisplayName(fromPlex, '');
}

function getClientEmailFromProfile(profile) {
  if (!profile) return '';
  const geclisa = String(profile.email_geclisa ?? '').trim();
  if (geclisa) return geclisa;
  const plex = String(profile.email_plex ?? '').trim();
  if (plex) return plex;
  return '';
}

function formatClientContactEmail(profile) {
  return getClientEmailFromProfile(profile) || 'Sin email';
}

const CHATWOOT_STATUS_LABELS = {
  open: 'Abierta',
  resolved: 'Resuelta',
  pending: 'Pendiente',
  snoozed: 'Pospuesta'
};

/** Estado pendiente / resuelta — icono en lista, texto completo en encabezado del chat. */
const CHATWOOT_STATUS_BADGES = {
  pending: { label: 'Pendiente', listIcon: '🕐', tone: 'pending' },
  resolved: { label: 'Resuelta', listIcon: '✓', tone: 'resolved' }
};

function getConversationStatusBadge(status) {
  const key = String(status || '').trim().toLowerCase();
  return CHATWOOT_STATUS_BADGES[key] || null;
}

function isConversationChatwootResolved(conv) {
  return String(conv?.status || '').trim().toLowerCase() === 'resolved';
}

/** Fases Pulse que cuentan como cerradas en la pestaña Resueltas. */
const CONVERSATION_FASE_RESUELTAS = new Set(['finalizado', 'finalizada', 'completado']);

const CONVERSATION_FASE_FINISHED = new Set([
  ...CONVERSATION_FASE_RESUELTAS,
  'cancelado'
]);

function getConversationEstadoFase(conversation, estadoById) {
  const key = normalizeConversationEstadoKey(conversation?.id);
  return String(estadoById?.[key]?.fase || '')
    .toLowerCase()
    .trim();
}

function isConversationFaseResuelta(conversation, estadoById) {
  return CONVERSATION_FASE_RESUELTAS.has(getConversationEstadoFase(conversation, estadoById));
}

function isConversationInResueltasList(conversation, estadoById) {
  return (
    isConversationChatwootResolved(conversation) || isConversationFaseResuelta(conversation, estadoById)
  );
}

/** Cola «No atendidas»: en curso — todo lo que no está en Resueltas (con o sin mensajes sin leer). */
function isConversationNoAtendida(conversation, estadoById) {
  return !isConversationInResueltasList(conversation, estadoById);
}

/** No leídos: solo mensajes sin leer en Chatwoot (igual que el rail lateral). */
function isConversationUnread(conv) {
  return Number(conv?.unread_count || 0) > 0;
}

function mergeServerConversationList(serverList, prevList, acknowledgedReadIds) {
  if (!Array.isArray(serverList)) return [];
  const prevById = new Map((prevList || []).map((c) => [c.id, c]));
  const acked = acknowledgedReadIds instanceof Set ? acknowledgedReadIds : new Set();

  return serverList.map((conv) => {
    const id = conv?.id;
    if (id == null || acked.has(id)) return conv;
    const prior = prevById.get(id);
    if (prior && Number(prior.unread_count) > 0 && Number(conv.unread_count) === 0) {
      return { ...conv, unread_count: prior.unread_count };
    }
    return conv;
  });
}

/** Rail lateral: cualquier conversación con unread_count (sin esperar fase bot). */
function isUnreadMessageToastCandidate(conv, selectedId) {
  if (!conv?.id || conv.id === selectedId) return false;
  return isConversationUnread(conv);
}

function normalizeConversationEstadoKey(conversationId) {
  return String(conversationId ?? '').trim();
}

function isConversationVisibleInAgentScope(conv, scopeAgentId, estadoById) {
  const scopedId = Number(scopeAgentId);
  if (!Number.isFinite(scopedId) || scopedId <= 0) return true;
  const assigneeId = Number(conv?.assignee?.id);
  if (Number.isFinite(assigneeId) && assigneeId > 0) {
    return assigneeId === scopedId;
  }
  return isConversationNoAtendida(conv, estadoById);
}

function getBotDisplayNameForInbox(inboxId) {
  return Number(inboxId) === 6 ? 'Bot Sanatorio' : 'Anita';
}

function resolveConversationHandlingMode({ fase, assignee, derivacionHumano, inboxId }) {
  const f = String(fase || '').toLowerCase().trim();
  const botDisplayName = getBotDisplayNameForInbox(inboxId);

  if (CONVERSATION_FASE_FINISHED.has(f)) {
    return {
      kind: 'finished',
      listIcon: '✅',
      label: 'Flujo finalizado',
      tone: 'finished-flow',
      title: 'Flujo de recetas finalizado (bot)'
    };
  }

  if (f === 'derivacion_humana' || derivacionHumano) {
    const operatorName = sanitizeDisplayName(assignee?.name, '');
    return {
      kind: 'human',
      listIcon: '👤',
      label: operatorName ? `Operador · ${operatorName}` : 'Operador humano',
      tone: 'human',
      title: operatorName
        ? `Asignado a ${operatorName} en Chatwoot`
        : 'Derivado a operador humano'
    };
  }

  // Fase activa del flujo (inicio, nuevo, etc.): Anita atiende aunque Chatwoot tenga assignee técnico
  if (f) {
    return {
      kind: 'bot',
      listIcon: '🤖',
      agentName: botDisplayName,
      label: `Agente IA · ${botDisplayName}`,
      tone: 'bot',
      title: `Atiende el agente virtual ${botDisplayName}`
    };
  }

  if (assignee?.id) {
    const operatorName = sanitizeDisplayName(assignee?.name, '');
    return {
      kind: 'human',
      listIcon: '👤',
      label: operatorName ? `Operador · ${operatorName}` : 'Operador humano',
      tone: 'human',
      title: operatorName
        ? `Asignado a ${operatorName} en Chatwoot`
        : 'Asignado en Chatwoot'
    };
  }

  return null;
}

function getConversationHandlingForConv(conversation, estadoById) {
  const key = normalizeConversationEstadoKey(conversation?.id);
  const estado = estadoById?.[key];
  return resolveConversationHandlingMode({
    fase: estado?.fase,
    assignee: conversation?.assignee,
    derivacionHumano: estado?.derivacion_humano,
    inboxId: conversation?.inbox_id
  });
}

function isBotGuardedConversation(conversation, estadoById) {
  return getConversationHandlingForConv(conversation, estadoById)?.kind === 'bot';
}

function getConversationListLeadBadge(conversation, estadoById) {
  const handling = getConversationHandlingForConv(conversation, estadoById);
  if (handling) return handling;
  return getConversationStatusBadge(conversation?.status);
}

function getConversationListNameTitle(conversation) {
  const name = String(conversation?.displayName || '').trim() || 'Paciente sin nombre';
  const phone = String(conversation?.sender?.phone_number || '').trim();
  if (phone) return `Paciente: ${name} · Tel. ${phone}`;
  return `Paciente: ${name}`;
}

const PROFILE_ACCORDION_SECTION = {
  contact: 'contact',
  plex: 'plex',
  meds: 'meds'
};

const CHATWOOT_PRIORITY_BADGES = {
  urgent: { label: 'Urgente', shortLabel: 'URG', headIcon: '⚡', tone: 'urgent' },
  high: { label: 'Alta', shortLabel: 'ALT', headIcon: '▲', tone: 'high' },
  medium: { label: 'Media', shortLabel: 'MED', headIcon: '●', tone: 'medium' },
  low: { label: 'Baja', shortLabel: 'BAJ', headIcon: '▽', tone: 'low' }
};

const CHATWOOT_PRIORITY_SORT_RANK = {
  urgent: 4,
  high: 3,
  medium: 2,
  low: 1
};

function getConversationPriorityRank(priority) {
  const key = String(priority || '').trim().toLowerCase();
  return CHATWOOT_PRIORITY_SORT_RANK[key] || 0;
}

function pickPrimaryAssigneeId(assigneesById) {
  let best = null;
  for (const entry of assigneesById.values()) {
    if (!best || entry.count > best.count) best = entry;
  }
  return best?.id ?? null;
}

function readAssigneeFilterCache() {
  try {
    const raw = sessionStorage.getItem(ASSIGNEE_FILTER_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const id = parsed?.id;
    const name = String(parsed?.name || '').trim();
    if (id == null || !name) return null;
    return {
      id,
      name,
      count: Math.max(0, Number(parsed?.count) || 0),
      chatLabels: Array.isArray(parsed?.chatLabels)
        ? parsed.chatLabels.map((l) => String(l || '').trim()).filter(Boolean)
        : []
    };
  } catch {
    return null;
  }
}

function writeAssigneeFilterCache(entry) {
  if (!entry?.id || !entry?.name) return;
  try {
    sessionStorage.setItem(
      ASSIGNEE_FILTER_CACHE_KEY,
      JSON.stringify({
        id: entry.id,
        name: entry.name,
        count: entry.count,
        chatLabels: (entry.chatLabels || []).slice(0, 24)
      })
    );
  } catch {
    // ignore quota / private mode
  }
}

function getConversationPriorityBadge(priority) {
  const key = String(priority || '').trim().toLowerCase();
  if (!key || key === 'none') return null;
  const custom = key.slice(0, 3).toUpperCase();
  return CHATWOOT_PRIORITY_BADGES[key] || { label: key, shortLabel: custom, headIcon: '◆', tone: 'custom' };
}

function ListFilterChip({ icon, count, active, className = '', title, ariaLabel, onClick }) {
  const n = Number(count) || 0;
  const label = ariaLabel || (n > 0 ? `${title} (${n})` : title);
  return (
    <button
      type="button"
      className={`crm-cw-filter-btn crm-cw-filter-chip ${className} ${active ? 'active' : ''}`.trim()}
      onClick={onClick}
      title={title}
      aria-label={label}
    >
      <span className="crm-cw-filter-chip__icon" aria-hidden="true">
        {icon}
      </span>
      {n > 0 ? <span className="crm-cw-filter-chip__count">{n}</span> : null}
    </button>
  );
}

function getAssigneeFilterInitials(name) {
  const n = String(name || '').trim();
  if (!n) return 'AG';
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  return n.slice(0, 2).toUpperCase();
}

function AssigneeAgentsFilterMenu({
  agents,
  activeAgentId,
  filterActive,
  agentNameById,
  onSelect,
  onClear,
  scopeRestricted = false
}) {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState(null);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);

  const enrichedAgents = useMemo(
    () =>
      (agents || []).map((agent) => ({
        ...agent,
        displayName: String(agentNameById?.[agent.id] || agent.name || '').trim() || `Agente #${agent.id}`
      })),
    [agents, agentNameById]
  );

  const totalChats = useMemo(
    () => enrichedAgents.reduce((sum, agent) => sum + (Number(agent.count) || 0), 0),
    [enrichedAgents]
  );

  const activeAgent = useMemo(
    () => enrichedAgents.find((agent) => agent.id === activeAgentId) || null,
    [enrichedAgents, activeAgentId]
  );

  const badgeCount = filterActive && activeAgent ? activeAgent.count : enrichedAgents.length;

  useEffect(() => {
    if (!open) return undefined;
    const syncPanelPos = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const panelWidth = Math.min(280, Math.max(240, window.innerWidth - 16));
      const shiftRightPx = 14;
      const left = Math.min(
        Math.max(8, rect.right - panelWidth + shiftRightPx),
        window.innerWidth - panelWidth - 8
      );
      setPanelPos({
        top: rect.bottom + 8,
        left,
        width: panelWidth
      });
    };
    syncPanelPos();
    window.addEventListener('resize', syncPanelPos);
    window.addEventListener('scroll', syncPanelPos, true);
    return () => {
      window.removeEventListener('resize', syncPanelPos);
      window.removeEventListener('scroll', syncPanelPos, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (wrapRef.current?.contains(t)) return;
      if (t?.closest?.('.crm-cw-assignee-filter__panel')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!enrichedAgents.length) return null;

  const triggerTitle = filterActive && activeAgent
    ? `Filtrando: ${activeAgent.displayName} (${activeAgent.count})`
    : `${enrichedAgents.length} operador${enrichedAgents.length === 1 ? '' : 'es'} · ${totalChats} chats asignados`;

  const panel =
    open &&
    panelPos &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        className="crm-cw-assignee-filter__panel"
        role="menu"
        aria-label="Filtrar por operador"
        style={{
          position: 'fixed',
          top: panelPos.top,
          left: panelPos.left,
          width: panelPos.width,
          zIndex: 12000
        }}
      >
        <div className="crm-cw-assignee-filter__head">
          <div>
            <div className="crm-cw-assignee-filter__title">Operadores</div>
            <div className="crm-cw-assignee-filter__subtitle">
              {enrichedAgents.length} asignado{enrichedAgents.length === 1 ? '' : 's'} · {totalChats}{' '}
              conversación{totalChats === 1 ? '' : 'es'}
            </div>
          </div>
        </div>
        <div className="crm-cw-assignee-filter__list">
          {enrichedAgents.map((agent) => {
            const isActive =
              filterActive && Number(activeAgentId) === Number(agent.id);
            return (
              <button
                key={agent.id}
                type="button"
                role="menuitemradio"
                aria-checked={isActive}
                className={`crm-cw-assignee-filter__row${isActive ? ' is-active' : ''}`}
                onClick={() => {
                  onSelect(agent.id);
                  setOpen(false);
                }}
              >
                <span className="crm-cw-assignee-filter__avatar" aria-hidden="true">
                  {getAssigneeFilterInitials(agent.displayName)}
                </span>
                <span className="crm-cw-assignee-filter__label">
                  <span className="crm-cw-assignee-filter__name">{agent.displayName}</span>
                  <span className="crm-cw-assignee-filter__meta">
                    {agent.count === 1 ? '1 conversación' : `${agent.count} conversaciones`}
                  </span>
                </span>
                <span className="crm-cw-assignee-filter__pill">{agent.count}</span>
                {isActive ? (
                  <ChatwootAgentCheckIcon className="crm-cw-assignee-filter__check-icon" />
                ) : (
                  <span className="crm-cw-assignee-filter__idle" aria-hidden="true" />
                )}
              </button>
            );
          })}
        </div>
        {filterActive && !scopeRestricted ? (
          <button
            type="button"
            className="crm-cw-assignee-filter__clear"
            onClick={() => {
              onClear();
              setOpen(false);
            }}
          >
            Ver todos los chats
          </button>
        ) : null}
      </div>,
      document.body
    );

  return (
    <div className="crm-cw-assignee-filter" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`crm-cw-filter-btn crm-cw-filter-chip crm-cw-filter-btn--assignee crm-cw-assignee-filter__trigger ${
          filterActive || open ? 'active' : ''
        }`.trim()}
        onClick={() => setOpen((v) => !v)}
        title={triggerTitle}
        aria-label={triggerTitle}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="crm-cw-filter-chip__icon" aria-hidden="true">
          ☰
        </span>
        {badgeCount > 0 ? <span className="crm-cw-filter-chip__count">{badgeCount}</span> : null}
      </button>
      {panel}
    </div>
  );
}

function renderChatHeadMetaChip({ kindLabel, icon, value, toneClass, title }) {
  return (
    <span
      className={`crm-cw-meta-chip crm-cw-meta-chip--compact ${toneClass}`}
      title={title}
      aria-label={`${kindLabel}: ${value}`}
    >
      <span className="crm-cw-meta-chip__icon" aria-hidden="true">
        {icon}
      </span>
      <span className="crm-cw-meta-chip__line">
        <span className="crm-cw-meta-chip__kind">{kindLabel}</span>
        <span className="crm-cw-meta-chip__sep" aria-hidden="true">
          ·
        </span>
        <span className="crm-cw-meta-chip__value">{value}</span>
      </span>
    </span>
  );
}

function formatChatwootProfileMeta(conversation) {
  const rawStatus = String(conversation?.status || '').trim().toLowerCase();
  const statusLabel = CHATWOOT_STATUS_LABELS[rawStatus] || (rawStatus || 'Sin estado');
  const inboxId = conversation?.inbox_id;
  if (inboxId == null || inboxId === '') return statusLabel;
  return `${statusLabel} · Bandeja Chatwoot #${inboxId}`;
}

function buildAsociacionHistorialEndpoint(profile) {
  if (!profile) return null;
  const dni = normalizeDigits(profile.dni_normalizado);
  if (dni) {
    return `${API_BASE_URL}/api/asociacion/historial?dni=${encodeURIComponent(dni)}`;
  }
  const params = [];
  if (profile.codcliente != null && profile.codcliente !== '') {
    params.push(`codcliente=${encodeURIComponent(profile.codcliente)}`);
  }
  if (profile.ficha_id != null && profile.ficha_id !== '') {
    params.push(`ficha_id=${encodeURIComponent(profile.ficha_id)}`);
  }
  if (!params.length) return null;
  return `${API_BASE_URL}/api/asociacion/historial?${params.join('&')}`;
}

function toHistorialDayStartMs(dateVal) {
  const d = new Date(dateVal);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function getFarmaciaMedEntriesFromRecetas(recetasList, itemsPlex, eventDate) {
  const day = toHistorialDayStartMs(eventDate);
  if (day == null) return [];
  const recIds = recetasList
    .filter((r) => r.fechaemision_plex && toHistorialDayStartMs(r.fechaemision_plex) === day)
    .map((r) => r.idreceta_plex)
    .filter((id) => id != null);
  const seen = new Set();
  const entries = [];
  itemsPlex
    .filter((it) => recIds.includes(it.idreceta))
    .forEach((it) => {
      const label = getPlexItemProductLabel(it);
      if (!label) return;
      const key = label.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      entries.push({
        label,
        isCronico: isFarmaciaMedCronico(it),
        idproducto: it.idproducto ?? it.id_producto ?? null
      });
    });
  return entries;
}

function parseFarmaciaMedTokensFromText(text) {
  const raw = String(text || '').trim();
  if (!raw || /^movimiento en farmacia$/i.test(raw)) return [];
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length >= 2 && !/^movimiento/i.test(part));
}

function getPlexItemProductLabel(it) {
  return String(
    it?.producto
      || it?.presentaci
      || it?.descripcion
      || it?.medicamento
      || it?.nombre
      || it?.nombre_generico
      || it?.medicamento_generico
      || ''
  ).trim();
}

function getPlexItemLabelKeys(it) {
  const keys = new Set();
  [
    it?.producto,
    it?.presentaci,
    it?.descripcion,
    it?.medicamento,
    it?.nombre,
    it?.nombre_generico,
    it?.medicamento_generico
  ].forEach((raw) => {
    const label = String(raw || '').trim();
    if (label.length >= 2) keys.add(label.toLowerCase());
  });
  return keys;
}

function isFarmaciaMedCronico(source) {
  const cronicaRaw = String(source?.cronica ?? '').trim().toLowerCase();
  const categoria = String(source?.categoria_nombre || source?.categorianombre || '')
    .trim()
    .toLowerCase();
  return (
    source?.es_cronico === true
    || source?.es_cronico === 1
    || ['1', 's', 'si', 'sí', 'true', 'yes'].includes(
      String(source?.es_cronico ?? '').trim().toLowerCase()
    )
    || ['1', 's', 'si', 'sí', 'true', 'yes', 'cronico', 'crónico'].includes(cronicaRaw)
    || ['cronico', 'crónico'].includes(categoria)
  );
}

function parseHistorialEventMs(ev) {
  const raw = ev?.date || ev?.fecha || ev?.fecha_movimiento;
  const day = toHistorialDayStartMs(raw);
  if (day != null) return day;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function buildFarmaciaCronicoLookup(itemsPlex) {
  const lookup = new Map();
  (itemsPlex || []).forEach((it) => {
    const chronic = isFarmaciaMedCronico(it);
    getPlexItemLabelKeys(it).forEach((key) => {
      if (!lookup.has(key)) lookup.set(key, chronic);
      else if (chronic) lookup.set(key, true);
    });
  });
  return lookup;
}

function inferCronicoFromLabel(label) {
  const norm = String(label || '').trim().toLowerCase();
  if (!norm) return false;
  return (
    /\bcron[oí]c/.test(norm)
    || /\bcrono\b/.test(norm)
    || /\btratamiento\s+cron/.test(norm)
  );
}

function resolveMedIsCronico(label, chronicLookup) {
  const key = String(label || '').trim().toLowerCase();
  if (!key) return false;
  if (inferCronicoFromLabel(label)) return true;
  if (chronicLookup.get(key)) return true;
  for (const [lookupKey, isChronic] of chronicLookup) {
    if (!isChronic) continue;
    if (key.includes(lookupKey) || lookupKey.includes(key)) return true;
  }
  return false;
}

async function enrichMedicamentosCronicoFromPadron(medicamentos, idproductos = []) {
  if (!medicamentos.length) return medicamentos;

  const labels = medicamentos.map((m) => m.label);
  const ids = [
    ...new Set(
      [
        ...idproductos,
        ...medicamentos.map((m) => m.idproducto).filter((id) => id != null && id !== '')
      ].map((id) => String(id).trim()).filter(Boolean)
    )
  ].slice(0, 120);

  try {
    const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/farmacia/cronica-padron`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ labels, idproductos: ids })
    });
    if (!res.ok) return medicamentos;
    const json = await res.json().catch(() => ({}));
    const cronicos = json?.cronicos || {};
    return medicamentos.map((med) => ({
      ...med,
      isCronico: Boolean(cronicos[med.label]) || med.isCronico || inferCronicoFromLabel(med.label)
    }));
  } catch {
    return medicamentos.map((med) => ({
      ...med,
      isCronico: med.isCronico || inferCronicoFromLabel(med.label)
    }));
  }
}

function buildFarmaciaMedicamentosCatalog({
  historial = [],
  recetas = [],
  itemsPlex = [],
  itemsGeclisa = []
}) {
  const chronicLookup = buildFarmaciaCronicoLookup([...(itemsPlex || []), ...(itemsGeclisa || [])]);
  const medsMap = new Map();

  const addMed = (nameRaw, weight = 1, eventMs = 0, forceCronico = false, idproducto = null) => {
    const name = String(nameRaw || '').trim();
    if (!name || name.length < 2) return;
    const key = name.toLowerCase();
    const isCronico = forceCronico || resolveMedIsCronico(name, chronicLookup);
    const prev = medsMap.get(key);
    if (!prev) {
      medsMap.set(key, { label: name, count: weight, lastAt: eventMs, isCronico, idproducto });
      return;
    }
    prev.count += weight;
    if (eventMs > prev.lastAt) prev.lastAt = eventMs;
    if (isCronico) prev.isCronico = true;
    if (idproducto != null && prev.idproducto == null) prev.idproducto = idproducto;
  };

  const attachItemMeta = (it) => {
    const label = getPlexItemProductLabel(it);
    if (!label || label.length < 2) return;
    const key = label.toLowerCase();
    const entry = medsMap.get(key);
    if (!entry) return;
    const itemId = it.idproducto ?? it.id_producto ?? null;
    if (itemId != null && entry.idproducto == null) entry.idproducto = itemId;
    if (isFarmaciaMedCronico(it)) entry.isCronico = true;
  };

  const recetasList = Array.isArray(recetas) ? recetas : [];
  const items = Array.isArray(itemsPlex) ? itemsPlex : [];
  const events = Array.isArray(historial) ? historial : [];

  events.forEach((ev) => {
    const origin = String(ev?.origin || ev?.tipo || '').toLowerCase();
    if (!origin.includes('farm')) return;

    const eventMs = parseHistorialEventMs(ev);
    const eventDate = ev.date || ev.fecha || ev.fecha_movimiento;
    const fromRecetas = getFarmaciaMedEntriesFromRecetas(recetasList, items, eventDate);
    if (fromRecetas.length) {
      fromRecetas.forEach((entry) =>
        addMed(entry.label, 1, eventMs, entry.isCronico, entry.idproducto)
      );
      return;
    }

    const desc = ev.descripcion || ev.desc || ev.producto || ev.servicio || ev.detalle || '';
    parseFarmaciaMedTokensFromText(desc).forEach((label) => addMed(label, 1, eventMs));
  });

  if (medsMap.size === 0 && items.length) {
    items.forEach((it) => {
      const label = getPlexItemProductLabel(it);
      addMed(label, 1, 0, isFarmaciaMedCronico(it));
    });
  }
  items.forEach(attachItemMeta);

  const all = Array.from(medsMap.values())
    .map((m) => ({
      label: toTitleCaseName(m.label),
      count: m.count,
      lastAt: m.lastAt,
      isCronico: Boolean(m.isCronico),
      idproducto: m.idproducto ?? null
    }))
    .sort((a, b) => b.count - a.count || (b.lastAt || 0) - (a.lastAt || 0) || a.label.localeCompare(b.label, 'es'));

  const farmaciaVisits = events.filter((ev) =>
    String(ev?.origin || ev?.tipo || '').toLowerCase().includes('farm')
  ).length;

  return {
    all,
    farmaciaVisits,
    uniqueCount: medsMap.size
  };
}

async function fetchClientFarmaciaInsights(profile) {
  const historialEndpoint = buildAsociacionHistorialEndpoint(profile);
  if (!historialEndpoint) {
    return { purchaseCount: null, medicamentos: [], uniqueCount: 0 };
  }

  const dni = normalizeDigits(profile?.dni_normalizado);
  let historial = [];
  let recetas = [];
  let itemsPlex = [];
  let itemsGeclisa = [];

  try {
    const requests = [fetch(historialEndpoint, { credentials: 'include' })];
    if (dni) {
      requests.push(
        fetch(
          `${API_BASE_URL}/api/asociacion-recetas/y-facturacion?search=${encodeURIComponent(dni)}&limitRecetas=200`,
          { credentials: 'include' }
        )
      );
    }

    const [histRes, recRes] = await Promise.all(requests);

    if (histRes?.ok) {
      const json = await histRes.json().catch(() => ({}));
      historial = Array.isArray(json?.historial) ? json.historial : [];
    }

    if (recRes?.ok) {
      const json = await recRes.json().catch(() => ({}));
      recetas = json?.recetas?.data ?? [];
      itemsPlex = json?.recetas?.itemsPlex ?? [];
      itemsGeclisa = json?.recetas?.itemsGeclisa ?? [];
    }
  } catch {
    return { purchaseCount: null, medicamentos: [], uniqueCount: 0 };
  }

  const { all, farmaciaVisits, uniqueCount } = buildFarmaciaMedicamentosCatalog({
    historial,
    recetas,
    itemsPlex,
    itemsGeclisa
  });
  const idproductos = [
    ...new Set(all.map((m) => m.idproducto).filter((id) => id != null && id !== '').map(String))
  ];
  const medicamentos = (await enrichMedicamentosCronicoFromPadron(all, idproductos)).sort(
    (a, b) => b.count - a.count || (b.lastAt || 0) - (a.lastAt || 0) || a.label.localeCompare(b.label, 'es')
  );

  return {
    purchaseCount: farmaciaVisits,
    medicamentos,
    uniqueCount
  };
}

function resolveContactDisplayName({ profile, conversation, cachedDisplayName }) {
  const fromDb = getClientDisplayNameFromProfile(profile);
  if (fromDb) return fromDb;

  const cached = String(cachedDisplayName || '').trim();
  if (cached) return cached;

  const whatsapp = sanitizeDisplayName(getChatwootSenderName(conversation), '');
  if (whatsapp) return toTitleCaseName(whatsapp);
  return 'Sin nombre';
}

function messageAuthor(message, selected, accountName, contactDisplayName, scopeAgentName = '') {
  if (isIncomingMessage(message)) {
    if (contactDisplayName) return contactDisplayName;
    return sanitizeDisplayName(getChatwootSenderName(selected), 'Sin nombre');
  }

  const displayAuthor = String(message?.pulse_display_author || '').trim();
  if (displayAuthor) return displayAuthor;

  const senderName = sanitizeDisplayName(message?.sender?.name, '');
  const accountNorm = String(accountName || '')
    .trim()
    .toLowerCase();
  const senderNorm = senderName.toLowerCase();
  const isChannelAccount =
    !senderName ||
    /farmagsa|admin\s*farmagsa/i.test(senderNorm) ||
    (accountNorm && (senderNorm === accountNorm || senderNorm.includes(accountNorm)));

  if (!isChannelAccount && senderName) return senderName;

  const assigneeName = sanitizeDisplayName(selected?.assignee?.name, '');
  if (assigneeName && !/farmagsa/i.test(assigneeName.toLowerCase())) return assigneeName;

  const scopeName = sanitizeDisplayName(scopeAgentName, '');
  if (scopeName) return scopeName;

  if (senderName) return senderName;
  if (accountName) return accountName;
  return 'Sin nombre';
}

function formatMessageHour(unixTime) {
  if (!unixTime) return '';
  return new Date(unixTime * 1000).toLocaleTimeString('es-AR', {
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatDayLabel(unixTime) {
  if (!unixTime) return '';
  const d = new Date(unixTime * 1000);
  const today = new Date();
  const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const startDate = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startToday - startDate) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Ayer';
  return d.toLocaleDateString('es-AR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long'
  });
}

function getDayKey(unixTime) {
  if (!unixTime) return 'unknown';
  const d = new Date(unixTime * 1000);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

function getOutgoingTickState(message, selectedConversation) {
  const status = String(message?.status || '').toLowerCase();
  if (status.includes('read')) return 'read';
  if (status.includes('deliver')) return 'delivered';

  // Fallback UX: si no hay estado granular y el chat no tiene no leídos, lo mostramos como leído.
  if ((selectedConversation?.unread_count || 0) === 0) return 'read';
  return 'sent';
}

function isIncomingMessage(message) {
  const type = message?.message_type;
  if (type === 0) return true;
  if (type === 'incoming') return true;
  if (typeof type === 'string' && type.trim() === '0') return true;
  return false;
}

/** Eventos de sistema Chatwoot (marcada, pendiente, asignación, etc.) — message_type activity = 2 */
function isActivityMessage(message) {
  const type = message?.message_type;
  if (type === 2 || type === 'activity') return true;
  if (typeof type === 'string' && type.trim() === '2') return true;

  const content = String(message?.content || '').trim();
  if (!content) return false;
  const low = content.toLowerCase();

  return (
    /conversaci[oó]n\s+(fue\s+)?(marcad|asignad|reabier|cerrad|eliminad|pospuest|resuelt)/i.test(content) ||
    /conversation\s+was\s+(marked|assigned|reopened|resolved|snoozed|deleted)/i.test(low) ||
    /marked\s+(open|resolved|pending)\s+by/i.test(low) ||
    /marcad[ao]\s+como\s+(abiert|pendient|resuelt|pospuest)/i.test(low) ||
    /(asignad[ao]|assigned)\s+(a|to)\s+/i.test(content) ||
    /(prioridad|priority)\s+(cambi|changed|set)/i.test(low) ||
    /cambi[oó]\s+la\s+prioridad/i.test(content) ||
    /changed\s+the\s+priority/i.test(low) ||
    /prioridad\s+de\s+.+\s+a\s+/i.test(content) ||
    /(etiqueta|label)\s+/.test(low) && /(agregad|added|removed|quit)/i.test(low)
  );
}

function getActivityEventText(message) {
  return String(message?.content || '').trim();
}

function parseQuotedReply(content) {
  const raw = String(content || '');
  const lines = raw.split('\n');
  if (!lines.length || !lines[0].startsWith('> ')) {
    return { quoted: '', text: raw };
  }

  const quotedLines = [];
  let i = 0;
  while (i < lines.length && lines[i].startsWith('> ')) {
    quotedLines.push(lines[i].slice(2));
    i += 1;
  }

  while (i < lines.length && lines[i].trim() === '') i += 1;
  const body = lines.slice(i).join('\n');

  const quotedRaw = quotedLines.join('\n').trim();
  let quotedAuthor = '';
  let quoted = quotedRaw;
  const authorMatch = quotedRaw.match(/^\[([^\]]+)\]\s*(.*)$/s);
  if (authorMatch) {
    quotedAuthor = String(authorMatch[1] || '').trim();
    quoted = String(authorMatch[2] || '').trim();
  }

  return {
    quoted,
    quotedAuthor,
    text: body.trim() || raw
  };
}

function getMessagePreview(content, message) {
  if (message && isActivityMessage(message)) {
    const short = getActivityEventText(message);
    return short.length > 72 ? `${short.slice(0, 69)}…` : short;
  }
  const parsed = parseQuotedReply(content);
  const text = (parsed.text || parsed.quoted || '').trim();
  if (text) return text;
  if (message && isLikelyWhatsappTycMessage(message, parsed)) {
    return isIncomingMessage(message) ? 'Aceptó TyC' : 'TyC enviados';
  }
  return '';
}

const MESSAGE_URL_REGEX = /(?:https?:\/\/|www\.)[^\s<>"']+/gi;

function trimMessageUrlTrailingPunctuation(url) {
  let clean = String(url || '');
  let suffix = '';
  while (/[.,;:!?…]$/.test(clean) && !clean.endsWith('/')) {
    suffix = clean.slice(-1) + suffix;
    clean = clean.slice(0, -1);
  }
  return { url: clean, suffix };
}

function messageUrlToHref(url) {
  const safe = String(url || '').trim();
  if (/^https?:\/\//i.test(safe)) return safe;
  if (/^www\./i.test(safe)) return `https://${safe}`;
  return safe;
}

function linkifyMessageNodes(text) {
  const raw = String(text || '');
  if (!raw) return null;

  const nodes = [];
  let lastIndex = 0;
  let linkIndex = 0;
  const re = new RegExp(MESSAGE_URL_REGEX.source, MESSAGE_URL_REGEX.flags);

  for (const match of raw.matchAll(re)) {
    const start = match.index ?? 0;
    const token = match[0];
    if (start > lastIndex) {
      nodes.push(raw.slice(lastIndex, start));
    }

    const { url, suffix } = trimMessageUrlTrailingPunctuation(token);
    if (url) {
      nodes.push(
        <a
          key={`cw-link-${linkIndex++}-${start}`}
          href={messageUrlToHref(url)}
          target="_blank"
          rel="noopener noreferrer"
          title="Abrir en una nueva pestaña"
          className="crm-cw-msg-link"
        >
          {url}
        </a>
      );
      if (suffix) nodes.push(suffix);
    } else {
      nodes.push(token);
    }

    lastIndex = start + token.length;
  }

  if (lastIndex < raw.length) {
    nodes.push(raw.slice(lastIndex));
  }

  return nodes.length ? nodes : [raw];
}

function renderMessageContent(text) {
  const nodes = linkifyMessageNodes(text);
  if (!nodes) return null;
  return nodes;
}

function getMessageAttachments(message) {
  const direct = Array.isArray(message?.attachments) ? message.attachments : [];
  const fromContentAttributes = Array.isArray(message?.content_attributes?.attachments)
    ? message.content_attributes.attachments
    : [];
  return [...direct, ...fromContentAttributes]
    .map((att, index) => {
      const url = att?.data_url || att?.thumb_url || att?.external_url || '';
      const fileType = String(att?.file_type || att?.type || '').toLowerCase();
      const fileName = String(att?.file_name || att?.filename || `adjunto-${index + 1}`);
      return {
        id: att?.id || `${fileName}-${index}`,
        url,
        fileType,
        fileName
      };
    })
    .filter((att) => Boolean(att.url));
}

const TYC_PUBLIC_URL = 'https://tyc.farmagsa.com.ar/';

function isLikelyWhatsappTycMessage(message, parsed) {
  if (!message || isActivityMessage(message)) return false;
  const text = (parsed?.text || parsed?.quoted || String(message?.content || '')).trim();
  if (text) return false;
  return getMessageAttachments(message).length === 0;
}

function renderTycPlaceholder(isIncoming) {
  const title = isIncoming
    ? '✅ Aceptación de Términos y Condiciones'
    : '📋 Términos y Condiciones (WhatsApp)';

  return (
    <div className="crm-cw-msg-content crm-cw-msg-tyc">
      <div className="crm-cw-msg-tyc-heading">{title}</div>
      {isIncoming ? (
        <p className="crm-cw-msg-tyc-sub">
          En el celular del paciente se ve la aceptación del programa de recetas crónicas. Acá podés revisar el
          documento.
        </p>
      ) : null}
      <div className="crm-cw-msg-tyc-frame-wrap">
        <iframe
          title="Términos y Condiciones Sanatorio Regional"
          src={TYC_PUBLIC_URL}
          className="crm-cw-msg-tyc-frame"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      </div>
      <a
        href={TYC_PUBLIC_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="crm-cw-msg-tyc-link"
      >
        Abrir Términos y Condiciones ↗
      </a>
    </div>
  );
}

function inferAttachmentType(attachment) {
  const rawType = String(attachment?.fileType || '').toLowerCase();
  if (rawType.startsWith('image/')) return 'image';
  if (rawType.startsWith('audio/')) return 'audio';
  if (rawType.includes('pdf')) return 'pdf';
  if (rawType.includes('vcard') || rawType.includes('x-vcard')) return 'contact';

  const target = `${attachment?.fileName || ''} ${attachment?.url || ''}`.toLowerCase();
  if (/\.(jpg|jpeg|png|gif|webp|bmp)(\?|$)/.test(target)) return 'image';
  if (/\.(mp3|wav|ogg|oga|opus|m4a|aac|webm)(\?|$)/.test(target)) return 'audio';
  if (/\.pdf(\?|$)/.test(target)) return 'pdf';
  if (/\.vcf(\?|$)/.test(target)) return 'contact';
  return 'file';
}

function normalizeDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

function normalizeText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatDni(dni) {
  const digits = normalizeDigits(dni).replace(/^0+/, '');
  if (!digits) return '-';
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** Domicilio: Geclisa primero, Plex como respaldo (misma lógica que pedidos/asociación). */
function formatDomicilioPlex(profile) {
  const geclisa = String(profile?.domicilio_geclisa ?? '').trim();
  const plex = String(profile?.domicilio_plex ?? '').trim();
  return geclisa || plex || '-';
}

/**
 * Mismo criterio que /api/asociacion/altas-whatsapp: alta digital por bot (fecha_alta_sanatorio) sin ficha GECLISA.
 */
function isAltaProgramaRecetasWhatsapp(profile) {
  if (!profile) return false;
  const hasAltaSanatorio =
    profile.fecha_alta_sanatorio != null && String(profile.fecha_alta_sanatorio).trim() !== '';
  const sinFichaGeclisa = profile.ficha_id == null || profile.ficha_id === '';
  return hasAltaSanatorio && sinFichaGeclisa;
}

function formatDateTimeAny(value) {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

function formatDateTimeFull(value) {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function normalizeDniForApi(dni) {
  return normalizeDigits(dni).replace(/^0+/, '');
}

async function fetchTycAcceptanceByDni(dni) {
  const normalized = normalizeDniForApi(dni);
  if (!normalized) return null;
  const res = await fetch(
    `${API_BASE_URL}/api/asociacion-recetas/tyc?dni=${encodeURIComponent(normalized)}`,
    { credentials: 'include' }
  );
  if (res.status === 404) return null;
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json?.message || `Error ${res.status}`);
  }
  return res.json();
}

function formatTycOrigen(origen) {
  const raw = String(origen || '').trim();
  if (!raw) return null;
  const labels = {
    n8n: 'Flujo automático',
    web: 'Web',
    manual: 'Manual',
    whatsapp: 'WhatsApp'
  };
  return labels[raw.toLowerCase()] || raw;
}

function buildTycAcceptanceLines(tyc) {
  if (!tyc) return null;
  const badge = resolveTycStatusBadge(tyc);

  if (tyc.aceptado === false) {
    return {
      status: 'not_accepted',
      headline: 'No aceptados',
      badge,
      acceptedAt: null,
      vigenteHasta: null,
      origen: null,
      primary: 'No aceptados',
      secondary: null
    };
  }

  const acceptedAt = formatDateTimeFull(tyc.accepted_at);
  const vigenteHasta = tyc.vigente_hasta ? formatDateTimeAny(tyc.vigente_hasta) : null;
  const origen = formatTycOrigen(tyc.origen);
  const acceptedAtLabel = acceptedAt !== '-' ? acceptedAt : null;
  const vigenteHastaLabel = vigenteHasta && vigenteHasta !== '-' ? vigenteHasta : null;

  return {
    status: 'accepted',
    headline: 'Aceptados',
    badge,
    acceptedAt: acceptedAtLabel,
    vigenteHasta: vigenteHastaLabel,
    origen,
    primary: 'Aceptados',
    secondary: [acceptedAtLabel, vigenteHastaLabel ? `hasta ${vigenteHastaLabel}` : null]
      .filter(Boolean)
      .join(' · ')
  };
}

function buildPhoneVariants(phoneRaw) {
  const phone = normalizeDigits(phoneRaw);
  const variants = [];
  const add = (value) => {
    const v = normalizeDigits(value);
    if (v && !variants.includes(v)) variants.push(v);
  };
  if (!phone) return variants;
  add(phone);
  if (phone.startsWith('549')) add(phone.slice(3));
  if (phone.startsWith('54')) add(phone.slice(2));
  if (phone.startsWith('0')) add(phone.slice(1));
  if (phone.length >= 10) add(phone.slice(-10));
  if (phone.length >= 8) add(phone.slice(-8));
  return variants;
}

function scoreAsociacionRow(row, phoneVariants, senderNameNorm) {
  const telPlex = normalizeDigits(row?.telefono_plex);
  const telGeclisa = normalizeDigits(row?.telefono_geclisa);
  const celGeclisa = normalizeDigits(row?.celular_geclisa);
  const phones = [telPlex, telGeclisa, celGeclisa].filter(Boolean);

  let score = 0;
  const exact = phoneVariants.some((v) => phones.some((p) => p === v));
  const partial = !exact && phoneVariants.some((v) => phones.some((p) => p.endsWith(v) || v.endsWith(p)));

  if (exact) score += 120;
  else if (partial) score += 80;
  if (exact && telPlex) score += 25;

  const rowNameNorm = normalizeText(
    `${row?.apellido_geclisa || ''} ${row?.nombre_geclisa || ''} ${row?.nombre_plex || ''}`
  );
  if (senderNameNorm && rowNameNorm) {
    if (rowNameNorm.includes(senderNameNorm) || senderNameNorm.includes(rowNameNorm)) score += 30;
    const nameTokens = senderNameNorm.split(' ').filter((t) => t.length >= 3);
    const hits = nameTokens.filter((t) => rowNameNorm.includes(t)).length;
    score += hits * 8;
  }
  return score;
}

async function fetchAsociacionBySearchTerm(term) {
  const res = await fetch(
    `${API_BASE_URL}/api/asociacion?page=1&limit=200&search=${encodeURIComponent(term)}`,
    { credentials: 'include' }
  );
  if (!res.ok) return [];
  const json = await res.json();
  return Array.isArray(json?.data) ? json.data : [];
}

async function findAsociacionByPhonePaginated(phoneVariants, senderNameNorm) {
  const limit = 200;
  const maxPages = 30;
  let best = null;
  for (let page = 1; page <= maxPages; page += 1) {
    const res = await fetch(`${API_BASE_URL}/api/asociacion?page=${page}&limit=${limit}`, {
      credentials: 'include'
    });
    if (!res.ok) break;
    const json = await res.json();
    const rows = Array.isArray(json?.data) ? json.data : [];
    if (!rows.length) break;
    const pageBest = rows
      .map((row) => ({ row, score: scoreAsociacionRow(row, phoneVariants, senderNameNorm) }))
      .sort((a, b) => b.score - a.score)[0];
    if (!best || (pageBest?.score || 0) > best.score) best = pageBest;
    if ((best?.score || 0) >= 120) break;
    if (rows.length < limit) break;
  }
  return best?.score >= 80 ? best.row : null;
}

async function fetchAsociacionByPhoneDirect(phoneRaw, senderNameNorm) {
  const res = await fetch(
    `${API_BASE_URL}/api/asociacion/by-phone?phone=${encodeURIComponent(phoneRaw)}&name=${encodeURIComponent(senderNameNorm || '')}`,
    { credentials: 'include' }
  );
  if (!res.ok) return null;
  const json = await res.json().catch(() => ({}));
  return json?.data || null;
}

function conversationsLiveFingerprint(list) {
  if (!Array.isArray(list) || !list.length) return '';
  return list
    .map((c) =>
      [
        c.id,
        c.updated_at,
        c.unread_count,
        c.status,
        c.last_message?.id,
        String(c.last_message?.content || '').slice(0, 160)
      ].join(':')
    )
    .join('|');
}

/** Búsqueda rápida por teléfono (lista lateral). */
async function resolveClientProfileQuick(conversation) {
  const senderPhone = conversation?.sender?.phone_number;
  if (!conversation?.id || !senderPhone) return null;
  try {
    const senderNameNorm = normalizeText(getChatwootSenderName(conversation));
    return await fetchAsociacionByPhoneDirect(senderPhone, senderNameNorm);
  } catch {
    return null;
  }
}

async function resolveClientProfileForConversation(conversation) {
  const senderPhone = conversation?.sender?.phone_number;
  const senderName = getChatwootSenderName(conversation);
  if (!conversation?.id || !senderPhone) return null;

  const phoneVariants = buildPhoneVariants(senderPhone);
  if (!phoneVariants.length) return null;

  try {
    const senderNameNorm = normalizeText(senderName);
    const directPhoneMatch = await fetchAsociacionByPhoneDirect(senderPhone, senderNameNorm);
    if (directPhoneMatch) return directPhoneMatch;

    const searchTerms = [];
    const addSearchTerm = (value) => {
      const v = normalizeText(value);
      if (v && !searchTerms.includes(v)) searchTerms.push(v);
    };
    addSearchTerm(senderNameNorm);
    senderNameNorm
      .split(' ')
      .filter((token) => token.length >= 3)
      .slice(0, 3)
      .forEach(addSearchTerm);

    const candidates = [];
    for (const term of searchTerms) {
      const rows = await fetchAsociacionBySearchTerm(term);
      candidates.push(...rows);
    }

    const uniqueMap = new Map();
    candidates.forEach((row) => {
      const key = `${row?.dni_normalizado || ''}|${row?.codcliente || ''}|${row?.ficha_id || ''}|${row?.nombre_plex || ''}|${row?.apellido_geclisa || ''}|${row?.nombre_geclisa || ''}`;
      if (!uniqueMap.has(key)) uniqueMap.set(key, row);
    });

    const matched = Array.from(uniqueMap.values())
      .map((row) => ({ row, score: scoreAsociacionRow(row, phoneVariants, senderNameNorm) }))
      .sort((a, b) => b.score - a.score)[0];

    if (!matched || matched.score < 80) {
      return findAsociacionByPhonePaginated(phoneVariants, senderNameNorm);
    }

    return matched.row;
  } catch {
    return null;
  }
}

function formatRecordingTime(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function getAudioExtensionFromMime(mimeType) {
  const mime = String(mimeType || '').toLowerCase();
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('mp4') || mime.includes('m4a')) return 'm4a';
  if (mime.includes('mpeg') || mime.includes('mp3')) return 'mp3';
  if (mime.includes('wav')) return 'wav';
  if (mime.includes('webm')) return 'webm';
  return 'ogg';
}

function formatAudioTime(totalSeconds) {
  const safe = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0;
  const min = String(Math.floor(safe / 60)).padStart(2, '0');
  const sec = String(safe % 60).padStart(2, '0');
  return `${min}:${sec}`;
}

function createWaveSeed(seed, barsCount = 34) {
  const text = String(seed || 'audio');
  let hash = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  const values = [];
  let state = hash >>> 0;
  for (let i = 0; i < barsCount; i += 1) {
    state = (1664525 * state + 1013904223) >>> 0;
    const n = state / 4294967295;
    values.push(0.18 + n * 0.82);
  }
  return values;
}

function PrettyAudioPlayer({ src, label }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const waveform = useMemo(() => createWaveSeed(src), [src]);
  const progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      return;
    }

    try {
      await audio.play();
    } catch {
      setIsPlaying(false);
    }
  };

  return (
    <div className={`crm-cw-audio-card ${isPlaying ? 'is-playing' : ''}`}>
      <button
        type="button"
        className="crm-cw-audio-play"
        onClick={togglePlayback}
        title={isPlaying ? 'Pausar audio' : 'Reproducir audio'}
        aria-label={isPlaying ? 'Pausar audio' : 'Reproducir audio'}
      >
        {isPlaying ? '||' : '>'}
      </button>

      <div className="crm-cw-audio-body">
        <div className="crm-cw-audio-wave" aria-hidden="true">
          {waveform.map((amp, index) => {
            const height = 4 + Math.round(amp * 10);
            const active = index / Math.max(1, waveform.length - 1) <= progress;
            return (
              <span
                key={index}
                className={`crm-cw-audio-wave-bar ${active ? 'active' : ''}`}
                style={{ height: `${height}px`, '--d': `${(index % 11) * 0.05}s` }}
              />
            );
          })}
        </div>

        <input
          type="range"
          className="crm-cw-audio-range"
          min={0}
          max={duration || 0}
          step="0.01"
          value={Math.min(currentTime, duration || 0)}
          onChange={(event) => {
            const next = Number(event.target.value || 0);
            if (audioRef.current) {
              audioRef.current.currentTime = next;
            }
            setCurrentTime(next);
          }}
          aria-label="Progreso de audio"
        />

        <div className="crm-cw-audio-meta">
          <span className="crm-cw-audio-meta-name">{label || 'Audio'}</span>
          <span className="crm-cw-audio-meta-time">
            {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
          </span>
        </div>
      </div>

      <a
        href={src}
        target="_blank"
        rel="noreferrer"
        className="crm-cw-audio-open"
        title="Abrir audio"
        aria-label="Abrir audio"
      >
        ^
      </a>

      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration || 0)}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime || 0)}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(duration || 0);
        }}
      />
    </div>
  );
}

function MicVisualizer({ spectrum = [] }) {
  // Render a smooth SVG waveform derived from the spectrum bands
  const bars = (spectrum && spectrum.length) || 0;
  const width = Math.max(80, bars * 6);
  const height = 22; // slightly smaller so waves are less prominent
  const mid = height / 2;

  const points = new Array(bars).fill(0).map((_, i) => {
    const v = Number(spectrum[i] || 0);
    const x = (i / Math.max(1, bars - 1)) * width;
    // reduce amplitude so waves are less dominant
    const y = mid - (v * (height / 2 - 6));
    return [x, y];
  });

  // build a smooth path using quadratic curves
  let d = '';
  if (points.length) {
    d = `M ${points[0][0].toFixed(1)} ${points[0][1].toFixed(1)}`;
    for (let i = 1; i < points.length; i += 1) {
      const prev = points[i - 1];
      const cur = points[i];
      const cx = ((prev[0] + cur[0]) / 2).toFixed(1);
      const cy = ((prev[1] + cur[1]) / 2).toFixed(1);
      d += ` Q ${prev[0].toFixed(1)} ${prev[1].toFixed(1)} ${cx} ${cy}`;
    }
    const last = points[points.length - 1];
    d += ` T ${last[0].toFixed(1)} ${last[1].toFixed(1)}`;
  }

  const gradientId = 'micWaveGradient';

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#9ca3af" stopOpacity="0.85" />
          <stop offset="60%" stopColor="#9ca3af" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#9ca3af" stopOpacity="0.85" />
        </linearGradient>
      </defs>
      <rect x={0} y={0} width={width} height={height} fill="transparent" />
      <path d={d} fill="none" stroke={`url(#${gradientId})`} strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round" opacity={0.95} />
    </svg>
  );
}

function Conversaciones() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isChatwootAdmin = useMemo(() => {
    if (!user) return false;
    if (String(user.username || '').trim().toLowerCase() === 'admin') return true;
    return Boolean(user.isAdmin);
  }, [user]);
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateParamValues, setTemplateParamValues] = useState([]);
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [humanTakeoverBusy, setHumanTakeoverBusy] = useState(false);
  const [humanFinalizeBusy, setHumanFinalizeBusy] = useState(false);
  const [humanReturnToBotBusy, setHumanReturnToBotBusy] = useState(false);
  const [startingCall, setStartingCall] = useState(false);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const [showProfilePanel, setShowProfilePanel] = useState(true);
  const [actionToasts, setActionToasts] = useState([]);
  const [chatwootAccountName, setChatwootAccountName] = useState('');
  const [chatwootAccountId, setChatwootAccountId] = useState(null);
  const [conversationActionBusy, setConversationActionBusy] = useState(false);
  const [conversationEstadoById, setConversationEstadoById] = useState({});
  const [showAgentPopover, setShowAgentPopover] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [activeMessageMenuId, setActiveMessageMenuId] = useState(null);
  const [forwardMessage, setForwardMessage] = useState(null);
  const [forwardSearch, setForwardSearch] = useState('');
  const [forwardingToConversationId, setForwardingToConversationId] = useState(null);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [composerMedia, setComposerMedia] = useState(null);
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);
  const [recordingElapsedMs, setRecordingElapsedMs] = useState(0);
  const [recordingError, setRecordingError] = useState('');
  const [recordingMimeType, setRecordingMimeType] = useState('');
  const [recordingLevel, setRecordingLevel] = useState(0);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const levelTimerRef = useRef(null);
  const visualizerAnimRef = useRef(null);
  const visualizerPhaseRef = useRef(0);
  const SPECTRUM_BARS = 12;
  const [recordingSpectrum, setRecordingSpectrum] = useState(new Array(SPECTRUM_BARS).fill(0));
  const prevSpectrumRef = useRef(null);
  const [clientProfile, setClientProfile] = useState(null);
  const [clientProfileConversationId, setClientProfileConversationId] = useState(null);
  const [tycAcceptance, setTycAcceptance] = useState(null);
  const [tycAcceptanceConversationId, setTycAcceptanceConversationId] = useState(null);
  const [loadingTycAcceptance, setLoadingTycAcceptance] = useState(false);
  const [showTycPopover, setShowTycPopover] = useState(false);
  const [tycPopoverStyle, setTycPopoverStyle] = useState(null);
  const [conversationProfilesById, setConversationProfilesById] = useState({});
  const [conversationDisplayNamesById, setConversationDisplayNamesById] = useState({});
  const [loadingClientProfile, setLoadingClientProfile] = useState(false);
  const [plexPurchaseCount, setPlexPurchaseCount] = useState(null);
  const [profileFarmaciaMedicamentos, setProfileFarmaciaMedicamentos] = useState([]);
  const [profileUniqueMedicamentos, setProfileUniqueMedicamentos] = useState(0);
  const [farmaciaMedSearch, setFarmaciaMedSearch] = useState('');
  const [farmaciaMedPage, setFarmaciaMedPage] = useState(1);
  const [farmaciaMedPageInput, setFarmaciaMedPageInput] = useState('1');
  const [farmaciaMedPageSize, setFarmaciaMedPageSize] = useState('5');
  const [chatSearch, setChatSearch] = useState('');
  const [chatFilter, setChatFilter] = useState('pending');
  const [inboxFilterId, setInboxFilterId] = useState(() => {
    if (typeof window === 'undefined') return 5;
    const raw = window.localStorage.getItem('crm_cw_inbox_filter_v1');
    const parsed = Number(raw);
    return CHATWOOT_INBOXES.some((item) => item.id === parsed) ? parsed : 5;
  });
  const [assigneeFilterId, setAssigneeFilterId] = useState(null);
  const [focusAssigneeId, setFocusAssigneeId] = useState(null);
  const [assigneeFilterCache, setAssigneeFilterCache] = useState(() => readAssigneeFilterCache());
  const [chatwootAgentNameById, setChatwootAgentNameById] = useState({});
  const [chatwootAssignableAgents, setChatwootAssignableAgents] = useState([]);
  const [chatwootTransferAgents, setChatwootTransferAgents] = useState([]);
  const [conversationScopeAgentId, setConversationScopeAgentId] = useState(null);
  const [conversationScopeAgentName, setConversationScopeAgentName] = useState('');
  const [canReplyMessages, setCanReplyMessages] = useState(false);
  const [canAssignAgent, setCanAssignAgent] = useState(false);
  const [canTransferConversation, setCanTransferConversation] = useState(false);
  const canAssignAgentEffective = canAssignChatwootAgent(user) || canAssignAgent;
  const conversationScopeRestricted = conversationScopeAgentId != null;
  const [listPage, setListPage] = useState(1);
  const [listPageInput, setListPageInput] = useState('1');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isCompactMobile, setIsCompactMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 640 : false
  );
  const [mobilePane, setMobilePane] = useState('list');
  const [mobileProfileOpen, setMobileProfileOpen] = useState(false);
  const [profileOpenSection, setProfileOpenSection] = useState(PROFILE_ACCORDION_SECTION.meds);
  const listSearchRef = useRef(null);
  const chatBodyRef = useRef(null);
  const inputRef = useRef(null);
  const mediaInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const recordingStartAtRef = useRef(0);
  const agentPopoverRef = useRef(null);
  const agentPopoverPanelRef = useRef(null);
  const [agentPopoverStyle, setAgentPopoverStyle] = useState(null);
  const tycPopoverRef = useRef(null);
  const tycPopoverPanelRef = useRef(null);
  const emojiBtnRef = useRef(null);
  const shouldSnapToBottomRef = useRef(false);
  const messagesRef = useRef([]);
  const hasOlderMessagesRef = useRef(false);
  const loadingOlderRef = useRef(false);
  const allowLoadOlderRef = useRef(false);
  const clientProfileRequestIdRef = useRef(0);
  const tycRequestIdRef = useRef(0);
  const conversationUnreadSnapshotRef = useRef(new Map());
  const unreadRefreshDebounceRef = useRef(null);
  const profilePrefetchQueueRef = useRef([]);
  const profilePrefetchRunningRef = useRef(false);
  const profilePrefetchAttemptedRef = useRef(new Set());
  const deepProfilePrefetchQueueRef = useRef([]);
  const deepProfilePrefetchRunningRef = useRef(false);
  const deepProfilePrefetchAttemptedRef = useRef(new Set());
  const conversationsListRef = useRef([]);
  /** IDs marcados leídos solo tras clic explícito en la lista (no por refresh de Chatwoot). */
  const userAcknowledgedReadRef = useRef(new Set());
  const lastListPageSyncForSelectionRef = useRef(null);

  function rememberResolvedDisplayNames(updates, conversationList) {
    if (!updates || !conversationList?.length) return;
    const byId = new Map(conversationList.map((conv) => [conv.id, conv]));
    const nameUpdates = {};

    Object.entries(updates).forEach(([conversationId, profile]) => {
      const conv =
        byId.get(Number(conversationId)) ||
        byId.get(conversationId) ||
        byId.get(String(conversationId));
      const displayName = getClientDisplayNameFromProfile(profile);
      if (!displayName) return;
      nameUpdates[conversationId] = displayName;
      if (conv?.sender?.phone_number) {
        persistContactDisplayName(conv.sender.phone_number, displayName);
      }
    });

    if (!Object.keys(nameUpdates).length) return;
    setConversationDisplayNamesById((prev) => ({ ...prev, ...nameUpdates }));
  }

  function hydrateCachedDisplayNames(conversationList) {
    if (!Array.isArray(conversationList) || !conversationList.length) return;
    const cachedNames = {};

    conversationList.forEach((conv) => {
      if (!conv?.id) return;
      const cached = getCachedContactDisplayName(conv.sender?.phone_number);
      if (cached) cachedNames[conv.id] = cached;
    });

    if (!Object.keys(cachedNames).length) return;
    setConversationDisplayNamesById((prev) => ({ ...prev, ...cachedNames }));
  }

  function persistInboxFilter(nextInboxId) {
    try {
      if (nextInboxId == null) {
        window.localStorage.removeItem('crm_cw_inbox_filter_v1');
        return;
      }
      window.localStorage.setItem('crm_cw_inbox_filter_v1', String(nextInboxId));
    } catch {
      // ignore persistence errors
    }
  }

  async function applyConversationProfileUpdates(updates) {
    if (!updates || !Object.keys(updates).length) return;
    setConversationProfilesById((prev) => ({ ...prev, ...updates }));
    rememberResolvedDisplayNames(updates, conversationsListRef.current);
  }

  function enqueueDeepProfilePrefetch(conversationsToResolve) {
    conversationsToResolve.forEach((conv) => {
      if (!conv?.id || !conv?.sender?.phone_number) return;
      if (deepProfilePrefetchAttemptedRef.current.has(conv.id)) return;
      if (deepProfilePrefetchQueueRef.current.some((c) => c.id === conv.id)) return;
      deepProfilePrefetchQueueRef.current.push(conv);
    });
    void drainDeepProfilePrefetchQueue();
  }

  async function drainDeepProfilePrefetchQueue() {
    if (deepProfilePrefetchRunningRef.current) return;
    deepProfilePrefetchRunningRef.current = true;

    while (deepProfilePrefetchQueueRef.current.length > 0) {
      const chunk = deepProfilePrefetchQueueRef.current.splice(0, 3);
      const updates = {};
      const tasks = chunk.map(async (conv) => {
        if (deepProfilePrefetchAttemptedRef.current.has(conv.id)) return;
        deepProfilePrefetchAttemptedRef.current.add(conv.id);
        const profile = await resolveClientProfileForConversation(conv);
        if (profile) updates[conv.id] = profile;
      });
      await Promise.all(tasks);
      await applyConversationProfileUpdates(updates);
    }

    deepProfilePrefetchRunningRef.current = false;
  }

  async function drainQuickProfilePrefetchQueue() {
    if (profilePrefetchRunningRef.current) return;
    profilePrefetchRunningRef.current = true;

    while (profilePrefetchQueueRef.current.length > 0) {
      const chunk = profilePrefetchQueueRef.current.splice(0, PROFILE_PREFETCH_CONCURRENCY);
      const updates = {};
      const needDeep = [];

      await Promise.all(
        chunk.map(async (conv) => {
          profilePrefetchAttemptedRef.current.add(conv.id);
          try {
            const profile = await resolveClientProfileQuick(conv);
            if (profile) {
              updates[conv.id] = profile;
              if (getClientDisplayNameFromProfile(profile)) return;
            }
          } catch {
            // sigue a búsqueda profunda
          }
          if (!deepProfilePrefetchAttemptedRef.current.has(conv.id)) {
            needDeep.push(conv);
          }
        })
      );

      await applyConversationProfileUpdates(updates);
      if (needDeep.length) enqueueDeepProfilePrefetch(needDeep);
    }

    profilePrefetchRunningRef.current = false;
  }

  function enqueueConversationProfilePrefetch(conversationList) {
    if (!Array.isArray(conversationList) || !conversationList.length) return;

    hydrateCachedDisplayNames(conversationList);

    const sorted = [...conversationList].sort(
      (a, b) => Number(b.updated_at || 0) - Number(a.updated_at || 0)
    );

    sorted.forEach((conv) => {
      if (!conv?.id || !conv?.sender?.phone_number) return;
      if (profilePrefetchAttemptedRef.current.has(conv.id)) return;
      if (profilePrefetchQueueRef.current.some((c) => c.id === conv.id)) return;
      profilePrefetchQueueRef.current.push(conv);
    });

    void drainQuickProfilePrefetchQueue();
  }

  async function refreshConversationEstados(conversationList) {
    const ids = (conversationList || []).map((conv) => conv?.id).filter((id) => id != null && id !== '');
    if (!ids.length) {
      setConversationEstadoById({});
      return;
    }
    try {
      const json = await fetchConversationsEstadoBulk(ids);
      setConversationEstadoById(json?.data && typeof json.data === 'object' ? json.data : {});
    } catch {
      // Sin estados del bot: se muestra icono Chatwoot (pendiente/resuelta).
    }
  }

  async function loadConversations({ silent = false } = {}) {
    try {
      if (!silent) setLoadingConversations(true);
      setError('');
      const res = await fetch(`${API_BASE_URL}/api/chatwoot/conversations?status=all&page=1`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const json = await res.json();
      const list = Array.isArray(json?.data) ? json.data : [];
      setConversations((prev) => {
        const merged = mergeServerConversationList(list, prev, userAcknowledgedReadRef.current);
        conversationsListRef.current = merged;
        const fpPrev = conversationsLiveFingerprint(prev);
        const fpNext = conversationsLiveFingerprint(merged);
        if (fpPrev === fpNext && prev.length === merged.length) return prev;
        return merged;
      });
      void refreshConversationEstados(list);
      enqueueConversationProfilePrefetch(list);
      setLastRefreshAt(Date.now());
      setSelectedId((current) => {
        if (!current) return null;
        if (!list.length) return null;
        const exists = list.some((conv) => conv.id === current);
        return exists ? current : null;
      });
    } catch {
      setError('No se pudieron cargar las conversaciones de Chatwoot.');
    } finally {
      if (!silent) setLoadingConversations(false);
    }
  }

  function scrollChatBodyToBottom() {
    const body = chatBodyRef.current;
    if (!body) return;
    const prevBehavior = body.style.scrollBehavior;
    body.style.scrollBehavior = 'auto';
    body.scrollTop = body.scrollHeight;
    body.style.scrollBehavior = prevBehavior;
  }

  function scheduleSnapChatToBottom({ enableOlderLoadAfter = true } = {}) {
    allowLoadOlderRef.current = false;
    scrollChatBodyToBottom();
    requestAnimationFrame(() => {
      scrollChatBodyToBottom();
      requestAnimationFrame(scrollChatBodyToBottom);
    });
    [50, 120, 250, 450, 700].forEach((ms) => {
      window.setTimeout(scrollChatBodyToBottom, ms);
    });
    if (enableOlderLoadAfter) {
      window.setTimeout(() => {
        allowLoadOlderRef.current = true;
      }, 850);
    }
  }

  async function loadMessages(conversationId, { silent = false } = {}) {
    if (!conversationId) {
      setMessages([]);
      messagesRef.current = [];
      setHasOlderMessages(false);
      hasOlderMessagesRef.current = false;
      return;
    }
    if (!silent) shouldSnapToBottomRef.current = true;
    if (!silent) allowLoadOlderRef.current = false;
    try {
      if (!silent) setLoadingMessages(true);
      const lastKnownId = silent ? messagesRef.current.at(-1)?.id : null;
      const query = lastKnownId ? `?after=${encodeURIComponent(String(lastKnownId))}` : '';
      const res = await fetch(
        `${API_BASE_URL}/api/chatwoot/conversations/${conversationId}/messages${query}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const json = await res.json();
      const incoming = Array.isArray(json?.data) ? json.data : [];
      if (silent && lastKnownId) {
        const prevMessages = messagesRef.current;
        const knownIds = new Set(prevMessages.map((msg) => msg.id));
        const toAdd = incoming.filter((msg) => msg?.id != null && !knownIds.has(msg.id));
        if (toAdd.length) {
          const next = [...prevMessages, ...toAdd];
          messagesRef.current = next;
          setMessages(next);
          void loadConversations({ silent: true });
        }
        requestAnimationFrame(() => {
          const body = chatBodyRef.current;
          if (!body) return;
          const distanceToBottom = body.scrollHeight - body.scrollTop - body.clientHeight;
          if (distanceToBottom < 180) scrollChatBodyToBottom();
        });
      } else {
        messagesRef.current = incoming;
        setMessages(incoming);
        const hasOlder = Boolean(json?.meta?.has_older);
        setHasOlderMessages(hasOlder);
        hasOlderMessagesRef.current = hasOlder;
      }
      setLastRefreshAt(Date.now());
    } catch {
      if (!silent) setError('No se pudieron cargar los mensajes.');
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  }

  async function loadOlderMessages() {
    const conversationId = selectedId;
    if (!conversationId || loadingOlderRef.current || !hasOlderMessagesRef.current) return;
    const oldestId = messagesRef.current[0]?.id;
    if (!oldestId) return;

    loadingOlderRef.current = true;
    setLoadingOlderMessages(true);
    const body = chatBodyRef.current;
    const prevScrollHeight = body?.scrollHeight ?? 0;
    const prevScrollTop = body?.scrollTop ?? 0;

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/chatwoot/conversations/${conversationId}/messages?before=${encodeURIComponent(String(oldestId))}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const json = await res.json();
      if (selectedId !== conversationId) return;

      const incoming = Array.isArray(json?.data) ? json.data : [];
      const hasOlder = Boolean(json?.meta?.has_older);
      setHasOlderMessages(hasOlder);
      hasOlderMessagesRef.current = hasOlder;

      if (incoming.length) {
        setMessages((prev) => {
          const ids = new Set(prev.map((msg) => msg.id));
          const older = incoming.filter((msg) => msg?.id != null && !ids.has(msg.id));
          if (!older.length) return prev;
          const next = [...older, ...prev];
          messagesRef.current = next;
          return next;
        });
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const el = chatBodyRef.current;
            if (!el || selectedId !== conversationId) return;
            const nextScrollHeight = el.scrollHeight;
            el.scrollTop = prevScrollTop + (nextScrollHeight - prevScrollHeight);
          });
        });
      }
    } catch {
      setError('No se pudieron cargar mensajes anteriores.');
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlderMessages(false);
    }
  }

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    hasOlderMessagesRef.current = hasOlderMessages;
  }, [hasOlderMessages]);

  useEffect(() => {
    async function loadChatwootAccount() {
      try {
        const res = await fetch(`${API_BASE_URL}/api/chatwoot/account`, {
          credentials: 'include'
        });
        if (!res.ok) return;
        const json = await res.json();
        const name = String(json?.data?.name || '').trim();
        const id = json?.data?.id;
        const scope = json?.data?.session_scope;
        if (name) setChatwootAccountName(name);
        if (id != null) setChatwootAccountId(id);
        const scopedId = Number(scope?.agent_id);
        if (scope?.restricted && Number.isFinite(scopedId) && scopedId > 0) {
          setConversationScopeAgentId(scopedId);
          setConversationScopeAgentName(String(scope?.agent_name || '').trim());
        } else {
          setConversationScopeAgentId(null);
          setConversationScopeAgentName('');
        }
        setCanReplyMessages(Boolean(scope?.can_reply_messages));
        setCanAssignAgent(Boolean(scope?.can_assign_agent));
        setCanTransferConversation(Boolean(scope?.can_transfer_conversation));
      } catch {
        // fallback silencioso
      }
    }

    loadChatwootAccount();
    loadConversations();

    const onExternalRefresh = () => {
      if (document.hidden) return;
      loadConversations({ silent: true });
    };
    window.addEventListener(CHATWOOT_CONVERSATIONS_CHANGED_EVENT, onExternalRefresh);

    const intervalId = setInterval(() => {
      if (document.hidden) return;
      loadConversations({ silent: true });
    }, LIVE_REFRESH_MS);

    const onVisibility = () => {
      if (!document.hidden) loadConversations({ silent: true });
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener(CHATWOOT_CONVERSATIONS_CHANGED_EVENT, onExternalRefresh);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => {
    if (!conversationScopeAgentId) return;
    setFocusAssigneeId(conversationScopeAgentId);
    setAssigneeFilterId(null);
    setChatFilter('pending');
  }, [conversationScopeAgentId]);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(PULSE_CMD_CONV_UNREAD_KEY) === '1') {
        sessionStorage.removeItem(PULSE_CMD_CONV_UNREAD_KEY);
        setAssigneeFilterId(null);
        setChatFilter('unread');
        setSelectedId(null);
        if (isCompactMobile) setMobilePane('list');
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!conversations.length) return;
    let pendingId = null;
    try {
      pendingId = sessionStorage.getItem(OPEN_CONVERSATION_KEY);
    } catch {
      pendingId = null;
    }
    const convId = Number(pendingId);
    if (!Number.isFinite(convId) || convId <= 0) return;
    if (!conversations.some((c) => Number(c.id) === convId)) return;
    try {
      sessionStorage.removeItem(OPEN_CONVERSATION_KEY);
    } catch {
      // ignore
    }
    openConversation(convId, { markRead: false });
  }, [conversations]);

  useEffect(() => {
    loadMessages(selectedId);
    shouldSnapToBottomRef.current = true;
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) return undefined;
    const intervalId = setInterval(() => {
      if (document.hidden) return;
      loadMessages(selectedId, { silent: true });
    }, LIVE_REFRESH_MS);
    return () => clearInterval(intervalId);
  }, [selectedId]);

  const selectedConversation = useMemo(
    () => conversations.find((c) => c.id === selectedId) || null,
    [conversations, selectedId]
  );

  const getProfileForConversation = (conv) => {
    if (!conv) return null;
    if (
      conv.id === selectedId &&
      clientProfile &&
      clientProfileConversationId === conv.id
    ) {
      return clientProfile;
    }
    return conversationProfilesById[conv.id] || null;
  };

  const getConversationDisplayName = (conv) =>
    resolveContactDisplayName({
      profile: getProfileForConversation(conv),
      conversation: conv,
      cachedDisplayName: conversationDisplayNamesById[conv?.id]
    });

  const unreadMessageToasts = useMemo(
    () =>
      conversations
        .filter((conv) => isUnreadMessageToastCandidate(conv, selectedId))
        .sort((a, b) => Number(b.updated_at || 0) - Number(a.updated_at || 0))
        .map((conv) => ({
          id: `unread-${conv.id}`,
          conversationId: conv.id,
          lastMessageId: conv.last_message?.id ?? null,
          title: getConversationDisplayName(conv),
          body:
            getMessagePreview(conv.last_message?.content, conv.last_message) ||
            'Mensaje sin leer',
          unreadCount: Number(conv.unread_count || 0)
        })),
    [
      conversations,
      selectedId,
      conversationProfilesById,
      conversationDisplayNamesById,
      clientProfile
    ]
  );

  const toasts = useMemo(
    () => [...actionToasts, ...unreadMessageToasts],
    [actionToasts, unreadMessageToasts]
  );

  const selectedContactDisplayName = useMemo(() => {
    if (!selectedConversation) return '';
    return getConversationDisplayName(selectedConversation);
  }, [
    selectedConversation,
    conversationProfilesById,
    conversationDisplayNamesById,
    clientProfile,
    clientProfileConversationId,
    selectedId
  ]);

  const selectedHandlingMode = useMemo(() => {
    if (!selectedConversation) return null;
    return getConversationHandlingForConv(selectedConversation, conversationEstadoById);
  }, [selectedConversation, conversationEstadoById]);
  const selectedBotDisplayName = useMemo(() => {
    if (selectedHandlingMode?.kind !== 'bot') return 'Anita';
    return getBotDisplayNameForInbox(selectedConversation?.inbox_id);
  }, [selectedHandlingMode?.kind, selectedConversation?.inbox_id]);

  const isSelectedSpectatorMode = useMemo(
    () =>
      Boolean(
        selectedConversation && isBotGuardedConversation(selectedConversation, conversationEstadoById)
      ),
    [selectedConversation, conversationEstadoById]
  );
  const isSelectedFinishedFlow = useMemo(
    () => selectedHandlingMode?.kind === 'finished',
    [selectedHandlingMode]
  );
  const canHumanTakeoverSelected = isSelectedSpectatorMode || isSelectedFinishedFlow;
  const isSelectedHumanMode = useMemo(
    () => selectedHandlingMode?.kind === 'human',
    [selectedHandlingMode]
  );

  const canTransferSelectedConversation = useMemo(() => {
    if (!canTransferConversation || !isSelectedHumanMode || !selectedConversation) return false;
    if (canAssignAgentEffective) return true;
    const assigneeId = Number(selectedConversation.assignee?.id);
    if (!Number.isFinite(assigneeId) || assigneeId <= 0) return false;
    return assigneeId === Number(conversationScopeAgentId);
  }, [
    canTransferConversation,
    isSelectedHumanMode,
    selectedConversation,
    canAssignAgentEffective,
    conversationScopeAgentId
  ]);

  const panelContactEmail = useMemo(
    () => formatClientContactEmail(getProfileForConversation(selectedConversation)),
    [
      selectedConversation,
      conversationProfilesById,
      clientProfile,
      clientProfileConversationId,
      selectedId
    ]
  );

  const inboxConversations = useMemo(
    () =>
      inboxFilterId == null
        ? []
        : conversations.filter((conv) => Number(conv?.inbox_id) === Number(inboxFilterId)),
    [conversations, inboxFilterId]
  );

  const unreadConversationsCount = useMemo(
    () => inboxConversations.reduce((acc, conv) => acc + (isConversationUnread(conv) ? 1 : 0), 0),
    [inboxConversations]
  );

  const resolvedConversationsCount = useMemo(
    () =>
      inboxConversations.reduce(
        (acc, conv) => acc + (isConversationInResueltasList(conv, conversationEstadoById) ? 1 : 0),
        0
      ),
    [inboxConversations, conversationEstadoById]
  );

  const noAtendidasConversationsCount = useMemo(
    () =>
      inboxConversations.reduce(
        (acc, conv) => acc + (isConversationNoAtendida(conv, conversationEstadoById) ? 1 : 0),
        0
      ),
    [inboxConversations, conversationEstadoById]
  );

  const allConversationsCount = inboxConversations.length;

  const assigneesById = useMemo(() => {
    const map = new Map();
    inboxConversations.forEach((conv) => {
      const assigneeId = conv.assignee?.id;
      const assigneeName = sanitizeDisplayName(conv.assignee?.name, '');
      if (!assigneeId || !assigneeName) return;

      const label = resolveContactDisplayName({
        profile: getProfileForConversation(conv),
        conversation: conv,
        cachedDisplayName: conversationDisplayNamesById[conv.id]
      });

      const id = Number(assigneeId);
      if (!Number.isFinite(id) || id <= 0) return;

      const existing = map.get(id);
      if (existing) {
        existing.count += 1;
        existing.chatLabels.push(label);
        return;
      }

      map.set(id, {
        id,
        name: assigneeName,
        count: 1,
        chatLabels: [label]
      });
    });
    return map;
  }, [
    inboxConversations,
    conversationProfilesById,
    conversationDisplayNamesById,
    clientProfile,
    clientProfileConversationId,
    selectedId
  ]);

  useEffect(() => {
    if (!assigneesById.size) {
      setFocusAssigneeId(assigneeFilterCache?.id ?? null);
      return;
    }

    if (chatFilter === 'assignee' && assigneeFilterId != null) {
      const pickedId = Number(assigneeFilterId);
      if (assigneesById.has(pickedId)) {
        setFocusAssigneeId(pickedId);
        return;
      }
    }

    const selectedConv = conversations.find((conv) => conv.id === selectedId);
    const selectedAssigneeId = Number(selectedConv?.assignee?.id);
    if (Number.isFinite(selectedAssigneeId) && selectedAssigneeId > 0 && assigneesById.has(selectedAssigneeId)) {
      setFocusAssigneeId(selectedAssigneeId);
      return;
    }

    setFocusAssigneeId((prev) => {
      const prevId = Number(prev);
      if (Number.isFinite(prevId) && prevId > 0 && assigneesById.has(prevId)) return prevId;
      return pickPrimaryAssigneeId(assigneesById);
    });
  }, [selectedId, conversations, assigneesById, assigneeFilterCache?.id, chatFilter, assigneeFilterId]);

  const assigneeFilterButton = useMemo(() => {
    if (!assigneesById.size) return null;
    const activeId =
      focusAssigneeId != null && assigneesById.has(focusAssigneeId)
        ? focusAssigneeId
        : pickPrimaryAssigneeId(assigneesById);
    return activeId ? assigneesById.get(activeId) : null;
  }, [assigneesById, focusAssigneeId]);

  const assigneeFilterButtonDisplay = useMemo(
    () => assigneeFilterButton || assigneeFilterCache,
    [assigneeFilterButton, assigneeFilterCache]
  );

  const assigneeFilterOptions = useMemo(() => {
    return [...assigneesById.values()]
      .map((entry) => ({
        ...entry,
        name: chatwootAgentNameById[entry.id] || entry.name
      }))
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return String(a.name).localeCompare(String(b.name), 'es', { sensitivity: 'base' });
      });
  }, [assigneesById, chatwootAgentNameById]);

  useEffect(() => {
    let cancelled = false;
    fetchChatwootMeta()
      .then((json) => {
        if (cancelled) return;
        const agents = json?.data?.agents || [];
        const transferAgents = json?.data?.transfer_agents;
        setChatwootAssignableAgents(agents);
        setChatwootTransferAgents(
          Array.isArray(transferAgents) && transferAgents.length ? transferAgents : agents
        );
        const map = {};
        agents.forEach((agent) => {
          const id = Number(agent.id);
          const label = String(agent.name || '').trim();
          if (Number.isFinite(id) && id > 0 && label) map[id] = label;
        });
        setChatwootAgentNameById(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!assigneeFilterButton) return;
    if (chatFilter === 'assignee' && assigneeFilterId != null) return;
    setAssigneeFilterCache(assigneeFilterButton);
    writeAssigneeFilterCache(assigneeFilterButton);
  }, [assigneeFilterButton, chatFilter, assigneeFilterId]);

  const filteredConversations = useMemo(() => {
    const term = chatSearch.trim().toLowerCase();
    const collator = (a, b) => {
      const priorityDiff =
        getConversationPriorityRank(b.priority) - getConversationPriorityRank(a.priority);
      if (priorityDiff !== 0) return priorityDiff;

      return Number(b.updated_at || 0) - Number(a.updated_at || 0);
    };

    const resolvedCollator = (a, b) => {
      const unreadDiff = (b.unread_count > 0 ? 1 : 0) - (a.unread_count > 0 ? 1 : 0);
      if (unreadDiff !== 0) return unreadDiff;

      const assigneeA = sanitizeDisplayName(a.assignee?.name, 'Sin asignar');
      const assigneeB = sanitizeDisplayName(b.assignee?.name, 'Sin asignar');
      const assigneeCmp = assigneeA.localeCompare(assigneeB, 'es');
      if (assigneeCmp !== 0) return assigneeCmp;

      return collator(a, b);
    };

    const sortFn = chatFilter === 'resolved' ? resolvedCollator : collator;

    if (inboxFilterId == null) return [];

    return [...inboxConversations]
      .map((conv) => ({
        ...conv,
        displayName: getConversationDisplayName(conv)
      }))
      .filter((conv) => {
        if (Number(conv?.inbox_id) !== Number(inboxFilterId)) {
          return false;
        }
        if (
          conversationScopeRestricted &&
          !isConversationVisibleInAgentScope(conv, conversationScopeAgentId, conversationEstadoById)
        ) {
          return false;
        }
        if (chatFilter === 'pending' && !isConversationNoAtendida(conv, conversationEstadoById)) {
          return false;
        }
        if (chatFilter === 'unread' && !isConversationUnread(conv)) {
          return false;
        }
        if (chatFilter === 'resolved' && !isConversationInResueltasList(conv, conversationEstadoById)) {
          return false;
        }
        if (chatFilter === 'assignee') {
          const filterId = Number(assigneeFilterId);
          const convAssigneeId = Number(conv.assignee?.id);
          if (!Number.isFinite(filterId) || filterId <= 0 || convAssigneeId !== filterId) return false;
        }
        if (!term) return true;
        const name = conv.displayName.toLowerCase();
        const last = String(conv.last_message?.content || '').toLowerCase();
        return name.includes(term) || last.includes(term);
      })
      .sort(sortFn);
  }, [
    conversations,
    chatSearch,
    chatFilter,
    inboxFilterId,
    assigneeFilterId,
    conversationScopeRestricted,
    conversationScopeAgentId,
    conversationEstadoById,
    conversationProfilesById,
    conversationDisplayNamesById,
    clientProfile,
    selectedId,
    inboxConversations
  ]);

  const listTotalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredConversations.length / CONVERSATIONS_LIST_PAGE_SIZE)),
    [filteredConversations.length]
  );

  const listPageSafe = Math.min(Math.max(1, listPage), listTotalPages);

  const paginatedConversations = useMemo(() => {
    const start = (listPageSafe - 1) * CONVERSATIONS_LIST_PAGE_SIZE;
    return filteredConversations.slice(start, start + CONVERSATIONS_LIST_PAGE_SIZE);
  }, [filteredConversations, listPageSafe]);

  const listPageRangeLabel = useMemo(() => {
    if (!filteredConversations.length) {
      return conversations.length > 0 ? 'Sin resultados' : '0 chats';
    }
    const start = (listPageSafe - 1) * CONVERSATIONS_LIST_PAGE_SIZE + 1;
    const end = Math.min(listPageSafe * CONVERSATIONS_LIST_PAGE_SIZE, filteredConversations.length);
    return `${start}–${end} de ${filteredConversations.length}`;
  }, [filteredConversations.length, listPageSafe, conversations.length]);

  const listEmptyState = useMemo(() => {
    if (filteredConversations.length > 0) return null;
    if (inboxFilterId == null) {
      return {
        icon: '↔',
        title: 'Elegí un canal',
        hint: 'Seleccioná GSA o Sanatorio Regional para ver sus conversaciones.'
      };
    }
    const term = chatSearch.trim();
    if (!conversations.length) {
      return {
        icon: '💬',
        title: 'No hay conversaciones',
        hint: 'Cuando llegue un mensaje por WhatsApp o Chatwoot, vas a verlo acá.'
      };
    }
    if (term && chatFilter === 'pending') {
      const shortTerm = term.length > 36 ? `${term.slice(0, 33)}…` : term;
      return {
        icon: '🔎',
        title: 'Sin coincidencias',
        hint: `No hay chats en curso (sin finalizar) que coincidan con «${shortTerm}».`,
        clearSearch: true,
        showPending: true,
        showAll: true
      };
    }
    if (term && chatFilter === 'unread') {
      const shortTerm = term.length > 36 ? `${term.slice(0, 33)}…` : term;
      return {
        icon: '🔎',
        title: 'Sin coincidencias',
        hint: `No hay chats sin leer que coincidan con «${shortTerm}».`,
        clearSearch: true,
        showPending: true,
        showAll: true
      };
    }
    if (term && chatFilter === 'resolved') {
      const shortTerm = term.length > 36 ? `${term.slice(0, 33)}…` : term;
      return {
        icon: '🔎',
        title: 'Sin coincidencias',
        hint: `No hay chats resueltos que coincidan con «${shortTerm}».`,
        clearSearch: true,
        showAll: true
      };
    }
    if (term) {
      const shortTerm = term.length > 36 ? `${term.slice(0, 33)}…` : term;
      return {
        icon: '🔎',
        title: 'Sin resultados',
        hint: `No encontramos chats para «${shortTerm}». Probá con otro nombre, teléfono o parte del último mensaje.`,
        clearSearch: true
      };
    }
    if (chatFilter === 'pending') {
      return {
        icon: '✅',
        title: 'Sin chats en curso',
        hint:
          'Acá van los chats en curso (sin finalizar). Los cerrados están en Resueltos; los con mensaje nuevo sin leer, en No leídos.',
        showAll: true
      };
    }
    if (chatFilter === 'unread') {
      return {
        icon: '✅',
        title: 'Todo al día',
        hint: 'No tenés chats sin leer en este momento.',
        showPending: true,
        showAll: true
      };
    }
    if (chatFilter === 'assignee') {
      const agentName = assigneeFilterButton?.name || 'este agente';
      return {
        icon: '👤',
        title: 'Sin chats de este agente',
        hint: `No hay chats asignados a ${agentName} con los filtros actuales.`,
        showPending: true,
        showAll: true
      };
    }
    if (chatFilter === 'resolved') {
      return {
        icon: '✓',
        title: 'Sin chats resueltos',
        hint:
          'Aparecen chats con estado Resuelto en Chatwoot o fase finalizado en Pulse, aunque estén asignados a otro agente.',
        showAll: true
      };
    }
    return {
      icon: '💬',
      title: 'No hay conversaciones',
      hint: 'No hay conversaciones disponibles.'
    };
  }, [filteredConversations.length, conversations.length, chatSearch, chatFilter, assigneeFilterButton?.name, inboxFilterId]);

  const visibleInboxMeta = useMemo(
    () => CHATWOOT_INBOXES.find((item) => item.id === Number(inboxFilterId)) || CHATWOOT_INBOXES[0],
    [inboxFilterId]
  );

  const inboxCounts = useMemo(() => {
    const counts = {};
    CHATWOOT_INBOXES.forEach((item) => {
      counts[item.id] = conversations.filter((conv) => Number(conv?.inbox_id) === item.id).length;
    });
    return counts;
  }, [conversations]);

  useEffect(() => {
    persistInboxFilter(inboxFilterId);
  }, [inboxFilterId]);

  useEffect(() => {
    if (inboxFilterId != null) return;
    setSelectedId(null);
  }, [inboxFilterId]);

  useEffect(() => {
    if (!selectedId) return;
    if (inboxFilterId == null) return;
    const selected = conversations.find((conv) => Number(conv.id) === Number(selectedId));
    if (!selected) return;
    if (Number(selected?.inbox_id) === Number(inboxFilterId)) return;
    setSelectedId(null);
  }, [filteredConversations, inboxFilterId, selectedId, conversations]);

  const farmaciaMedSearchTerm = farmaciaMedSearch.trim().toLowerCase();

  const filteredFarmaciaMeds = useMemo(() => {
    if (!profileFarmaciaMedicamentos.length) return [];
    if (!farmaciaMedSearchTerm) return profileFarmaciaMedicamentos;
    return profileFarmaciaMedicamentos.filter((med) =>
      med.label.toLowerCase().includes(farmaciaMedSearchTerm)
    );
  }, [profileFarmaciaMedicamentos, farmaciaMedSearchTerm]);

  const farmaciaMedShowAll = farmaciaMedPageSize === 'all';
  const farmaciaMedCompactList = farmaciaMedPageSize === '5' && !farmaciaMedShowAll;

  const farmaciaMedPageSizeNum = useMemo(() => {
    if (farmaciaMedShowAll) return Math.max(1, filteredFarmaciaMeds.length);
    const parsed = Number.parseInt(String(farmaciaMedPageSize), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 3;
  }, [farmaciaMedPageSize, farmaciaMedShowAll, filteredFarmaciaMeds.length]);

  const farmaciaMedTotalPages = useMemo(() => {
    if (farmaciaMedShowAll) return 1;
    return Math.max(1, Math.ceil(filteredFarmaciaMeds.length / farmaciaMedPageSizeNum));
  }, [filteredFarmaciaMeds.length, farmaciaMedPageSizeNum, farmaciaMedShowAll]);

  const farmaciaMedPageSafe = Math.min(Math.max(1, farmaciaMedPage), farmaciaMedTotalPages);

  const paginatedFarmaciaMeds = useMemo(() => {
    if (farmaciaMedShowAll) return filteredFarmaciaMeds;
    const start = (farmaciaMedPageSafe - 1) * farmaciaMedPageSizeNum;
    return filteredFarmaciaMeds.slice(start, start + farmaciaMedPageSizeNum);
  }, [filteredFarmaciaMeds, farmaciaMedPageSafe, farmaciaMedPageSizeNum, farmaciaMedShowAll]);

  const farmaciaMedPageRangeLabel = useMemo(() => {
    if (!filteredFarmaciaMeds.length) {
      return farmaciaMedSearchTerm ? 'Sin resultados' : '0 medicamentos';
    }
    if (farmaciaMedShowAll) {
      return `Todos · ${filteredFarmaciaMeds.length}`;
    }
    const start = (farmaciaMedPageSafe - 1) * farmaciaMedPageSizeNum + 1;
    const end = Math.min(farmaciaMedPageSafe * farmaciaMedPageSizeNum, filteredFarmaciaMeds.length);
    return `${start}–${end} de ${filteredFarmaciaMeds.length}`;
  }, [
    filteredFarmaciaMeds.length,
    farmaciaMedPageSafe,
    farmaciaMedPageSizeNum,
    farmaciaMedSearchTerm,
    farmaciaMedShowAll
  ]);

  const farmaciaMedCatalogMeta = useMemo(() => {
    const total = profileFarmaciaMedicamentos.length;
    const chronicCount = profileFarmaciaMedicamentos.filter((m) => m.isCronico).length;
    if (!total) return { label: '', chronicCount: 0 };
    if (farmaciaMedSearchTerm) {
      return {
        label: `${filteredFarmaciaMeds.length} en búsqueda · ${total} en total`,
        chronicCount
      };
    }
    return {
      label: `${total} medicamentos · ordenados por compras`,
      chronicCount
    };
  }, [
    profileFarmaciaMedicamentos,
    farmaciaMedSearchTerm,
    filteredFarmaciaMeds.length
  ]);

  function changeFarmaciaMedPage(nextPage) {
    const page = Math.max(1, Math.min(farmaciaMedTotalPages, nextPage));
    setFarmaciaMedPage(page);
    setFarmaciaMedPageInput(String(page));
  }

  function goToFarmaciaMedPageFromInput() {
    const parsed = Number.parseInt(String(farmaciaMedPageInput).trim(), 10);
    if (!Number.isFinite(parsed)) {
      setFarmaciaMedPageInput(String(farmaciaMedPageSafe));
      return;
    }
    changeFarmaciaMedPage(parsed);
  }

  useEffect(() => {
    setFarmaciaMedPage(1);
    setFarmaciaMedPageInput('1');
  }, [farmaciaMedSearch]);

  useEffect(() => {
    setFarmaciaMedPage(1);
    setFarmaciaMedPageInput('1');
  }, [farmaciaMedPageSize]);

  useEffect(() => {
    if (farmaciaMedPage > farmaciaMedTotalPages) {
      setFarmaciaMedPage(farmaciaMedTotalPages);
      setFarmaciaMedPageInput(String(farmaciaMedTotalPages));
    }
  }, [farmaciaMedPage, farmaciaMedTotalPages]);

  useEffect(() => {
    setProfileOpenSection(PROFILE_ACCORDION_SECTION.meds);
  }, [selectedId]);

  const openProfileAccordionSection = (section) => (event) => {
    event.preventDefault();
    setProfileOpenSection(section);
  };

  useEffect(() => {
    setListPage(1);
    setListPageInput('1');
  }, [chatSearch, chatFilter, assigneeFilterId]);

  useEffect(() => {
    if (!assigneeFilterButton && chatFilter === 'assignee') {
      setChatFilter('pending');
      setAssigneeFilterId(null);
    }
  }, [assigneeFilterButton, chatFilter]);

  function selectAssigneeFilter(agentId) {
    const id = Number(agentId);
    if (!Number.isFinite(id) || id <= 0) return;
    const entry = assigneesById.get(id);
    setAssigneeFilterId(id);
    setFocusAssigneeId(id);
    setChatFilter('assignee');
    if (entry) {
      setAssigneeFilterCache(entry);
      writeAssigneeFilterCache(entry);
    }
  }

  function clearAssigneeFilter() {
    if (conversationScopeRestricted && conversationScopeAgentId) {
      setAssigneeFilterId(null);
      setFocusAssigneeId(conversationScopeAgentId);
      setChatFilter('pending');
      return;
    }
    setAssigneeFilterId(null);
    setChatFilter('pending');
  }

  useEffect(() => {
    if (listPage > listTotalPages) {
      setListPage(listTotalPages);
      setListPageInput(String(listTotalPages));
    }
  }, [listPage, listTotalPages]);

  useEffect(() => {
    if (!selectedId) {
      lastListPageSyncForSelectionRef.current = null;
      return;
    }
    if (lastListPageSyncForSelectionRef.current === selectedId) return;
    lastListPageSyncForSelectionRef.current = selectedId;

    const index = filteredConversations.findIndex((conv) => conv.id === selectedId);
    if (index < 0) return;
    const pageForSelection = Math.floor(index / CONVERSATIONS_LIST_PAGE_SIZE) + 1;
    setListPage(pageForSelection);
    setListPageInput(String(pageForSelection));
  }, [selectedId, filteredConversations]);

  function changeListPage(nextPage) {
    const page = Math.max(1, Math.min(listTotalPages, nextPage));
    setListPage(page);
    setListPageInput(String(page));
  }

  function goToListPageFromInput() {
    const parsed = Number.parseInt(String(listPageInput).trim(), 10);
    if (!Number.isFinite(parsed)) {
      setListPageInput(String(listPageSafe));
      return;
    }
    changeListPage(parsed);
  }

  const forwardTargetConversations = useMemo(() => {
    const term = forwardSearch.trim().toLowerCase();
    return conversations
      .filter((conv) => conv.id !== selectedId)
      .filter((conv) => {
        if (!term) return true;
        const name = getConversationDisplayName(conv).toLowerCase();
        const last = String(conv.last_message?.content || '').toLowerCase();
        return name.includes(term) || last.includes(term);
      })
      .sort((a, b) => Number(b.updated_at || 0) - Number(a.updated_at || 0));
  }, [conversations, forwardSearch, selectedId, conversationProfilesById, conversationDisplayNamesById, clientProfile]);

  const canInteractSelectedChat =
    canReplyMessages && !isSelectedSpectatorMode && !isSelectedFinishedFlow;

  // Ventana de 24 h de WhatsApp: se cuenta desde el último mensaje ENTRANTE
  // (del cliente). Si pasó, sólo se puede escribir con plantillas (HSM).
  const lastInboundAt = useMemo(() => {
    let latest = 0;
    (messages || []).forEach((msg) => {
      if (msg && isIncomingMessage(msg)) {
        const ts = Number(msg.created_at) || 0;
        if (ts > latest) latest = ts;
      }
    });
    return latest; // epoch en segundos (Chatwoot)
  }, [messages]);

  const isWhatsappConversation = useMemo(() => {
    const channel = String(selectedConversation?.channel || '').toLowerCase();
    if (!channel) return true; // el CRM es WhatsApp-first; ante la duda, aplica
    return channel.includes('whatsapp');
  }, [selectedConversation]);

  const whatsappWindowExpired = useMemo(() => {
    if (!selectedId || !isWhatsappConversation) return false;
    if (!lastInboundAt) return false; // sin entrantes cargados aún: no bloqueamos
    const nowSec = nowTick / 1000;
    return nowSec - lastInboundAt > 24 * 60 * 60;
  }, [selectedId, isWhatsappConversation, lastInboundAt, nowTick]);

  const whatsappWindowExpiresAt = useMemo(
    () => (lastInboundAt ? new Date((lastInboundAt + 24 * 60 * 60) * 1000) : null),
    [lastInboundAt]
  );

  const showExpiredTemplateNotice =
    Boolean(selectedConversation) &&
    canReplyMessages &&
    whatsappWindowExpired;

  const canSendMessage =
    canInteractSelectedChat &&
    !whatsappWindowExpired &&
    Boolean(draft.trim() || composerMedia) &&
    Boolean(selectedId) &&
    !sending;
  const acceptedMediaTypes = 'image/*,audio/*,application/pdf,text/vcard,text/x-vcard,.vcf';

  // Refresca el reloj cada minuto para que la ventana de 24 h se actualice sola.
  useEffect(() => {
    const timer = setInterval(() => setNowTick(Date.now()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const closeTemplateModal = useCallback(() => {
    setTemplateModalOpen(false);
    setSelectedTemplate(null);
    setTemplateParamValues([]);
    setTemplatesError('');
  }, []);

  const openTemplatePicker = useCallback(async () => {
    if (!selectedId) return;
    setTemplateModalOpen(true);
    setSelectedTemplate(null);
    setTemplateParamValues([]);
    setTemplatesError('');
    setTemplatesLoading(true);
    try {
      const data = await fetchConversationTemplates(selectedId);
      setTemplates(Array.isArray(data?.templates) ? data.templates : []);
    } catch (err) {
      setTemplatesError(err?.message || 'No se pudieron cargar las plantillas');
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, [selectedId]);

  const handlePickTemplate = useCallback((tpl) => {
    setSelectedTemplate(tpl);
    const count = Number(tpl?.params_count) || 0;
    setTemplateParamValues(Array.from({ length: count }, () => ''));
  }, []);

  const fillTemplateBody = useCallback((body, values) => {
    return String(body || '').replace(/\{\{\s*(\d+)\s*\}\}/g, (match, num) => {
      const idx = Number(num) - 1;
      const val = values?.[idx];
      return val != null && String(val).trim() ? String(val) : match;
    });
  }, []);

  const handleSendTemplate = useCallback(async () => {
    if (!selectedId || !selectedTemplate || sendingTemplate) return;
    const count = Number(selectedTemplate.params_count) || 0;
    if (count > 0 && templateParamValues.some((v) => !String(v || '').trim())) {
      setTemplatesError('Completá todos los campos de la plantilla.');
      return;
    }
    const processedParams = {};
    for (let i = 0; i < count; i += 1) {
      processedParams[String(i + 1)] = String(templateParamValues[i] || '');
    }
    const content = fillTemplateBody(selectedTemplate.body, templateParamValues);
    setSendingTemplate(true);
    setTemplatesError('');
    try {
      await sendConversationTemplate(selectedId, {
        name: selectedTemplate.name,
        language: selectedTemplate.language,
        category: selectedTemplate.category || undefined,
        namespace: selectedTemplate.namespace || undefined,
        processed_params: processedParams,
        content
      });
      closeTemplateModal();
      await loadMessages(selectedId, { silent: true });
    } catch (err) {
      setTemplatesError(err?.message || 'No se pudo enviar la plantilla');
    } finally {
      setSendingTemplate(false);
    }
  }, [
    selectedId,
    selectedTemplate,
    sendingTemplate,
    templateParamValues,
    fillTemplateBody,
    closeTemplateModal
  ]);

  const messageItems = useMemo(() => {
    const items = [];
    let lastDayKey = '';
    messages.forEach((msg) => {
      const dayKey = getDayKey(msg.created_at);
      if (dayKey !== lastDayKey) {
        items.push({
          type: 'divider',
          key: `divider-${dayKey}`,
          label: formatDayLabel(msg.created_at)
        });
        lastDayKey = dayKey;
      }
      items.push({
        type: 'message',
        key: `msg-${msg.id}`,
        message: msg
      });
    });
    return items;
  }, [messages]);

  const markConversationAsRead = useCallback((conversationId) => {
    const id = Number(conversationId);
    if (!Number.isFinite(id) || id <= 0) return;

    userAcknowledgedReadRef.current.add(id);

    setConversations((prev) => {
      const target = prev.find((c) => c.id === id);
      if (!target || !(Number(target.unread_count) > 0)) return prev;

      void fetch(`${API_BASE_URL}/api/chatwoot/conversations/${id}/read`, {
        method: 'POST',
        credentials: 'include'
      }).catch(() => {
        // No bloqueamos UX si falla el marcado remoto.
      });

      const next = prev.map((conv) => (conv.id === id ? { ...conv, unread_count: 0 } : conv));
      conversationsListRef.current = conversationsListRef.current.map((conv) =>
        conv.id === id ? { ...conv, unread_count: 0 } : conv
      );
      return next;
    });
  }, []);

  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
      if (levelTimerRef.current) {
        clearInterval(levelTimerRef.current);
        levelTimerRef.current = null;
      }
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch {
          // ignore
        }
        audioContextRef.current = null;
        analyserRef.current = null;
      }
      if (prevSpectrumRef.current) prevSpectrumRef.current = null;
      setRecordingSpectrum(new Array(SPECTRUM_BARS).fill(0));
      if (visualizerAnimRef.current) {
        cancelAnimationFrame(visualizerAnimRef.current);
        visualizerAnimRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!shouldSnapToBottomRef.current || !selectedId) return;
    if (loadingMessages) return;
    if (!messages.length) return;

    scheduleSnapChatToBottom();
    setShowScrollToBottom(false);

    const body = chatBodyRef.current;
    let resizeObserver = null;
    let stopObserverTimer = null;
    if (body && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        if (!allowLoadOlderRef.current) {
          scrollChatBodyToBottom();
        }
      });
      resizeObserver.observe(body);
      stopObserverTimer = window.setTimeout(() => {
        resizeObserver?.disconnect();
        resizeObserver = null;
      }, 2000);
    }

    shouldSnapToBottomRef.current = false;

    return () => {
      if (stopObserverTimer) window.clearTimeout(stopObserverTimer);
      resizeObserver?.disconnect();
    };
  }, [selectedId, loadingMessages, messages.length]);

  useEffect(() => {
    const body = chatBodyRef.current;
    if (!body) return undefined;

    const handleScroll = () => {
      const distanceToBottom = body.scrollHeight - body.scrollTop - body.clientHeight;
      setShowScrollToBottom(distanceToBottom > 140);
      if (
        allowLoadOlderRef.current &&
        body.scrollTop < CHAT_LOAD_OLDER_THRESHOLD_PX &&
        hasOlderMessagesRef.current &&
        !loadingOlderRef.current &&
        !loadingMessages
      ) {
        void loadOlderMessages();
      }
    };

    handleScroll();
    body.addEventListener('scroll', handleScroll);
    return () => body.removeEventListener('scroll', handleScroll);
  }, [selectedId, messages.length, loadingMessages]);

  useEffect(() => {
    if (!conversations.length) return undefined;

    let optimisticUnreadById = null;
    let shouldRefreshList = false;

    conversations.forEach((conv) => {
      const snap = {
        unread: Number(conv.unread_count || 0),
        lastMessageId: conv.last_message?.id ?? null
      };
      const prev = conversationUnreadSnapshotRef.current.get(conv.id);

      if (prev) {
        const hasNewMessage =
          snap.lastMessageId != null && snap.lastMessageId !== prev.lastMessageId;
        const unreadIncreased = snap.unread > prev.unread;

        if (conv.id !== selectedId && (hasNewMessage || unreadIncreased)) {
          shouldRefreshList = true;
          if (hasNewMessage && snap.unread <= prev.unread) {
            optimisticUnreadById ??= new Map();
            optimisticUnreadById.set(conv.id, Math.max(1, snap.unread));
          }
        }
      }

      const snapshotUnread = optimisticUnreadById?.has(conv.id)
        ? optimisticUnreadById.get(conv.id)
        : snap.unread;
      conversationUnreadSnapshotRef.current.set(conv.id, {
        unread: snapshotUnread,
        lastMessageId: snap.lastMessageId
      });
    });

    if (optimisticUnreadById?.size) {
      setConversations((prev) =>
        prev.map((conv) =>
          optimisticUnreadById.has(conv.id)
            ? { ...conv, unread_count: optimisticUnreadById.get(conv.id) }
            : conv
        )
      );
    }

    if (shouldRefreshList) {
      if (unreadRefreshDebounceRef.current) {
        clearTimeout(unreadRefreshDebounceRef.current);
      }
      unreadRefreshDebounceRef.current = setTimeout(() => {
        unreadRefreshDebounceRef.current = null;
        loadConversations({ silent: true });
      }, 350);
    }

    return () => {
      if (unreadRefreshDebounceRef.current) {
        clearTimeout(unreadRefreshDebounceRef.current);
        unreadRefreshDebounceRef.current = null;
      }
    };
  }, [conversations, selectedId]);

  useEffect(() => {
    if (!actionToasts.length) return undefined;
    const timers = actionToasts.map((toast) =>
      setTimeout(() => {
        setActionToasts((prev) => prev.filter((t) => t.id !== toast.id));
      }, TOAST_TTL_MS)
    );
    return () => timers.forEach((id) => clearTimeout(id));
  }, [actionToasts]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => {
      const compact = window.innerWidth <= 640;
      setIsCompactMobile(compact);
      if (!compact) setMobilePane('chat');
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!isCompactMobile) {
      setMobilePane('chat');
      return;
    }
    if (selectedId) setMobilePane('chat');
  }, [isCompactMobile, selectedId]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const baseTitle = 'Conversaciones';
    const unread = Number(unreadConversationsCount) || 0;
    document.title = unread > 0 ? `(${unread}) ${baseTitle}` : baseTitle;
    return () => {
      document.title = 'Grupo Bienestar';
    };
  }, [unreadConversationsCount]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onKeyDown = (event) => {
      const tag = String(event.target?.tagName || '').toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea' || tag === 'select' || event.target?.isContentEditable;
      if (event.key === '/' && !isTyping) {
        event.preventDefault();
        listSearchRef.current?.focus();
      }
      if (event.key === 'Escape') {
        setShowAgentPopover(false);
        setShowTycPopover(false);
        setShowEmojiPicker(false);
        setActiveMessageMenuId(null);
        setMobileProfileOpen(false);
        if (selectedId && !isTyping) {
          event.preventDefault();
          clearSelectedConversation();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) setMobileProfileOpen(false);
    setShowTycPopover(false);
  }, [selectedId]);

  useEffect(() => {
    if (!isCompactMobile) setMobileProfileOpen(false);
  }, [isCompactMobile]);

  useEffect(() => {
    if (!showAgentPopover) {
      setAgentPopoverStyle(null);
      return undefined;
    }

    function updateAgentPopoverPosition() {
      const anchor = agentPopoverRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const estimatedHeight = agentPopoverPanelRef.current?.offsetHeight || 200;
      setAgentPopoverStyle(
        getFloatingPanelFixedStyle(rect, {
          width: 250,
          estimatedHeight,
          gap: 8,
          alignRight: false,
          zIndex: 12050
        })
      );
    }

    updateAgentPopoverPosition();
    const raf = requestAnimationFrame(updateAgentPopoverPosition);
    window.addEventListener('resize', updateAgentPopoverPosition);
    window.addEventListener('scroll', updateAgentPopoverPosition, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updateAgentPopoverPosition);
      window.removeEventListener('scroll', updateAgentPopoverPosition, true);
    };
  }, [showAgentPopover, selectedHandlingMode?.kind]);

  useEffect(() => {
    if (!showAgentPopover) return undefined;

    const handleClickOutside = (event) => {
      const target = event.target;
      if (agentPopoverRef.current?.contains(target)) return;
      if (agentPopoverPanelRef.current?.contains(target)) return;
      setShowAgentPopover(false);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setShowAgentPopover(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showAgentPopover]);

  useEffect(() => {
    if (!showTycPopover) {
      setTycPopoverStyle(null);
      return undefined;
    }

    function updateTycPopoverPosition() {
      const anchor = tycPopoverRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const estimatedHeight = tycPopoverPanelRef.current?.offsetHeight || 96;
      setTycPopoverStyle(
        getFloatingPanelFixedStyle(rect, {
          width: 280,
          estimatedHeight,
          gap: 8,
          alignRight: false,
          zIndex: 12050
        })
      );
    }

    updateTycPopoverPosition();
    const raf = requestAnimationFrame(updateTycPopoverPosition);
    window.addEventListener('resize', updateTycPopoverPosition);
    window.addEventListener('scroll', updateTycPopoverPosition, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updateTycPopoverPosition);
      window.removeEventListener('scroll', updateTycPopoverPosition, true);
    };
  }, [showTycPopover, tycAcceptance, loadingTycAcceptance]);

  useEffect(() => {
    if (!showTycPopover) return undefined;

    const handleClickOutside = (event) => {
      const target = event.target;
      if (tycPopoverRef.current?.contains(target)) return;
      if (tycPopoverPanelRef.current?.contains(target)) return;
      setShowTycPopover(false);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') setShowTycPopover(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showTycPopover]);

  useEffect(() => {
    if (!activeMessageMenuId) return undefined;

    const handleClickOutside = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('.crm-cw-msg-actions-wrap')) {
        setActiveMessageMenuId(null);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setActiveMessageMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [activeMessageMenuId]);

  useEffect(() => {
    if (!clientProfile || !selectedId) return;
    setConversationProfilesById((prev) => {
      if (prev[selectedId] === clientProfile) return prev;
      return { ...prev, [selectedId]: clientProfile };
    });
    const displayName = getClientDisplayNameFromProfile(clientProfile);
    if (displayName) {
      setConversationDisplayNamesById((prev) => ({ ...prev, [selectedId]: displayName }));
      const phone = selectedConversation?.sender?.phone_number;
      if (phone) persistContactDisplayName(phone, displayName);
    }
  }, [clientProfile, selectedId, selectedConversation?.sender?.phone_number]);

  useEffect(() => {
    async function setResolvedProfile(profile, requestId, conversationId) {
      if (clientProfileRequestIdRef.current !== requestId) return;
      setClientProfile(profile || null);
      setClientProfileConversationId(profile && conversationId ? conversationId : null);
      if (!profile) {
        setPlexPurchaseCount(null);
        setProfileFarmaciaMedicamentos([]);
        setFarmaciaMedSearch('');
        setFarmaciaMedPage(1);
        setFarmaciaMedPageInput('1');
        setProfileUniqueMedicamentos(0);
        setLoadingClientProfile(false);
        return;
      }

      try {
        const insights = await fetchClientFarmaciaInsights(profile);
        if (clientProfileRequestIdRef.current !== requestId) return;
        setPlexPurchaseCount(insights.purchaseCount);
        setProfileFarmaciaMedicamentos(insights.medicamentos);
        setProfileUniqueMedicamentos(insights.uniqueCount);
        setFarmaciaMedPage(1);
        setFarmaciaMedPageInput('1');
      } catch {
        if (clientProfileRequestIdRef.current !== requestId) return;
        setPlexPurchaseCount(null);
        setProfileFarmaciaMedicamentos([]);
        setFarmaciaMedSearch('');
        setFarmaciaMedPage(1);
        setFarmaciaMedPageInput('1');
        setProfileUniqueMedicamentos(0);
      } finally {
        if (clientProfileRequestIdRef.current !== requestId) return;
        setLoadingClientProfile(false);
      }
    }

    async function loadClientProfile() {
      const conversationId = selectedConversation?.id;
      const requestId = Date.now() + Math.random();
      clientProfileRequestIdRef.current = requestId;

      if (!conversationId || !selectedConversation?.sender?.phone_number) {
        setLoadingClientProfile(false);
        setClientProfile(null);
        setClientProfileConversationId(null);
        setPlexPurchaseCount(null);
        setProfileFarmaciaMedicamentos([]);
        setFarmaciaMedSearch('');
        setFarmaciaMedPage(1);
        setFarmaciaMedPageInput('1');
        setProfileUniqueMedicamentos(0);
        return;
      }

      setClientProfile(null);
      setClientProfileConversationId(null);
      setPlexPurchaseCount(null);
      setProfileFarmaciaMedicamentos([]);
      setFarmaciaMedSearch('');
      setFarmaciaMedPage(1);
      setFarmaciaMedPageInput('1');
      setProfileUniqueMedicamentos(0);
      setLoadingClientProfile(true);

      try {
        const profile = await resolveClientProfileForConversation(selectedConversation);
        await setResolvedProfile(profile, requestId, conversationId);
      } catch {
        if (clientProfileRequestIdRef.current !== requestId) return;
        setClientProfile(null);
        setClientProfileConversationId(null);
        setPlexPurchaseCount(null);
        setProfileFarmaciaMedicamentos([]);
        setFarmaciaMedSearch('');
        setFarmaciaMedPage(1);
        setFarmaciaMedPageInput('1');
        setProfileUniqueMedicamentos(0);
        setLoadingClientProfile(false);
      }
    }

    loadClientProfile();
  }, [
    selectedConversation?.id,
    selectedConversation?.sender?.phone_number,
    selectedConversation?.sender?.name,
    selectedConversation?.meta?.sender?.name
  ]);

  useEffect(() => {
    const conversationId = selectedConversation?.id;
    const dni = normalizeDniForApi(clientProfile?.dni_normalizado);
    const requestId = Date.now() + Math.random();
    tycRequestIdRef.current = requestId;

    if (!conversationId || !dni) {
      setTycAcceptance(null);
      setTycAcceptanceConversationId(null);
      setLoadingTycAcceptance(false);
      return;
    }

    setLoadingTycAcceptance(true);
    setTycAcceptance(null);
    setTycAcceptanceConversationId(null);

    fetchTycAcceptanceByDni(dni)
      .then((data) => {
        if (tycRequestIdRef.current !== requestId) return;
        setTycAcceptance(data);
        setTycAcceptanceConversationId(conversationId);
      })
      .catch(() => {
        if (tycRequestIdRef.current !== requestId) return;
        setTycAcceptance(null);
        setTycAcceptanceConversationId(null);
      })
      .finally(() => {
        if (tycRequestIdRef.current !== requestId) return;
        setLoadingTycAcceptance(false);
      });
  }, [selectedConversation?.id, clientProfile?.dni_normalizado]);

  const selectedTycLines = useMemo(() => {
    if (!selectedId || tycAcceptanceConversationId !== selectedId) return null;
    return buildTycAcceptanceLines(tycAcceptance);
  }, [selectedId, tycAcceptance, tycAcceptanceConversationId]);

  async function handleSendMessage() {
    const content = draft.trim();
    if ((!content && !composerMedia) || !selectedId || sending) return;
    const quotedContent = replyToMessage?.content
      ? content
        ? `> [${replyToMessage.author}] ${String(replyToMessage.content).replace(/\n/g, '\n> ')}\n\n${content}`
        : ''
      : content;

    try {
      setSending(true);
      const requestBody = composerMedia
        ? (() => {
            const formData = new FormData();
            if (quotedContent) formData.append('content', quotedContent);
            formData.append('attachment', composerMedia.file);
            return formData;
          })()
        : JSON.stringify({ content: quotedContent });
      const res = await fetch(`${API_BASE_URL}/api/chatwoot/conversations/${selectedId}/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: composerMedia
          ? undefined
          : {
              'Content-Type': 'application/json'
            },
        body: requestBody
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setDraft('');
      setComposerMedia(null);
      setReplyToMessage(null);
      shouldSnapToBottomRef.current = true;
      await loadMessages(selectedId, { silent: true });
    } catch {
      setError('No se pudo enviar el mensaje.');
    } finally {
      setSending(false);
    }
  }

  async function handleStartCall() {
    if (!selectedId || startingCall) return;
    try {
      setStartingCall(true);
      setError('');
      const res = await fetch(`${API_BASE_URL}/api/chatwoot/conversations/${selectedId}/call/start`, {
        method: 'POST',
        credentials: 'include'
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const json = await res.json().catch(() => ({}));
      const joinUrl = String(json?.data?.joinUrl || '').trim();
      if (!joinUrl) throw new Error('No se obtuvo link de llamada');
      const url = new URL(joinUrl, window.location.origin);
      // El agente entra sin token para conservar rol "agent".
      // El token queda reservado para el participante invitado.
      navigate(url.pathname);
      await loadMessages(selectedId, { silent: true });
      await loadConversations({ silent: true });
    } catch {
      setError('No se pudo iniciar la videollamada.');
    } finally {
      setStartingCall(false);
    }
  }

  function handlePickMedia() {
    if (!mediaInputRef.current) return;
    mediaInputRef.current.click();
  }

  async function startAudioRecording() {
    if (isRecordingAudio || sending) return;
    setRecordingError('');
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setRecordingError('Tu navegador no soporta grabacion de audio.');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferredTypes = ['audio/ogg;codecs=opus', 'audio/ogg', 'audio/mp4', 'audio/webm;codecs=opus', 'audio/webm'];
      const supportedType = preferredTypes.find((type) => {
        try {
          return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type);
        } catch {
          return false;
        }
      });
      const recorder = supportedType
        ? new MediaRecorder(stream, { mimeType: supportedType })
        : new MediaRecorder(stream);
      setRecordingMimeType(recorder.mimeType || supportedType || 'audio/ogg');
      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      recordingChunksRef.current = [];

      // Setup analyser to get real-time audio level for visual feedback
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
          const audioCtx = new AudioCtx();
          // resume in case the context is suspended by the browser
          if (audioCtx.state === 'suspended' && typeof audioCtx.resume === 'function') {
            try {
              audioCtx.resume().catch(() => {});
            } catch {
              // ignore
            }
          }
          const source = audioCtx.createMediaStreamSource(stream);
          const analyser = audioCtx.createAnalyser();
          // better resolution and responsiveness for a small visualizer
          analyser.fftSize = 2048;
          analyser.smoothingTimeConstant = 0.55;
          analyser.minDecibels = -100;
          analyser.maxDecibels = -5;
          source.connect(analyser);
          audioContextRef.current = audioCtx;
          analyserRef.current = analyser;
          const freqArray = new Uint8Array(analyser.frequencyBinCount);
          if (levelTimerRef.current) clearInterval(levelTimerRef.current);
          // Read frequency data regularly and compute a small band spectrum
          levelTimerRef.current = setInterval(() => {
            try {
              analyser.getByteFrequencyData(freqArray);
              const barsCount = SPECTRUM_BARS;
              const binsPerBar = Math.max(1, Math.floor(freqArray.length / barsCount));
              const next = new Array(barsCount).fill(0);
              for (let b = 0; b < barsCount; b += 1) {
                let sum = 0;
                const start = b * binsPerBar;
                for (let k = 0; k < binsPerBar; k += 1) {
                  const idx = start + k;
                  if (idx < freqArray.length) sum += freqArray[idx];
                }
                const avg = sum / binsPerBar / 255;
                const prev = (prevSpectrumRef.current && prevSpectrumRef.current[b]) || 0;
                // asymmetric smoothing: fast attack, slower decay
                if (avg > prev) {
                  next[b] = prev * 0.6 + avg * 0.4; // faster rise
                } else {
                  next[b] = prev * 0.92 + avg * 0.08; // slower fall
                }
              }
              prevSpectrumRef.current = next;
              setRecordingSpectrum(next);
              // debug output for developer console
              try {
                // use debug so it's easy to filter in console
                // eslint-disable-next-line no-console
                console.debug('mic:spectrum', next.map((v) => Math.round(v * 100)));
              } catch {}
              // expose a single level as the peak of the bands
              const peak = Math.max(...next);
              setRecordingLevel(Math.max(0, Math.min(1, peak)));
            } catch {
              // ignore analyser read errors
            }
          }, 80);
          // ensure visualizer animation runs (simulated if analyser is silent)
          startVisualizerLoop();
        }
      } catch {
        // ignore analyser setup failures
      }

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      recorder.start();
      recordingStartAtRef.current = Date.now();
      setRecordingElapsedMs(0);
      setIsRecordingAudio(true);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = setInterval(() => {
        setRecordingElapsedMs(Date.now() - recordingStartAtRef.current);
      }, 200);
    } catch {
      setRecordingError('No se pudo acceder al microfono. Revisa permisos del navegador.');
    }
  }

  function stopAudioRecording({ save }) {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;

    recorder.onstop = () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      setIsRecordingAudio(false);
      setRecordingElapsedMs(0);

      if (save) {
        const mimeType = recordingMimeType || recorder.mimeType || 'audio/ogg';
        const extension = getAudioExtensionFromMime(mimeType);
        const blob = new Blob(recordingChunksRef.current, { type: mimeType });
        if (blob.size > 0) {
          const file = new File([blob], `audio-${Date.now()}.${extension}`, { type: mimeType });
          setComposerMedia({
            file,
            kind: 'audio',
            name: file.name,
            sizeKb: Math.max(1, Math.round(file.size / 1024))
          });
        }
      }

      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
      mediaRecorderRef.current = null;
      recordingChunksRef.current = [];
      if (levelTimerRef.current) {
        clearInterval(levelTimerRef.current);
        levelTimerRef.current = null;
      }
      // stop visualizer animation
      if (visualizerAnimRef.current) {
        cancelAnimationFrame(visualizerAnimRef.current);
        visualizerAnimRef.current = null;
      }
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch {
          // ignore
        }
        audioContextRef.current = null;
        analyserRef.current = null;
      }
      // reset spectrum display
      prevSpectrumRef.current = null;
      setRecordingSpectrum(new Array(SPECTRUM_BARS).fill(0));
      setRecordingMimeType('');
    };

    recorder.stop();
  }

  function handleMicClick() {
    if (composerMedia || !selectedId || sending) return;
    if (isRecordingAudio) {
      stopAudioRecording({ save: true });
      return;
    }
    startAudioRecording();
  }

  function startVisualizerLoop() {
    if (visualizerAnimRef.current) return;
    const loop = () => {
      visualizerPhaseRef.current += 0.12;
      const sum = (recordingSpectrum || []).reduce((a, b) => a + b, 0);
      if (analyserRef.current && sum > 0.001) {
        // analyser is providing real data; let interval handle updates
      } else {
        // simulate a spectrum when analyser is silent or unavailable
        const bars = SPECTRUM_BARS;
        const base = Math.max(0.02, recordingLevel || 0.02);
        const next = new Array(bars).fill(0).map((_, i) => {
          const t = visualizerPhaseRef.current + i * 0.6;
          const s = (Math.sin(t) + 1) / 2;
          const noise = (Math.random() - 0.5) * 0.2;
          const val = Math.max(0, Math.min(1, base * 0.6 + s * base * 0.9 + noise * base));
          const prev = (prevSpectrumRef.current && prevSpectrumRef.current[i]) || 0;
          return prev * 0.85 + val * 0.15;
        });
        prevSpectrumRef.current = next;
        setRecordingSpectrum(next);
      }
      visualizerAnimRef.current = requestAnimationFrame(loop);
    };
    visualizerAnimRef.current = requestAnimationFrame(loop);
  }

  function handleMediaChange(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileType = String(file.type || '').toLowerCase();
    const lowerName = String(file.name || '').toLowerCase();
    const isImage = fileType.startsWith('image/');
    const isAudio = fileType.startsWith('audio/');
    const isPdf = fileType.includes('pdf') || lowerName.endsWith('.pdf');
    const isContact = fileType.includes('vcard') || lowerName.endsWith('.vcf');
    if (!isImage && !isAudio && !isPdf && !isContact) {
      setError('Solo puedes adjuntar imagen, audio, PDF o contacto (.vcf).');
      event.target.value = '';
      return;
    }

    setComposerMedia({
      file,
      kind: isImage ? 'image' : isAudio ? 'audio' : isPdf ? 'pdf' : 'contact',
      name: file.name,
      sizeKb: Math.max(1, Math.round(file.size / 1024))
    });
    event.target.value = '';
  }

  function handleSelectEmoji(emoji) {
    if (!inputRef.current) return;
    const textarea = inputRef.current;
    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const newDraft = draft.slice(0, start) + emoji + draft.slice(end);
    setDraft(newDraft);
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
      textarea.focus();
    }, 0);
  }

  function insertDraftQuickPhrase(phraseText) {
    const snippet = String(phraseText || '').trim();
    if (!snippet || !inputRef.current) return;
    const textarea = inputRef.current;
    setDraft(snippet);
    setTimeout(() => {
      textarea.selectionStart = snippet.length;
      textarea.selectionEnd = snippet.length;
      textarea.focus();
    }, 0);
  }

  function handleRemoveMedia() {
    setComposerMedia(null);
  }

  async function handleCopyMessage(messageId, content) {
    const text = String(content || '').trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessageId(messageId);
      setTimeout(() => {
        setCopiedMessageId((current) => (current === messageId ? null : current));
      }, 1200);
    } catch {
      // fallback silencioso
    }
  }

  function handleReplyMessage(msg) {
    if (!canInteractSelectedChat) {
      setActiveMessageMenuId(null);
      return;
    }
    setReplyToMessage({
      id: msg.id,
      author: messageAuthor(
        msg,
        selectedConversation,
        chatwootAccountName,
        selectedContactDisplayName,
        conversationScopeAgentName
      ),
      content: msg.content || ''
    });
    if (inputRef.current) {
      inputRef.current.focus();
    }
    setActiveMessageMenuId(null);
  }

  function handleOpenForward(msg) {
    setForwardMessage(msg);
    setForwardSearch('');
    setActiveMessageMenuId(null);
  }

  async function handleForwardToConversation(targetConversationId) {
    if (!forwardMessage?.content || !targetConversationId) return;
    try {
      setForwardingToConversationId(targetConversationId);
      const res = await fetch(`${API_BASE_URL}/api/chatwoot/conversations/${targetConversationId}/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: forwardMessage.content })
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setForwardMessage(null);
      setForwardSearch('');
      // refresco silencioso de bandeja
      loadConversations({ silent: true });
    } catch {
      setError('No se pudo reenviar el mensaje.');
    } finally {
      setForwardingToConversationId(null);
    }
  }

  function notifyConversationAction(message, variant = 'ok') {
    setActionToasts((prev) => [
      ...prev,
      {
        id: `cw-action-${Date.now()}`,
        title: message,
        body: variant === 'error' ? 'Revisá permisos en Chatwoot o intentá de nuevo.' : '',
        variant
      }
    ].slice(-4));
  }

  async function handleHumanTakeover() {
    if (!selectedId || !canHumanTakeoverSelected || humanTakeoverBusy) return;

    const patientName = sanitizeDisplayName(selectedConversation?.displayName, 'este paciente');
    const fromFinished = isSelectedFinishedFlow;
    const confirm = await fireConfirmDialog({
      title: fromFinished ? 'Retomar atención humana' : 'Tomar conversación',
      html: buildCrmSwalBody({
      lead: fromFinished
          ? `El trámite automático con <strong>${patientName}</strong> ya finalizó. Pasás a atención humana por chat.`
          : `Pausás a <strong>${selectedBotDisplayName}</strong> y tomás la charla con <strong>${patientName}</strong>.`,
        items: [
          'Fase Pulse: <strong>derivación humana</strong>',
          fromFinished
            ? 'Se conservan pedido y medicamento del flujo cerrado'
            : 'Aviso al paciente por el chat',
          'Respondés desde este panel'
        ]
      }),
      icon: fromFinished ? 'question' : 'warning',
      confirmButtonText: fromFinished ? 'Atender paciente' : 'Tomar conversación',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#7c3aed',
      cancelButtonColor: '#e2e8f0'
    });
    if (!confirm.isConfirmed) return;

    setHumanTakeoverBusy(true);
    try {
      let assigneeId = null;
      try {
        const metaJson = await fetchChatwootMeta();
        const agents = metaJson?.data?.agents || [];
        const email = String(user?.email || '').trim().toLowerCase();
        const username = String(user?.username || '').trim().toLowerCase();
        const matched = agents.find((agent) => {
          const agentEmail = String(agent?.email || '').trim().toLowerCase();
          const agentName = String(agent?.name || '').trim().toLowerCase();
          return (email && agentEmail === email) || (username && agentName.includes(username));
        });
        assigneeId = matched?.id ?? null;
      } catch {
        assigneeId = null;
      }

      const json = await requestHumanTakeover(selectedId, {
        assignee_id: assigneeId,
        notify_patient: true,
        patient_display_name: String(selectedContactDisplayName || '').trim()
      });
      const data = json?.data || {};
      const estadoKey = normalizeConversationEstadoKey(selectedId);
      setConversationEstadoById((prev) => ({
        ...prev,
        [estadoKey]: {
          ...(prev[estadoKey] || {}),
          fase: 'derivacion_humana',
          derivacion_humano: true
        }
      }));
      if (data.assignee) {
        patchConversationInList(selectedId, { assignee: data.assignee });
      }
      await loadMessages(selectedId, { silent: true });
      notifyConversationAction(
        fromFinished
          ? 'Conversación retomada en atención humana'
          : 'Conversación derivada a operador humano',
        'ok'
      );
    } catch (err) {
      notifyConversationAction(
        err?.message || 'No se pudo derivar la conversación a operador humano',
        'error'
      );
    } finally {
      setHumanTakeoverBusy(false);
    }
  }

  async function handleHumanReturnToBot() {
    if (
      !selectedId ||
      !isSelectedHumanMode ||
      humanReturnToBotBusy ||
      humanFinalizeBusy ||
      humanTakeoverBusy
    ) {
      return;
    }

    const patientName = sanitizeDisplayName(selectedConversation?.displayName, 'este paciente');
    const confirm = await fireConfirmDialog({
      title: 'Volver al bot',
      html: buildCrmSwalBody({
      lead: `Devolvés a <strong>${patientName}</strong> al asistente <strong>${selectedBotDisplayName}</strong>.`,
        items: [
          'Fase: <strong>inicio</strong>',
          'Aviso al paciente por el chat',
          'Vos quedás en modo observador'
        ]
      }),
      icon: 'question',
      confirmButtonText: 'Volver al bot',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0d9488',
      cancelButtonColor: '#e2e8f0'
    });
    if (!confirm.isConfirmed) return;

    setHumanReturnToBotBusy(true);
    try {
      const json = await requestHumanReturnToBot(selectedId, { notify_patient: true });
      const data = json?.data || {};
      const estadoKey = normalizeConversationEstadoKey(selectedId);
      setConversationEstadoById((prev) => ({
        ...prev,
        [estadoKey]: {
          ...(prev[estadoKey] || {}),
          fase: 'inicio',
          derivacion_humano: false
        }
      }));
      if (!data.assignee?.id) {
        patchConversationInList(selectedId, { assignee: null });
      } else if (data.assignee) {
        patchConversationInList(selectedId, { assignee: data.assignee });
      }
      dispatchConversationReturnToBot({
        conversationId: selectedId,
        fase: data.fase || 'inicio',
        event: data.event || 'resume_from_human'
      });
      await loadMessages(selectedId, { silent: true });
      notifyConversationAction('Conversación devuelta al asistente automático', 'ok');
    } catch (err) {
      notifyConversationAction(
        err?.message || 'No se pudo devolver la conversación al asistente automático',
        'error'
      );
    } finally {
      setHumanReturnToBotBusy(false);
    }
  }

  async function handleHumanFinalizeAttention() {
    if (
      !selectedId ||
      !isSelectedHumanMode ||
      humanFinalizeBusy ||
      humanTakeoverBusy ||
      humanReturnToBotBusy
    ) {
      return;
    }

    const patientName = sanitizeDisplayName(selectedConversation?.displayName, 'este paciente');
    const confirm = await fireConfirmDialog({
      title: 'Finalizar atención',
      html: buildCrmSwalBody({
        lead: `Cerrás la gestión con <strong>${patientName}</strong>.`,
        items: [
          'Fase: <strong>finalizada</strong>',
          'Chat <strong>resuelto</strong> en Chatwoot',
          `${selectedBotDisplayName} queda en pausa`
        ],
        footnote: 'No se crea pedido en Gestión Derivación.'
      }),
      icon: 'question',
      confirmButtonText: 'Finalizar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1c4f86',
      cancelButtonColor: '#e2e8f0'
    });
    if (!confirm.isConfirmed) return;

    setHumanFinalizeBusy(true);
    try {
      const json = await requestHumanFinalize(selectedId, { notify_patient: true });
      const data = json?.data || {};
      const estadoKey = normalizeConversationEstadoKey(selectedId);
      setConversationEstadoById((prev) => ({
        ...prev,
        [estadoKey]: {
          ...(prev[estadoKey] || {}),
          fase: 'finalizada',
          derivacion_humano: false
        }
      }));
      if (data.status) {
        patchConversationInList(selectedId, { status: data.status });
      }
      await loadMessages(selectedId, { silent: true });
      notifyConversationAction('Atención humana finalizada', 'ok');
    } catch (err) {
      notifyConversationAction(err?.message || 'No se pudo finalizar la atención', 'error');
    } finally {
      setHumanFinalizeBusy(false);
    }
  }

  function patchConversationInList(conversationId, patch) {
    if (!conversationId || !patch) return;
    setConversations((prev) =>
      prev.map((conv) => (conv.id === conversationId ? { ...conv, ...patch } : conv))
    );
    conversationsListRef.current = conversationsListRef.current.map((conv) =>
      conv.id === conversationId ? { ...conv, ...patch } : conv
    );
  }

  function handleConversationTransferred(data) {
    const id = selectedId;
    if (!id) return;
    if (data?.assignee) {
      patchConversationInList(id, { assignee: data.assignee });
    }
    void refreshConversationsQuietly();
    const newAssigneeId = Number(data?.assignee?.id);
    const lostAccess =
      conversationScopeRestricted &&
      (!Number.isFinite(newAssigneeId) || newAssigneeId !== Number(conversationScopeAgentId));
    if (lostAccess) {
      setSelectedId(null);
      setMessages([]);
      if (isCompactMobile) setMobilePane('list');
    }
  }

  function refreshConversationsQuietly() {
    return loadConversations({ silent: true });
  }

  function handleConversationDeleted(conversationId) {
    setConversations((prev) => prev.filter((conv) => conv.id !== conversationId));
    conversationsListRef.current = conversationsListRef.current.filter(
      (conv) => conv.id !== conversationId
    );
    if (selectedId === conversationId) {
      setSelectedId(null);
      setMessages([]);
      if (isCompactMobile) setMobilePane('list');
    }
  }

  function clearSelectedConversation() {
    clientProfileRequestIdRef.current = Date.now();
    setSelectedId(null);
    setClientProfile(null);
    setClientProfileConversationId(null);
    setPlexPurchaseCount(null);
    setMessages([]);
    setHasOlderMessages(false);
    hasOlderMessagesRef.current = false;
    setLoadingMessages(false);
    setReplyToMessage(null);
    setActiveMessageMenuId(null);
    setForwardMessage(null);
    setShowAgentPopover(false);
    setMobileProfileOpen(false);
    if (isCompactMobile) setMobilePane('chat');
  }

  function openConversation(conversationId, { markRead = false } = {}) {
    const conv = conversations.find((c) => c.id === conversationId);
    const hasUnread = Number(conv?.unread_count) > 0;

    if (selectedId === conversationId) {
      shouldSnapToBottomRef.current = true;
      allowLoadOlderRef.current = false;
      if (isCompactMobile) setMobilePane('chat');
      if (!loadingMessages) scheduleSnapChatToBottom();
      if (markRead && hasUnread) markConversationAsRead(conversationId);
      return;
    }

    clientProfileRequestIdRef.current = Date.now();
    setSelectedId(conversationId);
    setClientProfile(null);
    setClientProfileConversationId(null);
    setPlexPurchaseCount(null);
    setMessages([]);
    setHasOlderMessages(false);
    hasOlderMessagesRef.current = false;
    setLoadingMessages(true);
    setReplyToMessage(null);
    setActiveMessageMenuId(null);
    setForwardMessage(null);
    shouldSnapToBottomRef.current = true;
    allowLoadOlderRef.current = false;
    if (isCompactMobile) setMobilePane('chat');
    setMobileProfileOpen(false);
    if (markRead && hasUnread) markConversationAsRead(conversationId);
  }

  function openConversationFromList(conversationId) {
    const id = Number(conversationId);
    const conv = conversations.find((c) => Number(c.id) === id);
    if (conv && Number(conv.unread_count) > 0) {
      markConversationAsRead(id);
    }
    openConversation(conversationId, { markRead: false });
  }

  const showProfileColumn = showProfilePanel && Boolean(selectedId) && !isCompactMobile;
  const showMobileProfileSheet = isCompactMobile && mobileProfileOpen && Boolean(selectedId);

  return (
    <section className="crm-cw-page">
      {/* <header className="crm-cw-header">
        <div className="crm-cw-title-wrap">
          <h1 className="crm-cw-title">Conversaciones</h1>
        </div>
      </header> */}

      {error ? (
        <div className="crm-cw-error-banner" role="alert">
          <span>{error}</span>
          <button type="button" className="crm-cw-error-banner__retry" onClick={() => loadConversations()}>
            Reintentar
          </button>
        </div>
      ) : null}

      {/* Trazabilidad: /trazabilidad-asignaciones (permiso ver_trazabilidad_asignaciones) */}

      <div className={`crm-cw-layout ${!showProfileColumn ? 'no-profile' : ''} ${isCompactMobile ? `mobile-pane-${mobilePane}` : ''}`}>
        {!isCompactMobile || mobilePane === 'list' ? (
        <aside className="crm-cw-list">
          <div className="crm-cw-list-toolbar">
            <div className="crm-cw-inbox-switch" role="radiogroup" aria-label="Canales de conversaciones">
              <div
                className={`crm-cw-inbox-switch__thumb${
                  inboxFilterId === 5 ? ' is-left' : inboxFilterId === 6 ? ' is-right' : ' is-neutral'
                }`}
                aria-hidden="true"
              />
              {CHATWOOT_INBOXES.map((item) => {
                const active = Number(inboxFilterId) === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`crm-cw-inbox-switch__btn${active ? ' is-active' : ''}`}
                    role="radio"
                    aria-checked={active ? 'true' : 'false'}
                    onClick={() => {
                      setInboxFilterId(item.id);
                      setChatFilter('pending');
                      setAssigneeFilterId(null);
                      setListPage(1);
                      setSelectedId(null);
                    }}
                    title={item.label}
                  >
                    <strong>{item.label}</strong>
                    <em>{inboxCounts[item.id] || 0}</em>
                  </button>
                );
              })}
            </div>
            <CwListSearch
              value={chatSearch}
              onChange={(e) => setChatSearch(e.target.value)}
              inputRef={listSearchRef}
            />
            <div className="crm-cw-list-filters" role="toolbar" aria-label="Filtros de conversaciones">
              <ListFilterChip
                icon={<CwIconAll />}
                count={allConversationsCount}
                active={chatFilter === 'all'}
                title="Todos los chats"
                onClick={() => {
                  setAssigneeFilterId(null);
                  setChatFilter('all');
                }}
              />
              <ListFilterChip
                icon={<CwIconPending />}
                count={noAtendidasConversationsCount}
                active={chatFilter === 'pending'}
                className="crm-cw-filter-btn--pending"
                title="No atendidos — en curso (sin finalizar)"
                onClick={() => {
                  setAssigneeFilterId(null);
                  setChatFilter('pending');
                }}
              />
              <ListFilterChip
                icon={<CwIconUnread />}
                count={unreadConversationsCount}
                active={chatFilter === 'unread'}
                title="No leídos — mensajes sin leer"
                onClick={() => {
                  setAssigneeFilterId(null);
                  setChatFilter('unread');
                  setSelectedId(null);
                  if (isCompactMobile) setMobilePane('list');
                }}
              />
              <ListFilterChip
                icon={<CwIconResolved />}
                count={resolvedConversationsCount}
                active={chatFilter === 'resolved'}
                className="crm-cw-filter-btn--resolved"
                title="Resueltos — finalizados o cerrados"
                onClick={() => {
                  setAssigneeFilterId(null);
                  setChatFilter('resolved');
                }}
              />
              {isChatwootAdmin ? (
                <AssigneeAgentsFilterMenu
                  agents={assigneeFilterOptions}
                  activeAgentId={assigneeFilterId}
                  filterActive={chatFilter === 'assignee'}
                  agentNameById={chatwootAgentNameById}
                  scopeRestricted={conversationScopeRestricted}
                  onSelect={selectAssigneeFilter}
                  onClear={clearAssigneeFilter}
                />
              ) : null}
            </div>
          </div>

          {loadingConversations && conversations.length === 0 ? (
            <ConversationListSkeleton rows={8} />
          ) : (
            <>
              {loadingConversations ? (
                <div className="crm-cw-list-refresh" role="status" aria-live="polite">
                  <span className="crm-cw-pane-spinner crm-cw-pane-spinner--sm" aria-hidden="true" />
                  <span>Actualizando lista</span>
                </div>
              ) : null}
              <div className="crm-cw-list-scroll">
                {paginatedConversations.length > 0 ? (
                  paginatedConversations.map((conv) => {
                    const handling = getConversationListLeadBadge(conv, conversationEstadoById);
                    const chatStatusBadge = resolveChatStatusBadge(conv);
                    const botActive = handling?.kind === 'bot';
                    const finishedFlow = handling?.kind === 'finished';
                    const showHandlingChip =
                      handling && ['bot', 'human', 'finished'].includes(handling.kind);
                    return (
                    <button
                      type="button"
                      key={conv.id}
                      className={`crm-cw-item ${selectedId === conv.id ? 'active' : ''} ${
                        conv.unread_count > 0 ? 'unread' : ''
                      } ${botActive ? 'crm-cw-item--bot-active' : ''}${
                        handling?.kind === 'human' ? ' crm-cw-item--human-handling' : ''
                      }${finishedFlow ? ' crm-cw-item--finished-handling' : ''}`}
                      title={
                        botActive
                          ? `${handling?.label || 'Bot'} está gestionando la receta — un operador puede tomar el chat cuando haga falta`
                          : finishedFlow
                            ? `${handling?.label || 'Bot'} finalizó correctamente`
                            : handling?.title || undefined
                      }
                      onClick={() => openConversationFromList(conv.id)}
                    >
                      <div className="crm-cw-item-inner">
                        <ConversationListAvatar
                          name={conv.displayName}
                          unreadCount={conv.unread_count}
                        />
                        <div className="crm-cw-item-body">
                      <div className="crm-cw-item-head">
                        <div className="crm-cw-item-title-block">
                          <div className="crm-cw-item-title-line">
                            {!showHandlingChip && handling ? (
                              <span
                                className={`crm-cw-status-icon crm-cw-status-icon--${handling.tone}`}
                                title={handling.title || handling.label}
                                aria-label={handling.label}
                              >
                                {handling.listIcon}
                              </span>
                            ) : null}
                            <strong
                              className="crm-cw-item-name"
                              title={getConversationListNameTitle(conv)}
                            >
                              {conv.displayName}
                            </strong>
                          </div>
                          {showHandlingChip ? (
                            <ConversationListHandlingBadge handling={handling} />
                          ) : null}
                        </div>
                        {(() => {
                          const pr = getConversationPriorityBadge(conv.priority);
                          if (!pr) return null;
                          return (
                            <div className="crm-cw-item-badges">
                              <span
                                className={`crm-cw-priority-badge crm-cw-priority-badge--list crm-cw-priority-badge--${pr.tone}`}
                                title={`Prioridad: ${pr.label}`}
                              >
                                {pr.shortLabel || pr.label}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="crm-cw-item-sub">
                        <div className="crm-cw-item-msg">
                          {getMessagePreview(conv.last_message?.content, conv.last_message) || 'Sin mensajes'}
                        </div>
                        <span className="crm-cw-item-time">
                          {chatStatusBadge &&
                          (chatStatusBadge.tone === 'warning' || chatStatusBadge.tone === 'pending') ? (
                            <CrmStatusBadgeFrom
                              badge={chatStatusBadge}
                              compact
                              className="crm-cw-item-time__status"
                            />
                          ) : null}
                          <span className="crm-cw-item-time__when">
                            {formatListTime(conv.updated_at)}
                          </span>
                        </span>
                      </div>
                        </div>
                      </div>
                    </button>
                    );
                  })
                ) : listEmptyState ? (
                  <div className="crm-cw-list-empty" role="status" aria-live="polite">
                    <span className="crm-cw-list-empty__icon" aria-hidden="true">
                      {listEmptyState.icon}
                    </span>
                    <p className="crm-cw-list-empty__title">{listEmptyState.title}</p>
                    <p className="crm-cw-list-empty__hint">{listEmptyState.hint}</p>
                    {listEmptyState.clearSearch || listEmptyState.showPending || listEmptyState.showAll ? (
                      <div className="crm-cw-list-empty__actions">
                        {listEmptyState.clearSearch ? (
                          <button
                            type="button"
                            className="crm-cw-list-empty__btn"
                            onClick={() => setChatSearch('')}
                          >
                            Limpiar búsqueda
                          </button>
                        ) : null}
                        {listEmptyState.showAll ? (
                          <button
                            type="button"
                            className={`crm-cw-list-empty__btn ${
                              listEmptyState.clearSearch || listEmptyState.showPending
                                ? 'crm-cw-list-empty__btn--ghost'
                                : ''
                            }`}
                            onClick={() => setChatFilter('all')}
                          >
                            Ver todos
                          </button>
                        ) : null}
                        {listEmptyState.showPending ? (
                          <button
                            type="button"
                            className={`crm-cw-list-empty__btn ${
                              listEmptyState.clearSearch || listEmptyState.showAll
                                ? 'crm-cw-list-empty__btn--ghost'
                                : ''
                            }`}
                            onClick={() => setChatFilter('pending')}
                          >
                            Ver no atendidos
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div
                className={`crm-cw-list-pagination ${filteredConversations.length === 0 ? 'is-idle' : ''}`}
                role="navigation"
                aria-label="Paginación de conversaciones"
              >
                <div className="crm-cw-list-pagination__meta">
                  <span className="crm-cw-list-pagination__range">{listPageRangeLabel}</span>
                  <span className="crm-cw-list-pagination__pages">
                    Página {listPageSafe} de {listTotalPages}
                  </span>
                </div>
                <div className="crm-cw-list-pagination__controls">
                  <button
                    type="button"
                    className="crm-cw-list-pagination__btn"
                    disabled={listPageSafe <= 1 || filteredConversations.length === 0}
                    onClick={() => changeListPage(1)}
                    title="Primera página"
                    aria-label="Primera página"
                  >
                    <CwIconChevronFirst />
                  </button>
                  <button
                    type="button"
                    className="crm-cw-list-pagination__btn"
                    disabled={listPageSafe <= 1 || filteredConversations.length === 0}
                    onClick={() => changeListPage(listPageSafe - 1)}
                    title="Página anterior"
                    aria-label="Página anterior"
                  >
                    <CwIconChevronPrev />
                  </button>
                  <div className="crm-cw-list-pagination__goto">
                    <input
                      type="text"
                      className="crm-cw-list-pagination__input"
                      value={listPageInput}
                      onChange={(e) => setListPageInput(e.target.value.replace(/\D/g, ''))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          goToListPageFromInput();
                        }
                      }}
                      disabled={filteredConversations.length === 0}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      aria-label="Ir a página"
                      placeholder={`1-${listTotalPages}`}
                    />
                    <button
                      type="button"
                      className="crm-cw-list-pagination__go"
                      onClick={goToListPageFromInput}
                      disabled={filteredConversations.length === 0}
                    >
                      Ir
                    </button>
                  </div>
                  <button
                    type="button"
                    className="crm-cw-list-pagination__btn"
                    disabled={listPageSafe >= listTotalPages || filteredConversations.length === 0}
                    onClick={() => changeListPage(listPageSafe + 1)}
                    title="Página siguiente"
                    aria-label="Página siguiente"
                  >
                    <CwIconChevronNext />
                  </button>
                  <button
                    type="button"
                    className="crm-cw-list-pagination__btn"
                    disabled={listPageSafe >= listTotalPages || filteredConversations.length === 0}
                    onClick={() => changeListPage(listTotalPages)}
                    title="Última página"
                    aria-label="Última página"
                  >
                    <CwIconChevronLast />
                  </button>
                </div>
              </div>
            </>
          )}
        </aside>
        ) : null}

        {!isCompactMobile || mobilePane === 'chat' ? (
        <section
          className={`crm-cw-chat ${!selectedConversation ? 'crm-cw-chat--empty' : ''} ${
            isSelectedSpectatorMode ? 'crm-cw-chat--spectator' : ''
          } ${isSelectedFinishedFlow ? 'crm-cw-chat--finished' : ''} ${
            isSelectedHumanMode ? 'crm-cw-chat--human' : ''
          }`}
        >
          {!selectedConversation ? (
            <div className="crm-cw-chat-placeholder" role="status" aria-live="polite">
              <div className="crm-cw-chat-placeholder__card">
                <div className="crm-cw-chat-placeholder__visual" aria-hidden="true">
                  <span className="crm-cw-chat-placeholder__bubble crm-cw-chat-placeholder__bubble--back">💬</span>
                  <span className="crm-cw-chat-placeholder__bubble crm-cw-chat-placeholder__bubble--front">✨</span>
                  <span className="crm-cw-chat-placeholder__orbit" />
                </div>
                <h2 className="crm-cw-chat-placeholder__title">Seleccioná una conversación</h2>
                <p className="crm-cw-chat-placeholder__text">
                  Elegí un chat para ver el historial completo, el estado del paciente y la ficha
                  desde Geclisa/Plex.
                </p>
                <div className="crm-cw-chat-placeholder__stats">
                  <span className="crm-cw-chat-placeholder__stat">
                    {noAtendidasConversationsCount} no atendidos
                  </span>
                  <span className="crm-cw-chat-placeholder__stat">
                    {unreadConversationsCount} sin leer
                  </span>
                  <span className="crm-cw-chat-placeholder__stat">
                    {allConversationsCount} en total
                  </span>
                </div>
                <div className="crm-cw-chat-placeholder__hint">
                  <span className="crm-cw-chat-placeholder__hint-icon" aria-hidden="true">
                    {isCompactMobile ? '👆' : '👈'}
                  </span>
                  <span>
                    {isCompactMobile
                      ? 'Elegí un chat del listado para empezar'
                      : 'Usá el listado de la izquierda para empezar'}
                  </span>
                </div>
                {isCompactMobile ? (
                  <button
                    type="button"
                    className="crm-cw-chat-placeholder__list-btn"
                    onClick={() => setMobilePane('list')}
                  >
                    Ver listado de chats
                  </button>
                ) : null}
                <div className="crm-cw-chat-placeholder__chips" aria-hidden="true">
                  <span>📋 Historial</span>
                  <span>👤 Ficha</span>
                  <span>💊 Recetas</span>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="crm-cw-chat-head">
                <div className="crm-cw-chat-head-nav">
                  <button
                    type="button"
                    className="crm-cw-chat-back"
                    onClick={clearSelectedConversation}
                    aria-label="Volver al listado de conversaciones"
                    title="Volver al listado"
                  >
                    <CwIconChevronPrev />
                    <span className="crm-cw-chat-back__label">Chats</span>
                  </button>
                  {normalizeDniForApi(clientProfile?.dni_normalizado) ? (
                    <button
                      type="button"
                      ref={tycPopoverRef}
                      className={`crm-cw-chat-tyc-btn${
                        showTycPopover ? ' crm-cw-chat-tyc-btn--active' : ''
                      }${
                        selectedTycLines && tycAcceptance?.aceptado !== false
                          ? ' crm-cw-chat-tyc-btn--ok'
                          : selectedTycLines && tycAcceptance?.aceptado === false
                            ? ' crm-cw-chat-tyc-btn--pending'
                            : ' crm-cw-chat-tyc-btn--empty'
                      }`}
                      onClick={() => setShowTycPopover((prev) => !prev)}
                      disabled={loadingTycAcceptance}
                      title="Ver términos y condiciones"
                      aria-expanded={showTycPopover}
                      aria-haspopup="dialog"
                    >
                      <span className="crm-cw-chat-tyc-btn__label">
                        {loadingTycAcceptance ? 'TyC…' : 'TyC'}
                      </span>
                      <span
                        className={`crm-cw-chat-tyc-btn__chev${showTycPopover ? ' is-open' : ''}`}
                        aria-hidden="true"
                      >
                        <svg viewBox="0 0 24 24" fill="none">
                          <path
                            d="M6 9l6 6 6-6"
                            stroke="currentColor"
                            strokeWidth="2.25"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </button>
                  ) : null}
                  {showTycPopover && tycPopoverStyle && typeof document !== 'undefined'
                    ? createPortal(
                        <div
                          ref={tycPopoverPanelRef}
                          className="crm-cw-tyc-popover crm-cw-tyc-popover--portal"
                          role="dialog"
                          aria-label="Términos y condiciones"
                          style={tycPopoverStyle}
                        >
                          {loadingTycAcceptance ? (
                            <p className="crm-cw-tyc-popover__loading" role="status">
                              Consultando TyC…
                            </p>
                          ) : selectedTycLines ? (
                            <div className="crm-cw-tyc-popover__body">
                              <div className="crm-cw-tyc-popover__head">
                                <span
                                  className={`crm-cw-tyc-popover__status crm-cw-tyc-popover__status--${selectedTycLines.status}`}
                                >
                                  {selectedTycLines.status === 'accepted' ? (
                                    <svg
                                      className="crm-cw-tyc-popover__status-icon"
                                      viewBox="0 0 20 20"
                                      fill="none"
                                      aria-hidden="true"
                                    >
                                      <path
                                        d="M6.5 10.2 8.8 12.5 13.5 7.8"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  ) : null}
                                  {selectedTycLines.headline}
                                </span>
                                <CrmStatusBadgeFrom badge={selectedTycLines.badge} />
                              </div>
                              {selectedTycLines.acceptedAt ||
                              selectedTycLines.vigenteHasta ||
                              selectedTycLines.origen ? (
                                <dl className="crm-cw-tyc-popover__details">
                                  {selectedTycLines.acceptedAt ? (
                                    <div className="crm-cw-tyc-popover__detail">
                                      <dt>Fecha</dt>
                                      <dd>{selectedTycLines.acceptedAt}</dd>
                                    </div>
                                  ) : null}
                                  {selectedTycLines.vigenteHasta ? (
                                    <div className="crm-cw-tyc-popover__detail">
                                      <dt>Vigencia</dt>
                                      <dd>Hasta {selectedTycLines.vigenteHasta}</dd>
                                    </div>
                                  ) : null}
                                  {selectedTycLines.origen ? (
                                    <div className="crm-cw-tyc-popover__detail">
                                      <dt>Canal</dt>
                                      <dd>{selectedTycLines.origen}</dd>
                                    </div>
                                  ) : null}
                                </dl>
                              ) : null}
                            </div>
                          ) : (
                            <p className="crm-cw-tyc-popover__empty">Sin TyC registrado para este DNI.</p>
                          )}
                        </div>,
                        document.body
                      )
                    : null}
                </div>
                <div className="crm-cw-chat-head-body">
                  <div className="crm-cw-chat-head-row">
                    {isCompactMobile ? (
                      <button
                        type="button"
                        className="crm-cw-mobile-profile-btn"
                        onClick={() => setMobileProfileOpen(true)}
                        title="Ver ficha del contacto"
                      >
                        <CwIconProfile />
                        Ficha
                      </button>
                    ) : null}
                    <div className="crm-cw-chat-head-start">
                    <strong className="crm-cw-chat-head-name">{selectedContactDisplayName}</strong>
                    <div className="crm-cw-agent-toolbar">
                      <div className="crm-cw-agent-wrap" ref={agentPopoverRef}>
                        <button
                          type="button"
                          className={`crm-cw-agent-chip ${showAgentPopover ? 'active' : ''} ${
                            selectedHandlingMode?.kind === 'bot' ? 'crm-cw-agent-chip--bot-active' : ''
                          }`}
                          title={
                            selectedHandlingMode?.kind === 'bot'
                              ? `${selectedBotDisplayName} está atendiendo este chat (agente virtual)`
                              : 'Ver información del agente virtual'
                          }
                          onClick={() => setShowAgentPopover((prev) => !prev)}
                        >
                          <img
                            src={VIRTUAL_AGENT_AVATAR}
                            alt={selectedBotDisplayName}
                            className="crm-cw-agent-chip-avatar"
                            loading="lazy"
                          />
                          <span>{selectedBotDisplayName} · Agente virtual</span>
                        </button>
                      </div>
                      {canTransferSelectedConversation ? (
                        <ChatwootTransferConversationButton
                          conversationId={selectedId}
                          conversationDisplayName={selectedContactDisplayName}
                          currentAssigneeId={selectedConversation?.assignee?.id}
                          agents={chatwootTransferAgents}
                          disabled={
                            humanFinalizeBusy ||
                            humanTakeoverBusy ||
                            humanReturnToBotBusy ||
                            conversationActionBusy
                          }
                          onTransferred={handleConversationTransferred}
                          onNotify={notifyConversationAction}
                        />
                      ) : null}
                    </div>
                    {showAgentPopover && agentPopoverStyle && typeof document !== 'undefined'
                      ? createPortal(
                          <div
                            ref={agentPopoverPanelRef}
                            className={`crm-cw-agent-popover crm-cw-agent-popover--portal${
                              selectedHandlingMode?.kind === 'bot' ? ' crm-cw-agent-popover--bot-active' : ''
                            }`}
                            role="dialog"
                            aria-label={`Información de ${selectedBotDisplayName}`}
                            style={agentPopoverStyle}
                          >
                            <div className="crm-cw-agent-popover-head">
                              <img
                                src={VIRTUAL_AGENT_AVATAR}
                                alt={selectedBotDisplayName}
                                className="crm-cw-agent-popover-avatar"
                                loading="lazy"
                              />
                              <div>
                                <strong>{selectedBotDisplayName}</strong>
                                <span>Agente virtual</span>
                              </div>
                            </div>
                            <div className="crm-cw-agent-popover-body">
                              {selectedHandlingMode?.kind === 'human' ? (
                                <>
                                  <div>
                                    <strong>Operador humano</strong>
                                    {selectedConversation?.assignee?.name
                                      ? ` · ${sanitizeDisplayName(selectedConversation.assignee.name, '')}`
                                      : ''}
                                  </div>
                                  <div>
                                    Fase: derivación humana · La IA quedó en pausa; continúa el equipo por
                                    Chatwoot.
                                  </div>
                                </>
                              ) : selectedHandlingMode?.kind === 'finished' ? (
                                <>
                                  <div>
                                    <strong>Flujo finalizado</strong>
                                  </div>
                                  <div>
                                    El circuito automático de recetas cerró esta conversación (fase{' '}
                                    {conversationEstadoById[normalizeConversationEstadoKey(selectedConversation?.id)]
                                      ?.fase || 'finalizado'}
                                    ).
                                  </div>
                                </>
                              ) : selectedHandlingMode?.kind === 'bot' ? (
                                <>
                                  <div>
                                    <strong>{selectedBotDisplayName} · Agente virtual (IA)</strong>
                                  </div>
                                  <div>Atiende mensajes salientes · Estado: En línea · Asistencia automática.</div>
                                </>
                              ) : selectedConversation?.assignee?.id ? (
                                <>
                                  <div>
                                    <strong>Asignado en Chatwoot</strong> ·{' '}
                                    {sanitizeDisplayName(selectedConversation.assignee?.name, 'Operador')}
                                  </div>
                                  <div>Revisá si el operador tomó el chat o sigue el bot.</div>
                                </>
                              ) : (
                                <>
                                  <div>Atiende mensajes salientes de la cuenta.</div>
                                  <div>Estado: En línea · Asistencia automática.</div>
                                </>
                              )}
                            </div>
                          </div>,
                          document.body
                        )
                      : null}
                    <ChatwootConversationMenu
                      conversation={selectedConversation}
                      accountId={chatwootAccountId}
                      allowAssignAgent={canAssignAgentEffective}
                      prefetchedAgents={chatwootAssignableAgents}
                      conversationDisplayName={selectedContactDisplayName}
                      busy={conversationActionBusy}
                      onBusyChange={setConversationActionBusy}
                      onPatch={(patch) => {
                        patchConversationInList(selectedConversation.id, patch);
                        void refreshConversationsQuietly();
                      }}
                      onDeleted={handleConversationDeleted}
                      onNotify={notifyConversationAction}
                      showProfilePanel={showProfilePanel}
                      showProfileToggle={!isCompactMobile}
                      onToggleProfilePanel={() => setShowProfilePanel((prev) => !prev)}
                    />
                    </div>
                    {(() => {
                      const pr = getConversationPriorityBadge(selectedConversation.priority);
                      const st = getConversationStatusBadge(selectedConversation.status);
                      const chatStatusBadge = resolveChatStatusBadge(selectedConversation);
                      if (!pr && !st && !chatStatusBadge) return null;
                      return (
                        <div className="crm-cw-chat-head-end">
                          <div className="crm-cw-head-meta">
                            {chatStatusBadge &&
                            (chatStatusBadge.tone === 'warning' || chatStatusBadge.tone === 'pending') ? (
                              <CrmStatusBadgeFrom badge={chatStatusBadge} compact />
                            ) : null}
                            {pr
                              ? renderChatHeadMetaChip({
                                  kind: 'priority',
                                  kindLabel: 'Prioridad',
                                  icon: pr.headIcon || '◆',
                                  value: pr.label,
                                  toneClass: `crm-cw-meta-chip--priority crm-cw-meta-chip--${pr.tone}`,
                                  title: `Prioridad de atención: ${pr.label}`
                                })
                              : null}
                            {st
                              ? renderChatHeadMetaChip({
                                  kind: 'status',
                                  kindLabel: 'Estado',
                                  icon: st.listIcon,
                                  value: st.label,
                                  toneClass: `crm-cw-meta-chip--status crm-cw-meta-chip--${st.tone}`,
                                  title: `Estado del chat: ${st.label}`
                                })
                              : null}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              <div className="crm-cw-chat-body" ref={chatBodyRef}>
                {loadingMessages ? (
                  <div className="crm-cw-pane-loading crm-cw-pane-loading--chat" role="status" aria-live="polite">
                    <span className="crm-cw-pane-spinner" aria-hidden="true" />
                    <span>Cargando mensajes</span>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="crm-cw-empty">Esta conversación no tiene mensajes todavía.</div>
                ) : (
                  <>
                    {(loadingOlderMessages || hasOlderMessages) && (
                      <div className="crm-cw-chat-history-load" role="status" aria-live="polite">
                        {loadingOlderMessages ? (
                          <>
                            <span className="crm-cw-pane-spinner crm-cw-pane-spinner--sm" aria-hidden="true" />
                            <span>Cargando mensajes anteriores…</span>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="crm-cw-chat-history-load__btn"
                            onClick={() => {
                              void loadOlderMessages();
                            }}
                          >
                            Ver mensajes anteriores
                          </button>
                        )}
                      </div>
                    )}
                    {messageItems.map((item) => {
                    if (item.type === 'divider') {
                      return (
                        <div key={item.key} className="crm-cw-day-divider">
                          <span>{item.label}</span>
                        </div>
                      );
                    }
                    const msg = item.message;
                    if (isActivityMessage(msg)) {
                      const eventText = getActivityEventText(msg);
                      return (
                        <div key={item.key} className="crm-cw-activity-event" role="status">
                          <span className="crm-cw-activity-badge">{eventText}</span>
                          {msg.created_at ? (
                            <time className="crm-cw-activity-time" dateTime={new Date(msg.created_at * 1000).toISOString()}>
                              {formatTime(msg.created_at)}
                            </time>
                          ) : null}
                        </div>
                      );
                    }
                    const parsed = parseQuotedReply(msg.content || '');
                    const isIncoming = isIncomingMessage(msg);
                    const isTycPlaceholder = isLikelyWhatsappTycMessage(msg, parsed);
                    const attachments = getMessageAttachments(msg);
                    const hasAudioAttachment = attachments.some(
                      (attachment) => inferAttachmentType(attachment) === 'audio'
                    );
                    return (
                      <div
                        key={item.key}
                        className={`crm-cw-msg ${isIncoming ? 'in' : 'out'}`}
                      >
                        {(() => {
                          const quotedLabel = parsed.quotedAuthor
                            ? `En respuesta a ${parsed.quotedAuthor}`
                            : isIncoming
                              ? 'Mensaje citado'
                              : 'Respuesta';
                          return parsed.quoted ? (
                            <div className="crm-cw-msg-quoted">
                              <span className="crm-cw-msg-quoted-label">{quotedLabel}</span>
                              <span className="crm-cw-msg-quoted-text">{renderMessageContent(parsed.quoted)}</span>
                            </div>
                          ) : null;
                        })()}
                        <div
                          className={`crm-cw-msg-author-row ${isIncoming ? 'in' : 'out'}`}
                        >
                          {!isIncoming ? (
                            <img
                              src={VIRTUAL_AGENT_AVATAR}
                              alt={selectedBotDisplayName}
                              className="crm-cw-msg-agent-avatar"
                              loading="lazy"
                            />
                          ) : null}
                          <div className="crm-cw-msg-author">
                            {messageAuthor(
                              msg,
                              selectedConversation,
                              chatwootAccountName,
                              selectedContactDisplayName,
                              conversationScopeAgentName
                            )}
                          </div>
                        </div>
                        {(() => {
                          if (!attachments.length) return null;
                          return (
                            <div
                              className={`crm-cw-msg-attachments ${
                                !isIncoming && hasAudioAttachment ? 'with-inline-meta' : ''
                              }`}
                            >
                              {attachments.map((attachment) => {
                                const attachmentType = inferAttachmentType(attachment);
                                const isImage = attachmentType === 'image';
                                const isAudio = attachmentType === 'audio';
                                const isPdf = attachmentType === 'pdf';
                                const isContact = attachmentType === 'contact';
                                if (isImage) {
                                  return (
                                    <a
                                      key={attachment.id}
                                      href={attachment.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="crm-cw-msg-attachment media"
                                      title="Abrir imagen"
                                    >
                                      <img src={attachment.url} alt={attachment.fileName} loading="lazy" />
                                    </a>
                                  );
                                }
                                if (isAudio) {
                                  return (
                                    <div key={attachment.id} className="crm-cw-msg-attachment media audio">
                                      <PrettyAudioPlayer
                                        src={attachment.url}
                                        label="Audio"
                                      />
                                    </div>
                                  );
                                }
                                if (isPdf) {
                                  return (
                                    <a
                                      key={attachment.id}
                                      href={attachment.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="crm-cw-msg-attachment file"
                                    >
                                      📄 {attachment.fileName}
                                    </a>
                                  );
                                }
                                if (isContact) {
                                  return (
                                    <a
                                      key={attachment.id}
                                      href={attachment.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="crm-cw-msg-attachment file"
                                    >
                                      👤 {attachment.fileName}
                                    </a>
                                  );
                                }
                                return (
                                  <a
                                    key={attachment.id}
                                    href={attachment.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="crm-cw-msg-attachment file"
                                  >
                                    📎 {attachment.fileName}
                                  </a>
                                );
                              })}

                              {!isIncoming && hasAudioAttachment ? (
                                <div className="crm-cw-msg-meta inline">
                                  <span>{formatMessageHour(msg.created_at)}</span>
                                  <span
                                    className={`crm-cw-ticks ${getOutgoingTickState(msg, selectedConversation)}`}
                                    aria-label="Estado del mensaje"
                                  >
                                    {getOutgoingTickState(msg, selectedConversation) === 'sent' ? '✓' : '✓✓'}
                                  </span>
                                </div>
                              ) : null}
                            </div>
                          );
                        })()}
                        {isTycPlaceholder ? (
                          renderTycPlaceholder(isIncoming)
                        ) : parsed.text ? (
                          <div className="crm-cw-msg-content">{renderMessageContent(parsed.text)}</div>
                        ) : null}
                        <div className="crm-cw-msg-actions">
                          <div className="crm-cw-msg-actions-wrap">
                            <button
                              type="button"
                              className="crm-cw-msg-kebab-btn"
                              title="Acciones del mensaje"
                              onClick={() =>
                                setActiveMessageMenuId((current) => (current === msg.id ? null : msg.id))
                              }
                            >
                              ⋮
                            </button>
                            {activeMessageMenuId === msg.id ? (
                              <div className="crm-cw-msg-menu">
                                <button
                                  type="button"
                                  className="crm-cw-msg-menu-item"
                                  onClick={() => handleReplyMessage(msg)}
                                  disabled={!canInteractSelectedChat}
                                >
                                  ↩ Responder
                                </button>
                                <button
                                  type="button"
                                  className="crm-cw-msg-menu-item"
                                  onClick={() => {
                                    const copyText = isTycPlaceholder
                                      ? `${isIncoming ? 'Aceptación' : 'Envío'} de Términos y Condiciones — ${TYC_PUBLIC_URL}`
                                      : msg.content;
                                    handleCopyMessage(msg.id, copyText);
                                    setActiveMessageMenuId(null);
                                  }}
                                >
                                  ⧉ Copiar
                                </button>
                                <button
                                  type="button"
                                  className="crm-cw-msg-menu-item"
                                  onClick={() => handleOpenForward(msg)}
                                >
                                  ↪ Reenviar
                                </button>
                              </div>
                            ) : null}
                          </div>
                          {copiedMessageId === msg.id ? (
                            <span className="crm-cw-msg-action-toast">Copiado</span>
                          ) : null}
                        </div>
                        {!isIncoming && !hasAudioAttachment ? (
                          <div className="crm-cw-msg-meta">
                            <span>{formatMessageHour(msg.created_at)}</span>
                            <span
                              className={`crm-cw-ticks ${getOutgoingTickState(msg, selectedConversation)}`}
                              aria-label="Estado del mensaje"
                            >
                              {getOutgoingTickState(msg, selectedConversation) === 'sent' ? '✓' : '✓✓'}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                  </>
                )}
                {showScrollToBottom ? (
                  <div className="crm-cw-scroll-bottom-wrap">
                    <button
                      type="button"
                      className="crm-cw-scroll-bottom"
                      onClick={() => {
                        if (!chatBodyRef.current) return;
                        chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
                        setShowScrollToBottom(false);
                      }}
                      title="Ir al último mensaje"
                      aria-label="Ir al último mensaje"
                    >
                      <CwIconScrollDown />
                    </button>
                  </div>
                ) : null}
              </div>

              <div className="crm-cw-chat-foot">
                {isSelectedSpectatorMode && !showExpiredTemplateNotice ? (
                  <div className="crm-cw-spectator-notice" role="status" aria-live="polite">
                    <div className="crm-cw-spectator-notice-head">
                      <span className="crm-cw-spectator-notice-chip">Modo observador</span>
                      {canReplyMessages ? (
                        <button
                          type="button"
                          className="crm-cw-spectator-takeover-btn"
                          title="Tomar conversación (derivar a humano)"
                          disabled={humanTakeoverBusy}
                          onClick={() => void handleHumanTakeover()}
                        >
                          {humanTakeoverBusy ? 'Derivando…' : 'Tomar conversación'}
                        </button>
                      ) : null}
                    </div>
                    <strong>{selectedBotDisplayName} está atendiendo a este paciente</strong>
                    <span className="crm-cw-spectator-notice__hint">
                      El envío de mensajes está bloqueado para no interferir con el bot. Usá{' '}
                      <strong>Tomar conversación</strong> para pasar a atención humana; ahí vas a ver{' '}
                      <strong>Transferir</strong> (junto a Finalizar) para pasarla a otro operador.
                    </span>
                  </div>
                ) : isSelectedFinishedFlow && !showExpiredTemplateNotice ? (
                  <div className="crm-cw-finished-notice" role="status" aria-live="polite">
                    <div className="crm-cw-finished-notice-head">
                      <span className="crm-cw-finished-notice-chip">Flujo finalizado</span>
                      {canReplyMessages ? (
                        <button
                          type="button"
                          className="crm-cw-finished-takeover-btn"
                          title="Pasar a derivación humana y atender al paciente"
                          disabled={humanTakeoverBusy}
                          onClick={() => void handleHumanTakeover()}
                        >
                          {humanTakeoverBusy ? 'Activando…' : 'Atender paciente'}
                        </button>
                      ) : null}
                    </div>
                    <strong>El trámite automático de recetas ya terminó</strong>
                    <span className="crm-cw-finished-notice__hint">
                      Usá <strong>Atender paciente</strong> para escribirle: la fase pasa a{' '}
                      <strong>derivación humana</strong> en Pulse y el chat queda en atención humana.
                    </span>
                  </div>
                ) : null}
                {!isSelectedSpectatorMode && !isSelectedFinishedFlow && replyToMessage ? (
                  <div className="crm-cw-reply-preview">
                    <div className="crm-cw-reply-preview-text">
                      <strong>Respondiendo a {replyToMessage.author}</strong>
                      <span>{replyToMessage.content || '(sin contenido)'}</span>
                    </div>
                    <button
                      type="button"
                      className="crm-cw-reply-close"
                      onClick={() => setReplyToMessage(null)}
                      title="Cancelar respuesta"
                    >
                      ✕
                    </button>
                  </div>
                ) : null}
                {!isSelectedSpectatorMode && !isSelectedFinishedFlow && composerMedia ? (
                  <div className="crm-cw-media-preview">
                    <div className="crm-cw-media-preview-main">
                      <span className="crm-cw-media-preview-icon" aria-hidden="true">
                        {composerMedia.kind === 'image'
                          ? '🖼️'
                          : composerMedia.kind === 'audio'
                            ? '🎤'
                            : composerMedia.kind === 'pdf'
                              ? '📄'
                              : '👤'}
                      </span>
                      <div className="crm-cw-media-preview-text">
                        <strong>
                          {composerMedia.kind === 'image'
                            ? 'Imagen lista para enviar'
                            : composerMedia.kind === 'audio'
                              ? 'Audio listo para enviar'
                              : composerMedia.kind === 'pdf'
                                ? 'PDF listo para enviar'
                                : 'Contacto listo para enviar'}
                        </strong>
                        <span>{composerMedia.name} · {composerMedia.sizeKb} KB</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="crm-cw-media-remove"
                      onClick={handleRemoveMedia}
                      title="Quitar adjunto"
                    >
                      Quitar
                    </button>
                  </div>
                ) : null}
                {!isSelectedSpectatorMode && !isSelectedFinishedFlow && !showExpiredTemplateNotice && !canReplyMessages && selectedConversation ? (
                  <div className="crm-cw-composer-locked" role="status">
                    Solo <strong>administrativo_1</strong> puede responder en este módulo. Podés leer el historial y usar la ficha del contacto.
                  </div>
                ) : null}
                {showExpiredTemplateNotice ? (
                  <div className="crm-cw-window-expired" role="status">
                    <div className="crm-cw-window-expired__icon" aria-hidden="true">⚠️</div>
                    <div className="crm-cw-window-expired__body">
                      <strong>La ventana de conversación de WhatsApp (24 h desde el último mensaje del cliente) ha expirado.</strong>
                      <span>Para comunicarte, utilizá un mensaje de plantilla.</span>
                      {whatsappWindowExpiresAt ? (
                        <span className="crm-cw-window-expired__date">
                          Fecha de expiración de ventana: {whatsappWindowExpiresAt.toLocaleString('es-AR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="crm-cw-window-expired__btn"
                      onClick={openTemplatePicker}
                    >
                      <span aria-hidden="true">📋</span> Usar plantilla
                    </button>
                  </div>
                ) : null}
                {!isSelectedSpectatorMode && !isSelectedFinishedFlow && canReplyMessages && !whatsappWindowExpired ? (
                <>
                {isSelectedHumanMode && canReplyMessages ? (
                  <div className="crm-cw-human-notice" role="status" aria-live="polite">
                    <div className="crm-cw-human-notice-toolbar">
                      <span className="crm-cw-human-notice-chip">Atención humana</span>
                      <div className="crm-cw-human-notice-actions">
                        {canTransferSelectedConversation ? (
                          <ChatwootTransferConversationButton
                            conversationId={selectedId}
                            conversationDisplayName={selectedContactDisplayName}
                            currentAssigneeId={selectedConversation?.assignee?.id}
                            agents={chatwootTransferAgents}
                            disabled={
                              humanFinalizeBusy || humanTakeoverBusy || humanReturnToBotBusy
                            }
                            onTransferred={handleConversationTransferred}
                            onNotify={notifyConversationAction}
                          />
                        ) : null}
                        <button
                          type="button"
                          className="crm-cw-human-return-bot-btn"
                          title={`Devolver al asistente automático ${selectedBotDisplayName}`}
                          disabled={
                            humanReturnToBotBusy || humanFinalizeBusy || humanTakeoverBusy
                          }
                          onClick={() => void handleHumanReturnToBot()}
                        >
                          {humanReturnToBotBusy ? 'Volviendo…' : 'Volver al bot'}
                        </button>
                        <button
                          type="button"
                          className="crm-cw-human-finalize-btn"
                          title="Finalizar atención y cerrar el flujo"
                          disabled={
                            humanFinalizeBusy || humanTakeoverBusy || humanReturnToBotBusy
                          }
                          onClick={() => void handleHumanFinalizeAttention()}
                        >
                          {humanFinalizeBusy ? 'Finalizando…' : 'Finalizar atención'}
                        </button>
                      </div>
                    </div>
                    <span className="crm-cw-human-notice__hint">
                      <strong>Volver al bot</strong> si el paciente puede seguir con {selectedBotDisplayName}.{' '}
                      <strong>Finalizar atención</strong> cuando cerraste la gestión: {selectedBotDisplayName} queda en pausa y el chat
                      se marca como resuelto.
                    </span>
                  </div>
                ) : null}
                {isSelectedHumanMode ? (
                  <div className="crm-cw-quick-phrases" role="group" aria-label="Frases rápidas">
                    <span className="crm-cw-quick-phrases__label">Frases rápidas</span>
                    <div className="crm-cw-quick-phrases__track">
                      {HUMAN_QUICK_PHRASES.map((phrase) => (
                        <button
                          key={phrase.id}
                          type="button"
                          className="crm-cw-quick-phrases__chip"
                          title={phrase.text}
                          disabled={sending}
                          onClick={() => insertDraftQuickPhrase(phrase.text)}
                        >
                          {phrase.label}
                        </button>
                      ))}
                    </div>
                    <span className="crm-cw-quick-phrases__hint">
                      Tocá una frase para cargarla en el mensaje; si elegís otra, reemplaza la anterior. Editá y enviá con ➤
                    </span>
                  </div>
                ) : null}
                <div className="crm-cw-composer-row">
                  <input
                    ref={mediaInputRef}
                    type="file"
                    accept={acceptedMediaTypes}
                    className="crm-cw-media-input-hidden"
                    onChange={handleMediaChange}
                  />
                  <button
                    type="button"
                    className="crm-cw-attach"
                    onClick={handlePickMedia}
                    title="Adjuntar imagen, audio, PDF o contacto"
                    aria-label="Adjuntar imagen, audio, PDF o contacto"
                    disabled={!canReplyMessages}
                  >
                    ＋
                  </button>
                  <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <button
                      type="button"
                      className={`crm-cw-mic ${isRecordingAudio ? 'recording' : ''}`}
                      title={isRecordingAudio ? 'Grabando... haz click para guardar' : 'Haz click para grabar audio'}
                      aria-label={isRecordingAudio ? 'Grabando audio' : 'Grabar audio'}
                      disabled={!canReplyMessages || Boolean(composerMedia) || !selectedId || sending}
                      onClick={handleMicClick}
                    >
                      {'🎤'}
                    </button>

                    {isRecordingAudio ? (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '100%',
                          left: '50%',
                          transform: 'translateX(-50%) translateY(-6px)',
                          padding: '6px 8px',
                          background: 'rgba(0,0,0,0.72)',
                          color: '#fff',
                          borderRadius: 8,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          pointerEvents: 'none'
                        }}
                      >
                        <MicVisualizer spectrum={recordingSpectrum} />
                      </div>
                    ) : null}
                    {isRecordingAudio ? (
                      <button
                        type="button"
                        className="crm-cw-attach crm-cw-stop"
                        onClick={() => stopAudioRecording({ save: true })}
                        title="Parar grabación"
                        aria-label="Parar grabación"
                      >
                        ⏹
                      </button>
                    ) : null}
                  </div>
                  <div>
                    <button
                      ref={emojiBtnRef}
                      type="button"
                      className={`crm-cw-emoji-btn ${showEmojiPicker ? 'active' : ''}`}
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      title="Agregar emoji"
                      aria-label="Agregar emoji"
                      disabled={!canReplyMessages}
                    >
                      😊
                    </button>
                    {showEmojiPicker && (
                      <EmojiPicker
                        onSelectEmoji={handleSelectEmoji}
                        onClose={() => setShowEmojiPicker(false)}
                                              buttonRef={emojiBtnRef}
                      />
                    )}
                  </div>
                  <textarea
                    ref={inputRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (canSendMessage) handleSendMessage();
                      }
                    }}
                    placeholder="Escribe una respuesta..."
                    className="crm-cw-input"
                    rows={1}
                    disabled={!canReplyMessages}
                  />
                  <button
                    type="button"
                    className={`crm-cw-send ${sending ? 'is-sending' : ''}`}
                    onClick={handleSendMessage}
                    disabled={!canSendMessage}
                    title={sending ? 'Enviando...' : 'Enviar mensaje'}
                    aria-label={sending ? 'Enviando mensaje' : 'Enviar mensaje'}
                  >
                    {sending ? (
                      <span className="crm-cw-send-spinner" aria-hidden="true" />
                    ) : (
                      <span className="crm-cw-send-icon" aria-hidden="true">➤</span>
                    )}
                  </button>
                </div>
                {isRecordingAudio ? (
                  <div className="crm-cw-composer-hint recording">
                    Grabando audio... {formatRecordingTime(recordingElapsedMs)} · haz click para guardar
                  </div>
                ) : null}
                {recordingError ? <div className="crm-cw-error">{recordingError}</div> : null}
                </>
                ) : null}
              </div>
            </>
          )}
        </section>
        ) : null}

        {showProfileColumn || showMobileProfileSheet ? (
        <>
        {showMobileProfileSheet ? (
          <button
            type="button"
            className={`crm-cw-profile-sheet-backdrop${mobileProfileOpen ? ' is-open' : ''}`}
            aria-label="Cerrar ficha"
            onClick={() => setMobileProfileOpen(false)}
          />
        ) : null}
        <aside className={`crm-cw-profile${showMobileProfileSheet ? ' crm-cw-profile--sheet is-open' : ''}`}>
          {showMobileProfileSheet ? (
            <div className="crm-cw-profile-sheet-head">
              <strong>Ficha del contacto</strong>
              <button
                type="button"
                className="crm-cw-profile-sheet-close"
                onClick={() => setMobileProfileOpen(false)}
                aria-label="Cerrar ficha"
              >
                ✕
              </button>
            </div>
          ) : null}
          <div className="crm-cw-profile-scroll">
          <div className="crm-cw-profile-head">
            <div className="crm-cw-avatar">
              {selectedContactDisplayName.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="crm-cw-profile-name">
                {selectedContactDisplayName}
              </div>
              <div className="crm-cw-profile-sub" title="Estado de la conversación en Chatwoot y bandeja (inbox) por donde entra el WhatsApp">
                {formatChatwootProfileMeta(selectedConversation)}
              </div>
              <div className="crm-cw-profile-sub crm-cw-profile-sub--channel">
                Canal activo: {visibleInboxMeta.label} · {visibleInboxMeta.hint}
              </div>
            </div>
          </div>

          <details
            className="crm-cw-profile-section crm-cw-profile-details"
            open={profileOpenSection === PROFILE_ACCORDION_SECTION.contact}
          >
            <summary onClick={openProfileAccordionSection(PROFILE_ACCORDION_SECTION.contact)}>
              Contacto
            </summary>
            <div className="crm-cw-profile-details__body">
            <div className="crm-cw-kv crm-cw-kv--contact">
              <span>Teléfono</span>
              <strong>{selectedConversation?.sender?.phone_number || '-'}</strong>
            </div>
            <div className="crm-cw-kv crm-cw-kv--contact">
              <span>Email</span>
              <strong className="crm-cw-kv__email" title={panelContactEmail}>
                {panelContactEmail}
              </strong>
            </div>
            {clientProfile ? (
              <div className="crm-cw-kv crm-cw-kv--contact">
                <span>Ficha</span>
                <strong>
                  <CrmStatusBadgeFrom badge={resolveClienteStatusBadge(clientProfile)} compact />
                </strong>
              </div>
            ) : null}
            {loadingTycAcceptance ? (
              <div className="crm-cw-kv crm-cw-kv--contact">
                <span>TyC</span>
                <strong className="crm-cw-kv__muted">Consultando…</strong>
              </div>
            ) : selectedTycLines ? (
              <div className="crm-cw-kv crm-cw-kv--contact">
                <span>TyC</span>
                <strong
                  className={`crm-cw-kv__tyc crm-cw-kv__tyc--${selectedTycLines.status}`}
                  title={selectedTycLines.secondary || selectedTycLines.primary}
                >
                  {selectedTycLines.primary}
                  <CrmStatusBadgeFrom badge={selectedTycLines.badge} compact />
                  {selectedTycLines.secondary ? (
                    <span className="crm-cw-kv__sub">{selectedTycLines.secondary}</span>
                  ) : null}
                </strong>
              </div>
            ) : normalizeDniForApi(clientProfile?.dni_normalizado) ? (
              <div className="crm-cw-kv crm-cw-kv--contact">
                <span>TyC</span>
                <strong className="crm-cw-kv__muted">Sin registro</strong>
              </div>
            ) : null}
            </div>
          </details>

          <details
            className="crm-cw-profile-section crm-cw-profile-section--plex crm-cw-profile-details"
            open={profileOpenSection === PROFILE_ACCORDION_SECTION.plex}
          >
            <summary onClick={openProfileAccordionSection(PROFILE_ACCORDION_SECTION.plex)}>
              Datos desde Geclisa/Plex
            </summary>
            <div className="crm-cw-profile-details__body">
            {loadingClientProfile ? (
              <div className="crm-cw-plex-loading" role="status" aria-live="polite" aria-busy="true">
                <div className="crm-cw-plex-loading__banner">
                  <span className="crm-cw-plex-loading__spinner" aria-hidden="true" />
                  <div className="crm-cw-plex-loading__copy">
                    <strong className="crm-cw-plex-loading__title">Consultando Plex</strong>
                    <span className="crm-cw-plex-loading__hint">Cargando ficha, obra social y historial de farmacia</span>
                  </div>
                </div>
                <ul className="crm-cw-plex-loading__rows" aria-hidden="true">
                  {['DNI', 'Obra social', 'Domicilio', 'Último mov.', 'Total compras'].map((label) => (
                    <li key={label} className="crm-cw-plex-loading__row">
                      <span className="crm-cw-plex-loading__label">{label}</span>
                      <span className="crm-cw-plex-loading__bar" />
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="crm-cw-plex-card">
                <div className="crm-cw-kv"><span>DNI</span><strong>{formatDni(clientProfile?.dni_normalizado)}</strong></div>
                <div className="crm-cw-kv">
                  <span>Obra social</span>
                  <strong>{clientProfile?.obra_social_descripcio || clientProfile?.os_sigla_geclisa || '-'}</strong>
                </div>
                <div className="crm-cw-kv">
                  <span>Domicilio</span>
                  <strong>{formatDomicilioPlex(clientProfile)}</strong>
                </div>
                <div className="crm-cw-kv">
                  <span>Último mov.</span>
                  <strong>{formatDateTimeAny(clientProfile?.fecha_ult_compra)}</strong>
                </div>
                <div className="crm-cw-kv">
                  <span>Total compras</span>
                  <strong>{plexPurchaseCount ?? '-'}</strong>
                </div>
                {isAltaProgramaRecetasWhatsapp(clientProfile) ? (
                  <div className="crm-cw-plex-origin" role="note">
                    <span className="crm-cw-plex-origin-badge">Programa de recetas · WhatsApp</span>
                    <p className="crm-cw-plex-origin-text">
                      Paciente dado de alta por el bot de recetas crónicas del Sanatorio Regional. Aún sin ficha
                      vinculada en Geclisa.
                    </p>
                    {clientProfile?.fecha_alta_sanatorio ? (
                      <p className="crm-cw-plex-origin-meta">
                        Alta registrada: {formatDateTimeAny(clientProfile.fecha_alta_sanatorio)}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}
            </div>
          </details>

          {!loadingClientProfile ? (
            <details
              className="crm-cw-profile-section crm-cw-profile-section--meds crm-cw-profile-details"
              open={profileOpenSection === PROFILE_ACCORDION_SECTION.meds}
            >
              <summary onClick={openProfileAccordionSection(PROFILE_ACCORDION_SECTION.meds)}>
                Medicamentos en farmacia
              </summary>
              <div className="crm-cw-profile-details__body">
                <div className="crm-cw-med-kpi-row">
                  <div className="crm-cw-med-kpi">
                    <span className="crm-cw-med-kpi-val">{plexPurchaseCount ?? '0'}</span>
                    <span className="crm-cw-med-kpi-lbl">Compras farmacia</span>
                  </div>
                  <div className="crm-cw-med-kpi">
                    <span className="crm-cw-med-kpi-val">{profileUniqueMedicamentos}</span>
                    <span className="crm-cw-med-kpi-lbl">Productos distintos</span>
                  </div>
                </div>
                {profileFarmaciaMedicamentos.length === 0 ? (
                  <div className="crm-cw-med-empty">Sin medicamentos en farmacia.</div>
                ) : (
                  <div
                    className={`crm-cw-med-catalog ${
                      farmaciaMedCompactList ? 'crm-cw-med-catalog--compact' : 'crm-cw-med-catalog--scroll'
                    } ${farmaciaMedShowAll ? 'crm-cw-med-catalog--all' : ''}`}
                  >
                    <div className="crm-cw-med-toolbar" style={{marginTop: '5px', marginBottom: '5px'}}>
                      <div className="crm-cw-med-toolbar__search">
                        <span className="crm-cw-med-toolbar__search-icon" aria-hidden="true">
                          <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="8.75" cy="8.75" r="5.25" stroke="currentColor" strokeWidth="1.6" />
                            <path
                              d="M13 13L17.5 17.5"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                            />
                          </svg>
                        </span>
                        <input
                          type="search"
                          className="crm-cw-med-toolbar__search-input"
                          placeholder="Buscar medicamento..."
                          value={farmaciaMedSearch}
                          onChange={(e) => setFarmaciaMedSearch(e.target.value)}
                          aria-label="Buscar medicamentos en farmacia"
                        />
                        {farmaciaMedSearch ? (
                          <button
                            type="button"
                            className="crm-cw-med-toolbar__search-clear"
                            onClick={() => {
                              setFarmaciaMedSearch('');
                              setFarmaciaMedPage(1);
                              setFarmaciaMedPageInput('1');
                            }}
                            title="Limpiar búsqueda"
                            aria-label="Limpiar búsqueda"
                          >
                            ×
                          </button>
                        ) : null}
                      </div>
                      <span className="crm-cw-med-toolbar__divider" aria-hidden="true" />
                      <select
                        className="crm-cw-med-toolbar__size"
                        value={farmaciaMedPageSize}
                        onChange={(e) => setFarmaciaMedPageSize(e.target.value)}
                        aria-label="Cantidad por página"
                        title="Cantidad por página"
                      >
                        {FARMACIA_MED_PAGE_SIZE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="crm-cw-med-catalog__meta">
                      <span>{farmaciaMedCatalogMeta.label}</span>
                    </div>
                    <div className="crm-cw-med-catalog__body">
                    <div className="crm-cw-med-scroll">
                      {paginatedFarmaciaMeds.length === 0 ? (
                        <div className="crm-cw-med-scroll-empty">
                          <span aria-hidden="true">🔎</span>
                          <p>Sin coincidencias para tu búsqueda.</p>
                          <button
                            type="button"
                            className="crm-cw-med-scroll-empty__btn"
                            onClick={() => {
                              setFarmaciaMedSearch('');
                              setFarmaciaMedPage(1);
                              setFarmaciaMedPageInput('1');
                            }}
                          >
                            Limpiar
                          </button>
                        </div>
                      ) : (
                        <ul className="crm-cw-med-list" aria-label="Medicamentos en farmacia">
                          {paginatedFarmaciaMeds.map((med, index) => {
                            const rank = farmaciaMedShowAll
                              ? index + 1
                              : (farmaciaMedPageSafe - 1) * farmaciaMedPageSizeNum + index + 1;
                            return (
                              <li
                                key={`${med.label}-${med.lastAt}-${rank}`}
                                className={`crm-cw-med-row ${med.isCronico ? 'is-chronic' : ''}`}
                              >
                                <span className="crm-cw-med-row-rank" aria-hidden="true">
                                  {rank}
                                </span>
                                <span className="crm-cw-med-row-name" title={med.label}>
                                  {med.label}
                                </span>
                                {med.isCronico ? (
                                  <span className="crm-cw-med-badge-cronico">Crónico</span>
                                ) : null}
                                <span className="crm-cw-med-row-count" title="Veces en compras farmacia">
                                  {med.count}x
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>

                    {!farmaciaMedShowAll ? (
                    <div
                      className={`crm-cw-med-pagination ${filteredFarmaciaMeds.length === 0 ? 'is-idle' : ''}`}
                      role="navigation"
                      aria-label="Paginación de medicamentos"
                    >
                      <div className="crm-cw-med-pagination__meta">
                        <span className="crm-cw-med-pagination__range">{farmaciaMedPageRangeLabel}</span>
                        <span className="crm-cw-med-pagination__pages">
                          Pág. {farmaciaMedPageSafe} / {farmaciaMedTotalPages}
                        </span>
                      </div>
                      <div className="crm-cw-med-pagination__controls">
                        <button
                          type="button"
                          className="crm-cw-med-pagination__btn"
                          disabled={farmaciaMedPageSafe <= 1 || filteredFarmaciaMeds.length === 0}
                          onClick={() => changeFarmaciaMedPage(1)}
                          title="Primera página"
                          aria-label="Primera página"
                        >
                          ⏮
                        </button>
                        <button
                          type="button"
                          className="crm-cw-med-pagination__btn"
                          disabled={farmaciaMedPageSafe <= 1 || filteredFarmaciaMeds.length === 0}
                          onClick={() => changeFarmaciaMedPage(farmaciaMedPageSafe - 1)}
                          title="Página anterior"
                          aria-label="Página anterior"
                        >
                          ◀
                        </button>
                        <div className="crm-cw-med-pagination__goto">
                          <input
                            type="text"
                            className="crm-cw-med-pagination__input"
                            value={farmaciaMedPageInput}
                            onChange={(e) => setFarmaciaMedPageInput(e.target.value.replace(/\D/g, ''))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                goToFarmaciaMedPageFromInput();
                              }
                            }}
                            disabled={filteredFarmaciaMeds.length === 0}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            aria-label="Ir a página"
                            placeholder={`1-${farmaciaMedTotalPages}`}
                          />
                          <button
                            type="button"
                            className="crm-cw-med-pagination__go"
                            onClick={goToFarmaciaMedPageFromInput}
                            disabled={filteredFarmaciaMeds.length === 0}
                          >
                            Ir
                          </button>
                        </div>
                        <button
                          type="button"
                          className="crm-cw-med-pagination__btn"
                          disabled={
                            farmaciaMedPageSafe >= farmaciaMedTotalPages
                            || filteredFarmaciaMeds.length === 0
                          }
                          onClick={() => changeFarmaciaMedPage(farmaciaMedPageSafe + 1)}
                          title="Página siguiente"
                          aria-label="Página siguiente"
                        >
                          ▶
                        </button>
                        <button
                          type="button"
                          className="crm-cw-med-pagination__btn"
                          disabled={
                            farmaciaMedPageSafe >= farmaciaMedTotalPages
                            || filteredFarmaciaMeds.length === 0
                          }
                          onClick={() => changeFarmaciaMedPage(farmaciaMedTotalPages)}
                          title="Última página"
                          aria-label="Última página"
                        >
                          ⏭
                        </button>
                      </div>
                    </div>
                    ) : (
                      <div className="crm-cw-med-pagination crm-cw-med-pagination--all" aria-live="polite">
                        <span className="crm-cw-med-pagination__range">{farmaciaMedPageRangeLabel}</span>
                      </div>
                    )}
                    </div>
                  </div>
                )}
            </div>
          </details>
          ) : null}
          </div>
        </aside>
        </>
        ) : null}
      </div>

      <ConversacionesMessageToastRail
        toasts={toasts}
        onOpen={(conversationId) => {
          openConversation(conversationId, { markRead: false });
        }}
      />

      {toasts.some((t) => !t.conversationId) ? (
        <div className="crm-cw-toast-stack crm-cw-toast-stack--actions" aria-live="polite" aria-atomic="true">
          {toasts
            .filter((t) => !t.conversationId)
            .map((toast) => (
              <button
                key={toast.id}
                type="button"
                className={`crm-cw-toast${toast.variant === 'error' ? ' crm-cw-toast--error' : ''}`}
                onClick={() => setActionToasts((prev) => prev.filter((t) => t.id !== toast.id))}
              >
                <div className="crm-cw-toast-dot" />
                <div className="crm-cw-toast-content">
                  <strong>{toast.title}</strong>
                  {toast.body ? <span>{toast.body}</span> : null}
                </div>
              </button>
            ))}
        </div>
      ) : null}

      {forwardMessage ? (
        <div className="crm-cw-forward-overlay" role="dialog" aria-label="Reenviar mensaje">
          <div className="crm-cw-forward-modal">
            <div className="crm-cw-forward-head">
              <strong>Reenviar mensaje</strong>
              <button
                type="button"
                className="crm-cw-forward-close"
                onClick={() => {
                  if (forwardingToConversationId) return;
                  setForwardMessage(null);
                  setForwardSearch('');
                }}
              >
                ✕
              </button>
            </div>
            <div className="crm-cw-forward-quoted">{getMessagePreview(forwardMessage.content) || '(sin contenido)'}</div>
            <input
              type="text"
              className="crm-cw-forward-search"
              placeholder="Buscar chat destino..."
              value={forwardSearch}
              onChange={(e) => setForwardSearch(e.target.value)}
            />
            <div className="crm-cw-forward-list">
              {forwardTargetConversations.length === 0 ? (
                <div className="crm-cw-empty">No hay chats para reenviar.</div>
              ) : (
                forwardTargetConversations.map((conv) => (
                  <button
                    key={conv.id}
                    type="button"
                    className="crm-cw-forward-item"
                    onClick={() => handleForwardToConversation(conv.id)}
                    disabled={Boolean(forwardingToConversationId)}
                  >
                    <strong>{getConversationDisplayName(conv)}</strong>
                    <span>{getMessagePreview(conv.last_message?.content) || 'Sin mensajes'}</span>
                    {forwardingToConversationId === conv.id ? (
                      <em>Enviando...</em>
                    ) : null}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      ) : null}

      {templateModalOpen ? (
        <div
          className="crm-cw-tpl-overlay"
          role="dialog"
          aria-label="Enviar plantilla de WhatsApp"
          onClick={(e) => {
            if (e.target === e.currentTarget && !sendingTemplate) closeTemplateModal();
          }}
        >
          <div className="crm-cw-tpl-modal">
            <div className="crm-cw-tpl-head">
              <div className="crm-cw-tpl-head__title">
                <span aria-hidden="true">📋</span>
                <strong>Plantillas de WhatsApp</strong>
              </div>
              <button
                type="button"
                className="crm-cw-tpl-close"
                onClick={closeTemplateModal}
                disabled={sendingTemplate}
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>
            <p className="crm-cw-tpl-sub">
              La ventana de 24 h expiró. Elegí una plantilla aprobada para reabrir la conversación.
            </p>

            {templatesLoading ? (
              <div className="crm-cw-tpl-loading">Cargando plantillas…</div>
            ) : templatesError && !selectedTemplate ? (
              <div className="crm-cw-tpl-error">{templatesError}</div>
            ) : !selectedTemplate ? (
              templates.length === 0 ? (
                <div className="crm-cw-tpl-empty">
                  No hay plantillas aprobadas en este inbox de WhatsApp.
                </div>
              ) : (
                <div className="crm-cw-tpl-list">
                  {templates
                    .filter(
                      (tpl) =>
                        tpl.name === 'saludo_inicial'
                    )
                    .map((tpl) => (
                    <button
                      key={`${tpl.name}-${tpl.language}`}
                      type="button"
                      className="crm-cw-tpl-item"
                      onClick={() => handlePickTemplate(tpl)}
                    >
                      <div className="crm-cw-tpl-item__head">
                        <strong>{tpl.name}</strong>
                        <span className="crm-cw-tpl-item__lang">{tpl.language}</span>
                        {tpl.category ? (
                          <span className="crm-cw-tpl-item__cat">{tpl.category}</span>
                        ) : null}
                      </div>
                      <span className="crm-cw-tpl-item__body">{tpl.body || '(sin cuerpo)'}</span>
                      {tpl.params_count > 0 ? (
                        <span className="crm-cw-tpl-item__params">
                          {tpl.params_count} variable{tpl.params_count > 1 ? 's' : ''} para completar
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              )
            ) : (
              <div className="crm-cw-tpl-detail">
                <button
                  type="button"
                  className="crm-cw-tpl-back"
                  onClick={() => {
                    setSelectedTemplate(null);
                    setTemplateParamValues([]);
                    setTemplatesError('');
                  }}
                  disabled={sendingTemplate}
                >
                  ← Volver a la lista
                </button>
                <div className="crm-cw-tpl-detail__name">
                  <strong>{selectedTemplate.name}</strong>
                  <span className="crm-cw-tpl-item__lang">{selectedTemplate.language}</span>
                </div>
                {selectedTemplate.header_text ? (
                  <div className="crm-cw-tpl-preview crm-cw-tpl-preview--header">
                    {selectedTemplate.header_text}
                  </div>
                ) : null}
                <div className="crm-cw-tpl-preview">
                  {fillTemplateBody(selectedTemplate.body, templateParamValues)}
                </div>
                {selectedTemplate.footer_text ? (
                  <div className="crm-cw-tpl-footer">{selectedTemplate.footer_text}</div>
                ) : null}

                {selectedTemplate.params_count > 0 ? (
                  <div className="crm-cw-tpl-params">
                    {Array.from({ length: selectedTemplate.params_count }).map((_, idx) => (
                      <label key={idx} className="crm-cw-tpl-param">
                        <span>Variable {`{{${idx + 1}}}`}</span>
                        <input
                          type="text"
                          value={templateParamValues[idx] || ''}
                          placeholder={`Valor para {{${idx + 1}}}`}
                          onChange={(e) => {
                            const next = [...templateParamValues];
                            next[idx] = e.target.value;
                            setTemplateParamValues(next);
                          }}
                        />
                      </label>
                    ))}
                  </div>
                ) : null}

                {templatesError ? (
                  <div className="crm-cw-tpl-error">{templatesError}</div>
                ) : null}

                <button
                  type="button"
                  className="crm-cw-tpl-send"
                  onClick={handleSendTemplate}
                  disabled={sendingTemplate}
                >
                  {sendingTemplate ? 'Enviando…' : 'Enviar plantilla'}
                </button>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* <div className="crm-cw-live-indicator" title="Actualización automática">
        En vivo
        {lastRefreshAt ? ` · ${new Date(lastRefreshAt).toLocaleTimeString('es-AR')}` : ''}
      </div> */}
    </section>
  );
}

export default Conversaciones;
